import axios from 'axios';
import { isTokenPresent, getAuthHeader } from '../utils/auth';
import { dateUtils } from '../utils/dateUtils';
import moment from 'moment-timezone';
import { saveAs } from 'file-saver';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear } from 'date-fns';
import { formatCurrency, extractNumericValue } from '../utils/dataTransformations';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

console.log('BASE_URL:', BASE_URL)

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Clear local storage and redirect to login
const handleUnauthorized = () => {
  localStorage.removeItem('token');
  window.location.href = '/login';
};

// Request interceptor to add Authorization header if authenticated and also handle moment objects
axiosInstance.interceptors.request.use(
  config => {
    if (isTokenPresent()) {
      const authHeader = getAuthHeader();
      if (authHeader) {
	config.headers.Authorization = authHeader;
      }
    }

    if (config.params) {
      config.params = Object.fromEntries(
        Object.entries(config.params)
          .map(([key, value]) => {
            // Skip moment internal properties
            if (key.startsWith('_')) {
              return false;
            }
            
            // Handle moment objects
            if (moment.isMoment(value)) {
              return [key, value.format('YYYY-MM-DD')];
            }
            
            // Keep other values as is
            return [key, value];
          })
          .filter(Boolean)
      );
    }

    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor: Handle MFA and auth errors
axiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      const { status, data } = error.response;

      // ✅ MFA Requirement Handling
      if (status === 403 && data?.requires_mfa === 'True') {
        const operationType = data.operation_type || 'fund_management';
        const currentPath = window.location.pathname;
        window.location.href = `/mfa-setup?redirect=${encodeURIComponent(currentPath)}&operationType=${operationType}`;

        // Halt further error handling
        return new Promise(() => {});
      }

      // �� Auth error fallback
      if (status === 401) {
        // Only 401 should trigger re-login
        handleUnauthorized();
      } else {
        // Let frontend components handle 403 and others
        console.error('API error:', data);
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

const api = {
  get: (url, config = {}) => axiosInstance.get(url, config),
  post: (url, data, config = {}) => axiosInstance.post(url, data, config),
  put: (url, data, config = {}) => axiosInstance.put(url, data, config),
  patch: (url, data, config = {}) => axiosInstance.patch(url, data, config),
  delete: (url, config = {}) => axiosInstance.delete(url, config),
};

// Fund Sources API
export const getFundSources = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.isActive !== undefined) {
      queryParams.append('is_active', filters.isActive);
    }

    if (filters.search) {
      queryParams.append('search', filters.search);
    }

    if (filters.ordering) {
      queryParams.append('ordering', filters.ordering);
    }

    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    if (filters.include_cross_branch) {
      queryParams.append('include_cross_branch', 'true');
    }

    if (filters.include_cross_department) {
      queryParams.append('include_cross_department', 'true');
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    const response = await api.get(`/fund-management/sources/${queryString}`);
    return response;
  } catch (error) {
    if (error.response?.data) {
      const errorData = error.response.data;

      // Handle MFA-specific errors
      if (errorData.error_code === 'mfa_required' || errorData.error_code === 'mfa_setup_required') {
        const operationType = errorData.operation_type || 'fund_management';
        const returnUrl = window.location.pathname;

        window.location.href = `/mfa-setup?operation=${operationType}&returnUrl=${encodeURIComponent(returnUrl)}`;

        // Prevent further error handling
        throw new Error('MFA_REDIRECT');
      }
    }

    throw error;
  }
};

// Fetch all branches
export const fetchBranches = async () => {
  try {
    const response = await axiosInstance.get('/core/branches/');

    console.log('Raw Branches API Response:', response.data);

    let branches = [];

    if (response.data.results) {
      branches = response.data.results;
    } else if (Array.isArray(response.data)) {
      branches = response.data;
    } else {
      console.error('Unexpected branches API response structure:', response.data);
      branches = [];
    }

    return branches.map(branch => ({
      ...branch,
      id: branch.id,
      name: branch.name || 'Unnamed Branch',
      code: branch.code || '',
      city: branch.city || '',
      address: branch.address || '',
      contact_info: branch.contact_info || {},
      is_active: branch.is_active !== false,
      displayName: `${branch.name} (${branch.code})`,
      created_at: branch.created_at,
      updated_at: branch.updated_at
    }));

  } catch (error) {
    console.error('Error fetching branches:', error);
    throw error;
  }
};

// Fetch all departments
export const fetchDepartments = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    
    if (params.branch) {
      queryParams.append('branch', params.branch);
    }
    
    if (params.isActive !== undefined) {
      queryParams.append('is_active', params.isActive);
    }
    
    const url = queryParams.toString() 
      ? `/core/departments/?${queryParams.toString()}`
      : '/core/departments/';
    
    const response = await axiosInstance.get(url);
    
    console.log('Raw Departments API Response:', response.data);
    
    let departments = [];
    if (response.data.results) {
      departments = response.data.results;
    } else if (Array.isArray(response.data)) {
      departments = response.data;
    } else {
      console.error('Unexpected departments API response structure:', response.data);
      departments = [];
    }
    
    return departments.map(department => ({
      ...department,
      id: department.id,
      name: department.name || 'Unnamed Department',
      code: department.code || '',
      description: department.description || '',
      operational_hours: department.operational_hours || {},
      manager: department.manager || null,
      is_active: department.is_active !== false,
      displayName: `${department.name} (${department.code})`,
      created_at: department.created_at,
      updated_at: department.updated_at
    }));
    
  } catch (error) {
    console.error('Error fetching departments:', error);
    throw error;
  }
};

// Optional: Fetch departments for a specific branch
export const fetchDepartmentsByBranch = async (branchId) => {
  try {
    const response = await axiosInstance.get(`/core/departments/?branch=${branchId}`);

    console.log(`Raw Departments for Branch ${branchId} API Response:`, response.data);

    let departments = [];

    if (response.data.results) {
      departments = response.data.results;
    } else if (Array.isArray(response.data)) {
      departments = response.data;
    } else {
      console.error('Unexpected departments API response structure:', response.data);
      departments = [];
    }

    return departments.map(department => ({
      ...department,
      id: department.id,
      name: department.name || 'Unnamed Department',
      code: department.code || '',
      description: department.description || '',
      displayName: `${department.name} (${department.code})`
    }));

  } catch (error) {
    console.error(`Error fetching departments for branch ${branchId}:`, error);
    throw error;
  }
};

export const getFundSourceById = async (id) => {
  return await api.get(`/fund-management/sources/${id}/`);
};

export const createFundSource = async (data) => {
  return await api.post('/fund-management/sources/', data);
};

export const updateFundSource = async (id, data) => {
  return await api.put(`/fund-management/sources/${id}/`, data);
};

export const partialUpdateFundSource = async (id, data) => {
  return await api.patch(`/fund-management/sources/${id}/`, data);
};

export const deleteFundSource = async (id) => {
  return await api.delete(`/fund-management/sources/${id}/`);
};

// Fund Accounts API
export const getFundAccounts = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Preserve original logic and add new filters intelligently
    if (filters.isActive !== undefined) {
      queryParams.append('is_active', String(filters.isActive));
    }

    if (filters.status) {
      queryParams.append('status', filters.status);
    }

    if (filters.type) {
      queryParams.append('account_type', filters.type);
    }

    if (filters.search) {
      queryParams.append('search', encodeURIComponent(filters.search));
    }

    if (filters.ordering) {
      queryParams.append('ordering', filters.ordering);
    }

    if (filters.page) {
      queryParams.append('page', filters.page);
    }

    if (filters.branch) {
      queryParams.append('branch', filters.branch);
    }

    if (filters.department) {
      queryParams.append('department', filters.department);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const url = `/fund-management/accounts/${queryString}`;

    const response = await api.get(url);
    
    const responseData = response.data;
    
    // Handle different API response formats
    let results = [];
    let count = 0;
    
    if (Array.isArray(responseData)) {
      // If API returns array directly
      results = responseData;
      count = responseData.length;
    } else if (responseData && Array.isArray(responseData.results)) {
      // If API returns paginated response with results array
      results = responseData.results;
      count = responseData.count || responseData.results.length;
    } else if (responseData && typeof responseData === 'object') {
      // If API returns single object, wrap in array
      results = [responseData];
      count = 1;
    }
    
    return {
      data: {
        results: results,
        count: count,
        next: responseData.next || null,
        previous: responseData.previous || null
      },
      // Also provide direct access for components expecting just the array
      results: results,
      count: count,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    };
  } catch (error) {
    // Enhanced MFA handling                                                                                                                
    if (error.response?.data?.requires_mfa) {
      return {                                                                                                                              
        error: true,                                                                                                                        
        requiresMfa: true,                                                                                                                  
        status: error.response.status,                                                                                                      
        data: error.response.data                                                                                                           
      };                                                                                                                                    
    }                                                                                                                                       
                                                                                                                                            
    console.error("Error fetching fund accounts:", error);                                                                                  
    throw error;                                                                                                                            
  }                                                                                                                                         
};

export const getFundAccountById = async (id) => {
  return await api.get(`/fund-management/accounts/${id}/`);
};

export const createFundAccount = async (data) => {
  return await api.post('/fund-management/accounts/', data);
};

export const updateFundAccount = async (id, data) => {
  return await api.put(`/fund-management/accounts/${id}/`, data);
};

export const partialUpdateFundAccount = async (id, data) => {
  return await api.patch(`/fund-management/accounts/${id}/`, data);
};

export const deleteFundAccount = async (id) => {
  return await api.delete(`/fund-management/accounts/${id}/`);
};

export const getBanks = async () => {
  try {
    const response = await api.get('/fund-management/banks/');
    console.log('Raw Banks API Response:', response.data);
    
    let banks = [];
    if (response.data.results) {
      banks = response.data.results;
    } else if (Array.isArray(response.data)) {
      banks = response.data;
    } else {
      console.error('Unexpected banks API response structure:', response.data);
      banks = [];
    }

    return banks.map(bank => ({
      ...bank,
      id: bank.id,
      name: bank.name || 'Unnamed Bank',
      code: bank.code || '',
      is_active: bank.is_active !== false,
      displayName: `${bank.name} (${bank.code})`,
      created_at: bank.created_at,
      updated_at: bank.updated_at
    }));

  } catch (error) {
    console.error('Error fetching banks:', error);
    throw error;
  }
};

export const validateAccountNumber = async (data) => {
  return await api.post('/fund-management/accounts/validate_account_number/', data);
};

// Fund Account Transactions and Balance History
export const getFundAccountTransactions = async (accountId) => {
  return await api.get(`/fund-management/accounts/${accountId}/transactions/`);
};

export const getFundAccountBalanceHistory = async (accountId, filters = {}) => {
  const queryParams = new URLSearchParams();

  // Handle all filter parameters
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

  console.log('API Call:', `/fund-management/accounts/${accountId}/balance_history/${queryString}`);

  const response = await api.get(`/fund-management/accounts/${accountId}/balance_history/${queryString}`);
  return response; // Return actual balance history data
};

export const getAccountTransactions = async (accountId, filters = {}) => {
  try {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/fund-management/accounts/${accountId}/transactions/${queryString}`);
    return response;
  } catch (error) {
    console.error('API Error in getAccountTransactions:', error);
    throw error;
  }
};

const extractFilenameFromHeaders = (headers) => {
  const contentDisposition = headers['content-disposition'];
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1];
    }
  }
  return `transactions_${new Date().toISOString().split('T')[0]}.pdf`;
};


export const exportTransactions = async (accountId, filters = {}) => {
  try {
    const format = filters.format || 'pdf';
    
    // Build query params manually as a clean object
    const queryParams = {
      operation_type: 'FUND_ACCOUNT_EXPORT_TRANSACTIONS',
      export_format: format,
    };

    // Only add defined filter values
    if (filters.transaction_type) queryParams.transaction_type = filters.transaction_type;
    if (filters.status) queryParams.status = filters.status;
    if (filters.search) queryParams.search = filters.search;
    if (filters.start_date) queryParams.start_date = filters.start_date;
    if (filters.end_date) queryParams.end_date = filters.end_date;

    console.log('Export request params:', queryParams);
    console.log('Request URL:', `/fund-management/accounts/${accountId}/export_transactions/`);

    const response = await axiosInstance.get(
      `/fund-management/accounts/${accountId}/export_transactions/`,
      {
        params: queryParams,
        responseType: 'blob',
      }
    );

    console.log('Export response received:', response.status);

    return {
      success: true,
      data: response.data,
      filename: extractFilenameFromHeaders(response.headers),
    };
  } catch (error) {
    console.error('Error exporting transactions:', error);
    console.log('Error response status:', error.response?.status);
    console.log('Error response headers:', error.response?.headers);
    
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      console.log('Error response body:', text);
    }

    if (error.response?.status === 403 && error.response?.data?.requires_mfa) {
      return {
        success: false,
        requiresMfa: true,
        data: error.response.data,
      };
    }

    throw error;
  }
};

/**
 * Fetch fund transactions with optional filtering, sorting, and pagination
 *
 * @param {Object} filters - Filter parameters
 * @param {string} filters.search - Search term
 * @param {string} filters.ordering - Field to order by (prefix with - for descending)
 * @param {string} filters.transaction_type - Type of transaction (RECEIVED, RELEASED, etc.)
 * @param {string} filters.start_date - Start date filter (YYYY-MM-DD)
 * @param {string} filters.end_date - End date filter (YYYY-MM-DD)
 * @param {string} filters.status - Status filter (PENDING, COMPLETED, REJECTED)
 * @param {number} filters.page - Page number for pagination
 * @param {number} filters.page_size - Number of items per page
 * @returns {Promise} - API response
 */
export const getFundTransactions = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.ordering) queryParams.append('ordering', filters.ordering);
    if (filters.transaction_type) queryParams.append('transaction_type', filters.transaction_type);
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.page) queryParams.append('page', filters.page);
    queryParams.append('page_size', filters.page_size || 10);

    if (filters.fund_source_id) queryParams.append('fund_source_id', filters.fund_source_id);
    if (filters.fund_source) queryParams.append('fund_source', filters.fund_source);

    if (filters.department) queryParams.append('department', filters.department);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);
    if (filters.branch) queryParams.append('branch', filters.branch);
    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    const response = await api.get(`/fund-management/transactions/${queryString}`);

    console.log('getFundTransactions:', {
      url: `/fund-management/transactions/${queryString}`,
      count: response.data?.count ?? 'N/A',
      total: response.data?.results?.length ?? response.data?.length ?? 0
    });

    return response;
  } catch (error) {
    console.error('getFundTransactions error:', error.message);
    throw error;
  }
};

export const getAllFundTransactions = async (filters = {}) => {
  const allResults = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const response = await getFundTransactions({ ...filters, page, page_size: 100 });
    const results = response.data?.results || [];
    allResults.push(...results);
    hasMore = Boolean(response.data?.next);
    page += 1;
  }

  return allResults;
};

export const getFundTransactionById = async (transactionId) => {
  try {
    const response = await api.get(`/fund-management/transactions/${transactionId}/`);

    return {
      data: response.data,
      status: response.status
    };
  } catch (error) {
    console.error('Error fetching fund transaction by ID:', error);

    // Provide detailed error information for debugging
    if (error.response) {
      // Server responded with error status
      throw {
        message: error.response.data?.error || 'Failed to fetch transaction',
        status: error.response.status,
        data: error.response.data,
        originalError: error
      };
    } else if (error.request) {
      // Request was made but no response received
      throw {
        message: 'Network error - no response from server',
        status: null,
        data: null,
        originalError: error
      };
    } else {
      // Something else happened
      throw {
        message: error.message || 'Unknown error occurred',
        status: null,
        data: null,
        originalError: error
      };
    }
  }
};

/**
 * Get a specific fund transaction by ID
 * @param {string|number} id - Transaction ID
 * @returns {Promise<AxiosResponse>} Response with transaction data
 */
export const getFundTransaction = async (id) => {
  try {
    return await api.get(`/fund-management/transactions/${id}/`);
  } catch (error) {
    console.error("Error fetching transaction:", error);
    throw error;
  }
};

export const checkFundAvailability = async (data) => {
  try {
    // ADD THIS LINE to ensure amount is never an array
    const requestAmount = Array.isArray(data.requested_amount) ? data.requested_amount[0] : data.requested_amount;
    
    const response = await api.post('/fund-management/transactions/check_availability/', {
      transaction_type: data.transaction_type || 'RELEASED',
      amount: requestAmount, // CHANGE: use requestAmount instead of data.requested_amount
      from_account: data.fund_account_id,
      department: data.department_id,
      branch: data.branch,
      description: `Fund availability check for ${data.reference_type || 'transaction'}`
    });

    return response;
  } catch (error) {
    // Keep all your existing error handling unchanged
    if (error.response?.data?.requires_mfa) {
      return {
        error: true,
        requiresMfa: true,
        status: error.response.status,
        data: error.response.data
      };
    }

    console.error("Error checking fund availability:", error);
    throw error;
  }
};

export const calculateTransactionFees = async (transactionData) => {
  try {
    const payload = {
      transaction_type: transactionData.transaction_type,
      amount: transactionData.amount,
      from_account: transactionData.from_account,
      to_account: transactionData.to_account,
      urgency_level: transactionData.urgency_level || 'NORMAL'
    };

    // Clean up null/undefined values
    Object.keys(payload).forEach(key => {
      if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
        delete payload[key];
      }
    });

    const response = await axiosInstance.post('/fund-management/transactions/calculate-fees/', payload);
    return response;
  } catch (error) {
    console.error('Error calculating transaction fees:', error);
    throw error;
  }
};

export const getTransactionHistory = async (transactionId) => {
  try {
    const response = await axiosInstance.get(`/fund-management/transactions/${transactionId}/history/`);
    return response;
  } catch (error) {
    console.error('Error fetching transaction history:', error);
    throw error;
  }
};

export const previewTransactionChanges = async (transactionId, proposedChanges) => {
  try {
    const response = await axiosInstance.post(
      `/fund-management/transactions/${transactionId}/preview-changes/`,
      proposedChanges
    );
    return response;
  } catch (error) {
    console.error('Error previewing transaction changes:', error);
    throw error;
  }
};

export const getInterDepartmentBillingRates = async () => {
  try {
    const response = await axiosInstance.get('/fund-management/inter-department-billing/billing-rates/');
    return response;
  } catch (error) {
    console.error('Error fetching inter-department billing rates:', error);
    throw error;
  }
};

export const getInterDepartmentBillingData = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add filters based on your InterDepartmentBillingViewSet
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.billing_type) queryParams.append('billing_type', filters.billing_type);
    if (filters.billing_department) queryParams.append('billing_department', filters.billing_department);
    if (filters.charged_department) queryParams.append('charged_department', filters.charged_department);
    if (filters.billing_branch) queryParams.append('billing_branch', filters.billing_branch);
    if (filters.charged_branch) queryParams.append('charged_branch', filters.charged_branch);
    if (filters.date_from) queryParams.append('billing_date__gte', filters.date_from);
    if (filters.date_to) queryParams.append('billing_date__lte', filters.date_to);
    if (filters.due_date_from) queryParams.append('due_date__gte', filters.due_date_from);
    if (filters.due_date_to) queryParams.append('due_date__lte', filters.due_date_to);
    if (filters.amount_min) queryParams.append('amount__gte', filters.amount_min);
    if (filters.amount_max) queryParams.append('amount__lte', filters.amount_max);
    if (filters.requested_by) queryParams.append('requested_by', filters.requested_by);
    if (filters.approved_by) queryParams.append('approved_by', filters.approved_by);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.ordering) queryParams.append('ordering', filters.ordering);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.page_size) queryParams.append('page_size', filters.page_size || 10);
    if (filters.limit) queryParams.append('limit', filters.limit);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/fund-management/inter-department-billing/${queryString}`);
  } catch (error) {
    console.error('Error in getInterDepartmentBillingData:', error);
    throw error;
  }
};

export const validateBeneficiaryDetails = async (beneficiaryData) => {
  try {
    const payload = {
      account_number: beneficiaryData.account_number,
      bank_code: beneficiaryData.bank_code,
      swift_code: beneficiaryData.swift_code
    };

    // Clean up null/undefined values
    Object.keys(payload).forEach(key => {
      if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
        delete payload[key];
      }
    });

    const response = await axiosInstance.post('/fund-management/validate-beneficiary/', payload);
    return response;
  } catch (error) {
    console.error('Error validating beneficiary details:', error);
    throw error;
  }
};

export const getTransactionTemplates = async (category = null) => {
  try {
    const params = {};
    if (category) {
      params.category = category;
    }

    const response = await axiosInstance.get('/fund-management/transaction-templates/', { params });
    return response;
  } catch (error) {
    console.error('Error fetching transaction templates:', error);
    throw error;
  }
};

/**
 * Validate a transaction against business rules and compliance requirements
 * @param {Object} transactionData - Transaction data to validate
 * @returns {Promise} - Validation results
 */
export const validateTransaction = async (transactionData) => {
  try {
    const payload = {
      transaction_type: transactionData.transaction_type,
      amount: transactionData.amount,
      department: transactionData.department,
      branch: transactionData.branch,
      from_account: transactionData.from_account,
      to_account: transactionData.to_account,
      date_executed: transactionData.date_executed,
      description: transactionData.description || ''
    };

    // Remove null/undefined values
    Object.keys(payload).forEach(key => {
      if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
        delete payload[key];
      }
    });

    const response = await axiosInstance.post('/fund-management/transactions/validate-transaction/', payload);
    return response;
  } catch (error) {
    console.error('Error in validateTransaction:', error);

    // Return structured error response to match expected format
    if (error.response?.data) {
      throw error;
    }

    // Create fallback error response
    throw {
      response: {
        data: {
          is_valid: false,
          errors: ['Network error occurred during validation'],
          warnings: [],
          business_rule_violations: [],
          compliance_issues: [],
          recommendations: ['Please check your connection and try again']
        }
      }
    };
  }
};

/**
 * Calculate comprehensive risk score for a transaction
 * @param {Object} transactionData - Transaction data for risk assessment
 * @returns {Promise} - Risk assessment results
 */
export const calculateRiskScore = async (transactionData) => {
  try {
    // Validate required fields
    if (!transactionData.department || transactionData.department === '' || transactionData.department === null) {
      throw new Error('Department is required for risk score calculation');
    }

    const payload = {
      transaction_type: transactionData.transaction_type,
      amount: transactionData.amount,
      department: transactionData.department
    };

    // Only add non-empty optional fields
    if (transactionData.branch && transactionData.branch !== '' && transactionData.branch !== null) {
      payload.branch = transactionData.branch;
    }
    
    if (transactionData.description && transactionData.description !== '') {
      payload.description = transactionData.description;
    }
    
    if (transactionData.date_executed) {
      payload.date_executed = transactionData.date_executed;
    }
    
    if (transactionData.from_account && transactionData.from_account !== '' && transactionData.from_account !== null) {
      payload.from_account = transactionData.from_account;
    }
    
    if (transactionData.to_account && transactionData.to_account !== '' && transactionData.to_account !== null) {
      payload.to_account = transactionData.to_account;
    }
    
    if (transactionData.beneficiary_name && transactionData.beneficiary_name !== '') {
      payload.beneficiary_name = transactionData.beneficiary_name;
    }
    
    if (transactionData.priority && transactionData.priority !== '') {
      payload.priority = transactionData.priority;
    }

    const response = await axiosInstance.post('/fund-management/transactions/calculate-risk-score/', payload);
    return response;
  } catch (error) {
    console.error('Error in calculateRiskScore:', error);

    if (error.response?.data) {
      throw error;
    }

    // Return fallback data
    throw {
      response: {
        data: {
          score: 50,
          level: 'MEDIUM',
          description: 'Unable to calculate risk score - network error',
          color_code: '#ffc107',
          risk_factors: ['Network connectivity issue'],
          risk_breakdown: {
            amount_risk: 0,
            timing_risk: 0,
            user_risk: 0,
            cross_department_risk: 0,
            description_risk: 0,
            transaction_type_risk: 0,
            frequency_risk: 0
          },
          recommendations: ['Please check your connection and try again'],
          requires_additional_approval: true,
          calculated_at: new Date().toISOString()
        }
      }
    };
  }
};

export const getApprovalWorkflows = async () => {
  try {
    return await api.get('/approval-workflows/');
  } catch (error) {
    console.error("Error fetching approval workflows:", error);
    throw error;
  }
};

/**
 * Get approval workflow requirements for a transaction
 * @param {Object} transactionData - Transaction data for approval workflow
 * @returns {Promise} - Approval workflow information
 */
export const getApprovalWorkflow = async (transactionData) => {
  try {
    // Validate required fields
    if (!transactionData.department || transactionData.department === '' || transactionData.department === null) {
      throw new Error('Department is required for approval workflow');
    }

    const payload = {
      transaction_type: transactionData.transaction_type,
      amount: transactionData.amount,
      department: transactionData.department
    };

    // Only add non-empty optional fields
    if (transactionData.branch && transactionData.branch !== '' && transactionData.branch !== null) {
      payload.branch = transactionData.branch;
    }
    
    if (transactionData.priority && transactionData.priority !== '') {
      payload.priority = transactionData.priority;
    }

    const response = await axiosInstance.post('/fund-management/transactions/approval-workflow/', payload);
    return response;
  } catch (error) {
    console.error('Error in getApprovalWorkflow:', error);

    // Return fallback approval workflow data
    const amount = parseFloat(transactionData.amount) || 0;
    const requiresApproval = amount > 10000; // Default threshold

    throw {
      response: {
        data: {
          requires_approval: requiresApproval,
          approval_levels: requiresApproval ? ['Manager'] : [],
          approvers: requiresApproval ? [
            {
              level: 'Manager',
              required: true,
              user_roles: ['Fund Manager', 'Department Head']
            }
          ] : [],
          estimated_approval_time: requiresApproval ? '2-4 hours' : 'Immediate',
          warnings: ['Unable to fetch approval workflow - using defaults']
        }
      }
    };
  }
};

export const getTransactionWorkflowStatus = async (transactionId) => {
  try {
    const response = await axiosInstance.get(
      `/fund-management/transactions/${transactionId}/workflow-status/`
    );
    return response;
  } catch (error) {
    console.error('Error fetching transaction workflow status:', error);
    throw error;
  }
};

export const getTransactionWorkflow = async (transactionId) => {
  try {
    const response = await axiosInstance.get(
      `/fund-management/approvals/workflow/${transactionId}/`
    );
    return response;
  } catch (error) {
    console.error('Error fetching transaction workflow:', error);
    
    // Return fallback workflow structure for error handling
    const fallbackWorkflow = {
      data: {
        workflow_steps: [
          {
            level: 0,
            title: 'Initial Request',
            status: 'completed',
            approver: 'System',
            approver_name: 'System',
            date: new Date().toISOString(),
            description: 'Transaction submitted for approval',
            comments: 'Unable to fetch workflow data'
          },
          {
            level: 1,
            title: 'Department Review',
            status: 'pending',
            approver: 'Pending Assignment',
            approver_name: 'Pending Assignment',
            date: null,
            description: 'Awaiting department approval',
            comments: null
          }
        ],
        metadata: {
          transaction_id: transactionId,
          transaction_type: 'UNKNOWN',
          amount: 0,
          currency: 'NGN',
          requires_approval: true,
          approval_status: 'pending',
          transaction_status: 'PENDING',
          total_levels_required: 1,
          levels_completed: 0,
          overall_status: 'pending',
          can_be_approved_by_user: false
        },
        required_levels: [1]
      }
    };
    
    throw { response: fallbackWorkflow, originalError: error };
  }
};

export const createFundAllocation = async (data) => {
  try {
    const response = await api.post('/fund-management/allocations/', {
      reference_type: data.reference_type,
      reference_id: data.reference_id,
      fund_account: data.fund_account_id,
      amount: data.amount,
      description: data.description,
      requires_mfa: data.requires_mfa || false,
      requested_by: data.user_id || null,
      branch: data.branch_id || null,
      department: data.department_id || null,
    });
    
    return response;
  } catch (error) {
    // Enhanced MFA handling
    if (error.response?.data?.requires_mfa) {
      return {
        error: true,
        requiresMfa: true,
        status: error.response.status,
        data: error.response.data
      };
    }
    
    console.error("Error creating fund allocation:", error);
    throw error;
  }
};

export const getFundAllocations = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (filters.status) {
      queryParams.append('status', filters.status);
    }
    
    if (filters.reference_type) {
      queryParams.append('reference_type', filters.reference_type);
    }
    
    if (filters.fund_account) {
      queryParams.append('fund_account', filters.fund_account);
    }
    
    if (filters.search) {
      queryParams.append('search', encodeURIComponent(filters.search));
    }
    
    if (filters.ordering) {
      queryParams.append('ordering', filters.ordering);
    }
    
    if (filters.page) {
      queryParams.append('page', filters.page);
    }
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const url = `/fund-management/allocations/${queryString}`;
    
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error("Error fetching fund allocations:", error);
    throw error;
  }
};

export const approveFundAllocation = async (allocationId) => {
  try {
    const response = await api.post(`/fund-management/allocations/${allocationId}/approve/`);
    return response;
  } catch (error) {
    console.error("Error approving fund allocation:", error);
    throw error;
  }
};

export const rejectFundAllocation = async (allocationId, rejectionReason) => {
  try {
    const response = await api.post(`/fund-management/allocations/${allocationId}/reject/`, {
      rejection_reason: rejectionReason
    });
    return response;
  } catch (error) {
    console.error("Error rejecting fund allocation:", error);
    throw error;
  }
};

export const cancelFundAllocation = async (allocationId) => {
  try {
    const response = await api.post(`/fund-management/allocations/${allocationId}/cancel/`);
    return response;
  } catch (error) {
    console.error("Error cancelling fund allocation:", error);
    throw error;
  }
};

export const getFundUtilizationData = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    if (filters.start_date) {
      queryParams.append('start_date', filters.start_date);
    }

    if (filters.end_date) {
      queryParams.append('end_date', filters.end_date);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/api/reports/utilization/${queryString}`);
    return response.data;

  } catch (error) {
    if (error.response?.data) {
      const errorData = error.response.data;
      
      if (errorData.error_code === 'mfa_required' || errorData.error_code === 'mfa_setup_required') {
        const operationType = errorData.operation_type || 'fund_management';
        const returnUrl = window.location.pathname;
        
        window.location.href = `/mfa-setup?operation=${operationType}&returnUrl=${encodeURIComponent(returnUrl)}`;
        throw new Error('MFA_REDIRECT');
      }
    }
    
    throw error;
  }
};

export const getFundPredictiveData = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/api/reports/predictive/${queryString}`);
    return response.data;

  } catch (error) {
    if (error.response?.data) {
      const errorData = error.response.data;
      
      if (errorData.error_code === 'mfa_required' || errorData.error_code === 'mfa_setup_required') {
        const operationType = errorData.operation_type || 'fund_management';
        const returnUrl = window.location.pathname;
        
        window.location.href = `/mfa-setup?operation=${operationType}&returnUrl=${encodeURIComponent(returnUrl)}`;
        throw new Error('MFA_REDIRECT');
      }
    }
    
    throw error;
  }
};

export const getFundComplianceData = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    if (filters.start_date) {
      queryParams.append('start_date', filters.start_date);
    }

    if (filters.end_date) {
      queryParams.append('end_date', filters.end_date);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/api/reports/compliance/${queryString}`);
    return response.data;

  } catch (error) {
    if (error.response?.data) {
      const errorData = error.response.data;
      
      if (errorData.error_code === 'mfa_required' || errorData.error_code === 'mfa_setup_required') {
        const operationType = errorData.operation_type || 'fund_management';
        const returnUrl = window.location.pathname;
        
        window.location.href = `/mfa-setup?operation=${operationType}&returnUrl=${encodeURIComponent(returnUrl)}`;
        throw new Error('MFA_REDIRECT');
      }
    }
    
    throw error;
  }
};

export const getFundFlowAnalysisData = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    if (filters.start_date) {
      queryParams.append('start_date', filters.start_date);
    }

    if (filters.end_date) {
      queryParams.append('end_date', filters.end_date);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/api/reports/flow-analysis/${queryString}`);
    return response.data;

  } catch (error) {
    if (error.response?.data) {
      const errorData = error.response.data;
      
      if (errorData.error_code === 'mfa_required' || errorData.error_code === 'mfa_setup_required') {
        const operationType = errorData.operation_type || 'fund_management';
        const returnUrl = window.location.pathname;
        
        window.location.href = `/mfa-setup?operation=${operationType}&returnUrl=${encodeURIComponent(returnUrl)}`;
        throw new Error('MFA_REDIRECT');
      }
    }
    
    throw error;
  }
};

export const exportFundReportPDF = async (filters = {}) => {
  try {
    // ADDED: Frontend validation for supported report types
    const supportedReportTypes = ['utilization', 'compliance', 'predictive', 'flow', 'comprehensive'];
    if (filters.report_type && !supportedReportTypes.includes(filters.report_type)) {
      throw new Error(`Unsupported report type: ${filters.report_type}. Supported types: ${supportedReportTypes.join(', ')}`);
    }

    const queryParams = new URLSearchParams();

    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    if (filters.start_date) {
      queryParams.append('start_date', filters.start_date);
    }

    if (filters.end_date) {
      queryParams.append('end_date', filters.end_date);
    }

    if (filters.report_type) {
      queryParams.append('report_type', filters.report_type);
    }                                                                                                                                       
                                                                                                                                            
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';                                                         
    const response = await api.get(`/fund-management/api/reports/export/pdf/${queryString}`, {                                              
      responseType: 'blob'                                                                                                                  
    });                                                                                                                                     
                                                                                                                                            
    return response.data;                                                                                                                   
                                                                                                                                            
  } catch (error) {                                                                                                                         
    if (error.response?.data) {                                                                                                             
      const errorData = error.response.data;                                                                                                
                                                                                                                                            
      if (errorData.error_code === 'mfa_required' || errorData.error_code === 'mfa_setup_required') {                                       
        const operationType = errorData.operation_type || 'fund_management';
        const returnUrl = window.location.pathname;                                                                                         
                                                                                                                                            
        window.location.href = `/mfa-setup?operation=${operationType}&returnUrl=${encodeURIComponent(returnUrl)}`;                          
        throw new Error('MFA_REDIRECT');                                                                                                    
      }                                                                                                                                     
    }                                                                                                                                       
                                                                                                                                            
    throw error;                                                                                                                            
  }                                                                                                                                         
};

/**
 * Enhanced transaction validation with comprehensive checks
 * @param {Object} transactionData - Complete transaction data
 * @returns {Promise} - Comprehensive validation results
 */
export const performComprehensiveValidation = async (transactionData) => {
  try {
    // Perform multiple validations in parallel
    const validationPromises = [
      validateTransaction(transactionData),
      calculateRiskScore(transactionData)
    ];

    // Add conditional validations based on permissions or requirements
    if (transactionData.amount > 5000) {
      validationPromises.push(getBudgetImpactCheck(transactionData));
    }

    if (transactionData.department && transactionData.branch) {
      validationPromises.push(getDepartmentLimits({
        department: transactionData.department,
        branch: transactionData.branch
      }));
      validationPromises.push(getApprovalWorkflow(transactionData));
    }

    const results = await Promise.allSettled(validationPromises);

    // Process results
    const validationResult = {
      basic_validation: null,
      risk_assessment: null,
      budget_impact: null,
      department_limits: null,
      approval_workflow: null,
      overall_status: 'VALID',
      critical_issues: [],
      warnings: [],
      recommendations: []
    };

    // Process basic validation
    if (results[0].status === 'fulfilled') {
      validationResult.basic_validation = results[0].value.data;
      if (!results[0].value.data.is_valid) {
        validationResult.overall_status = 'INVALID';
        validationResult.critical_issues.push(...results[0].value.data.errors);
      }
      validationResult.warnings.push(...(results[0].value.data.warnings || []));
    }

    // Process risk assessment
    if (results[1].status === 'fulfilled') {
      validationResult.risk_assessment = results[1].value.data;
      if (results[1].value.data.score >= 75) {
        validationResult.overall_status = 'HIGH_RISK';
      }
      validationResult.recommendations.push(...(results[1].value.data.recommendations || []));
    }

    // Process additional validations if they were performed
    let resultIndex = 2;
    if (transactionData.amount > 5000 && results[resultIndex]) {
      if (results[resultIndex].status === 'fulfilled') {
        validationResult.budget_impact = results[resultIndex].value.data;
      }
      resultIndex++;
    }

    if (transactionData.department && transactionData.branch) {
      if (results[resultIndex] && results[resultIndex].status === 'fulfilled') {
        validationResult.department_limits = results[resultIndex].value.data;
      }
      resultIndex++;

      if (results[resultIndex] && results[resultIndex].status === 'fulfilled') {
        validationResult.approval_workflow = results[resultIndex].value.data;
      }
    }

    return {
      data: validationResult
    };

  } catch (error) {
    console.error('Error in performComprehensiveValidation:', error);
    throw error;
  }
};

/**
 * Get all attachment types
 * @returns {Promise<AxiosResponse>} Response with attachment types data
 */
export const getAttachmentTypes = async () => {
  try {
    return await api.get('/attachment-types/');
  } catch (error) {
    console.error("Error fetching attachment types:", error);
    throw error;
  }
};

/**
 * Delete a fund transaction by ID
 *
 * @param {string|number} id - The transaction ID to delete
 * @returns {Promise} - API response
 */
export const deleteFundTransaction = async (id) => {
  try {
    return await api.delete(`/fund-management/transactions/${id}/`);
  } catch (error) {
    console.error('Error in deleteFundTransaction:', error);
    throw error;
  }
};

/**
 * Export fund transactions in specified format (Excel or PDF)
 *
 * @param {Object} filters - Filter parameters
 * @param {string} filters.search - Search term
 * @param {string} filters.transaction_type - Type of transaction
 * @param {string} filters.start_date - Start date filter (YYYY-MM-DD)
 * @param {string} filters.end_date - End date filter (YYYY-MM-DD)
 * @param {string} filters.status - Status filter
 * @param {string} filters.format - Export format ('excel' or 'pdf')
 * @returns {Promise} - API response with file download
 */
export const exportFundTransactions = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.transaction_type) queryParams.append('transaction_type', filters.transaction_type);
    if (filters.department) queryParams.append('department', filters.department);
    if (filters.branch) queryParams.append('branch', filters.branch);
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.format) queryParams.append('format', filters.format);
    queryParams.append('operation_type', 'FUND_ACCOUNT_EXPORT_TRANSACTIONS');

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    const getMimeType = (format) => {
      switch (format) {
        case 'excel': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'csv':   return 'text/csv';
        case 'pdf':   return 'application/pdf';
        default:      return 'application/octet-stream';
      }
    };

    const getExtension = (format) => {
      switch (format) {
        case 'excel': return 'xlsx';
        case 'csv':   return 'csv';
        case 'pdf':   return 'pdf';
        default:      return 'bin';
      }
    };

    const mimeType = getMimeType(filters.format);

    const response = await api.get(`/fund-management/transactions/export/${queryString}`, {
      responseType: 'blob',
      headers: { 'Accept': mimeType },
      // Prevent the global interceptor from swallowing blob error responses
      validateStatus: (status) => status < 500
    });

    // If the response is not a success, parse the blob as text to surface the real error
    if (response.status !== 200) {
      const errorText = await response.data.text();
      let errorDetail;
      try {
        errorDetail = JSON.parse(errorText)?.detail || errorText;
      } catch {
        errorDetail = errorText;
      }
      throw new Error(`Export failed (${response.status}): ${errorDetail}`);
    }

    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `fund-transactions-${new Date().toISOString().split('T')[0]}.${getExtension(filters.format)}`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response;
  } catch (error) {
    console.error('Error in exportFundTransactions:', error);
    throw error;
  }
};

export const getFundTransactionSummary = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.transaction_type) queryParams.append('transaction_type', filters.transaction_type);
    if (filters.department) queryParams.append('department', filters.department);
    if (filters.branch) queryParams.append('branch', filters.branch);
    if (filters.status) queryParams.append('status', filters.status);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/fund-management/transactions/summary/${queryString}`);
  } catch (error) {
    console.error('Error in getFundTransactionSummary:', error);
    throw error;
  }
};

/**
 * Change the status of a fund transaction
 *
 * @param {string|number} id - The transaction ID
 * @param {Object} data - Status update data
 * @param {string} data.status - New status (PENDING, COMPLETED, REJECTED)
 * @param {string} data.remarks - Optional remarks for the status change
 * @returns {Promise} - API response
 */
export const changeFundTransactionStatus = async (id, data) => {
  try {
    return await api.patch(`/fund-management/transactions/${id}/change_status/`, data);
  } catch (error) {
    console.error('Error in changeFundTransactionStatus:', error);
    throw error;
  }
};

/**
 * Create a new fund transaction
 *
 * @param {Object} data - Transaction data
 * @returns {Promise} - API response
 */
export const createFundTransaction = async (data) => {
  try {
    return await api.post('/fund-management/transactions/', data);
  } catch (error) {
    console.error('Error in createFundTransaction:', error);
    throw error;
  }
};

/**
 * Update an existing fund transaction
 *
 * @param {string|number} id - The transaction ID to update
 * @param {Object} data - Updated transaction data
 * @returns {Promise} - API response
 */
export const updateFundTransaction = async (id, data) => {
  try {
    return await api.put(`/fund-management/transactions/${id}/`, data);
  } catch (error) {
    console.error('Error in updateFundTransaction:', error);
    throw error;
  }
};

/**
 * Get transaction approval history
 *
 * @param {string|number} transactionId - The transaction ID
 * @returns {Promise} - API response with approval history
 */
export const getTransactionApprovals = async (transactionId) => {
  try {
    return await api.get(`/fund-management/transactions/${transactionId}/approvals/`);
  } catch (error) {
    console.error('Error in getTransactionApprovals:', error);
    throw error;
  }
};

export const getTransactionDetails = async (transactionId) => {
  try {
    return await api.get(`/fund-management/transactions/${transactionId}/`);
  } catch (error) {
    console.error('Error in getTransactionDetails:', error);
    throw error;
  }
};

export const getFundTransactionDetails = async (transactionId, context = {}) => {
  try {
    // Build query parameters for context (branch_id, department_id)
    const params = new URLSearchParams();

    if (context.branch_id) {
      params.append('branch_id', context.branch_id);
    }

    if (context.department_id) {
      params.append('department_id', context.department_id);
    }

    // Construct the URL - using the retrieve action of FundTransactionViewSet
    const url = `/fund-management/transactions/${transactionId}/`;
    const queryString = params.toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;

    const response = await axiosInstance.get(fullUrl);
    return response;
  } catch (error) {
    console.error('Error fetching fund transaction details:', error);
    throw error;
  }
};

export const getFundTransactionApprovals = async (filters = {}) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();

    // Add filters to query params if they exist
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.ordering) queryParams.append('ordering', filters.ordering);
    if (filters.transaction_type) queryParams.append('transaction_type', filters.transaction_type);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.page_size) queryParams.append('page_size', filters.page_size || 10);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/fund-management/approvals/${queryString}`);
  } catch (error) {
    console.error('Error in getFundTransactionApprovals:', error);
    throw error;
  }
};

export const getFundRetirements = async (filters = {}, page = 1) => {
  try {
    // Create query params from filters, removing undefined values
    const queryParams = new URLSearchParams();

    if (filters.is_complete !== undefined) {
      queryParams.append('is_complete', filters.is_complete);
    }

    if (filters.search) {
      queryParams.append('search', filters.search);
    }

    if (filters.ordering) {
      queryParams.append('ordering', filters.ordering);
    }

    if (filters.start_date) {
      queryParams.append('start_date', filters.start_date);
    }

    if (filters.end_date) {
      queryParams.append('end_date', filters.end_date);
    }

    // Add page parameter for pagination
    queryParams.append('page', page);

    const response = await axiosInstance.get(`/fund-management/retirements/?${queryParams.toString()}`);
    return response;
  } catch (error) {
    console.error('Error fetching fund retirements:', error);
    throw error;
  }
};

export const getFundRetirementDetails = (id) =>
  api.get(`/fund-management/retirements/${id}/`);

/**
 * Mark a fund retirement as complete
 * @param {string|number} id - ID of the fund retirement to mark as complete
 * @returns {Promise} - Promise resolving to the API response
 */
export const markFundRetirementComplete = async (id) => {
  try {
    const response = await axiosInstance.post(`/fund-management/retirements/${id}/mark_complete/`, {});
    return response;
  } catch (error) {
    console.error(`Error marking fund retirement ${id} as complete:`, error);
    throw error;
  }
};

/**
 * Delete a fund retirement
 * @param {string|number} id - ID of the fund retirement to delete
 * @returns {Promise} - Promise resolving to the API response
 */
export const deleteFundRetirement = async (id) => {
  try {
    const response = await axiosInstance.delete(`/fund-management/retirements/${id}/`);
    return response;
  } catch (error) {
    console.error(`Error deleting fund retirement ${id}:`, error);
    throw error;
  }
};

/**
 * Add a receipt to a fund retirement
 * @param {string|number} id - ID of the fund retirement to add a receipt to
 * @param {Object} receiptData - Data for the receipt
 * @returns {Promise} - Promise resolving to the API response
 */
export const addFundRetirementReceipt = async (id, receiptData) => {
  try {
    const response = await axiosInstance.post(`/fund-management/retirements/${id}/add_receipt/`, {
      receipt: receiptData
    });
    return response;
  } catch (error) {
    console.error(`Error adding receipt to fund retirement ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new fund retirement
 * @param {Object} retirementData - Data for the new fund retirement
 * @returns {Promise} - Promise resolving to the API response
 */
export const createFundRetirement = async (retirementData) => {
  try {
    const response = await axiosInstance.post('/fund-management/retirements/', retirementData);
    return response;
  } catch (error) {
    console.error('Error creating fund retirement:', error);
    throw error;
  }
};

/**
 * Update an existing fund retirement
 * @param {string|number} id - ID of the fund retirement to update
 * @param {Object} retirementData - Updated data for the fund retirement
 * @returns {Promise} - Promise resolving to the API response
 */
export const updateFundRetirement = async (id, retirementData) => {
  try {
    const response = await axiosInstance.patch(`/fund-management/retirements/${id}/`, retirementData);
    return response;
  } catch (error) {
    console.error(`Error updating fund retirement ${id}:`, error);
    throw error;
  }
};

/**
 * Get a single fund retirement by ID
 * @param {string|number} id - ID of the fund retirement to fetch
 * @returns {Promise} - Promise resolving to the API response
 */
export const getFundRetirementById = async (id) => {
  try {
    const response = await axiosInstance.get(`/fund-management/retirements/${id}/`);
    return response;
  } catch (error) {
    console.error(`Error fetching fund retirement ${id}:`, error);
    throw error;
  }
};

export const getFundApprovals = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add filters to query parameters
    if (filters.transaction_id) queryParams.append('transaction_id', filters.transaction_id);
    if (filters.approved !== undefined) queryParams.append('approved', filters.approved);
    if (filters.approval_level) queryParams.append('approval_level', filters.approval_level);
    if (filters.approver) queryParams.append('approver', filters.approver);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.ordering) queryParams.append('ordering', filters.ordering);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.page_size) queryParams.append('page_size', filters.page_size || 10); // optional
    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/fund-management/approvals/${queryString}`);
  } catch (error) {
    console.error('Error in getFundApprovals:', error);
    throw error;
  }
};

export const getFundApproval = async (id) => {
  try {
    return await axiosInstance.get(`/fund-management/approvals/${id}/`);
  } catch (error) {
    console.error(`Error fetching fund approval with ID ${id}:`, error);
    throw error;
  }
};

export const createFundApproval = async (approvalData) => {
  try {
    return await axiosInstance.post(`/fund-management/approvals/`, approvalData);
  } catch (error) {
    console.error('Error creating fund approval:', error);
    throw error;
  }
};

export const updateFundApproval = async (id, approvalData) => {
  try {
    return await axiosInstance.patch(`/fund-management/approvals/${id}/`, approvalData);
  } catch (error) {
    console.error(`Error updating fund approval with ID ${id}:`, error);
    throw error;
  }
};

export const deleteFundApproval = async (id) => {
  try {
    return await axiosInstance.delete(`/fund-management/approvals/${id}/`);
  } catch (error) {
    console.error(`Error deleting fund approval with ID ${id}:`, error);
    throw error;
  }
};

export const approveFundTransaction = async (approvalData) => {
  try {
    return await axiosInstance.post(`/fund-management/approvals/approve_transaction/`, approvalData);
  } catch (error) {
    console.error('Error processing fund approval:', error);
    throw error;
  }
};

export const getFundApprovalHistory = async (approvalId, params = {}) => {
  try {
    // First, get the approval to extract the transaction_id
    const approvalResponse = await axiosInstance.get(
      `/fund-management/approvals/${approvalId}/`,
      { params }
    );
    
    const approval = approvalResponse.data;
    const transactionId = approval.transaction_id;
    
    if (!transactionId) {
      throw new Error('Transaction ID not found in approval');
    }
    
    // Now get the workflow which contains the history
    const workflowResponse = await axiosInstance.get(
      `/fund-management/approvals/workflow/${transactionId}/`,
      { params }
    );
    
    // Transform workflow steps into history format
    const workflowSteps = workflowResponse.data.workflow_steps || [];
    
    // Convert workflow steps to history records format
    const historyRecords = workflowSteps.map((step, index) => ({
      id: step.approval_id || `step-${index}`,
      timestamp: step.date,
      action: step.status === 'completed' ? 'APPROVED' : 
               step.status === 'rejected' ? 'REJECTED' : 
               step.status === 'pending' ? 'PENDING_REVIEW' : 'CREATED',
      user: {
        username: step.approver
      },
      details: step.comments || step.description,
      branch_name: workflowResponse.data.metadata?.branch || 'N/A',
      department_name: workflowResponse.data.metadata?.department || 'N/A'
    }));
    
    // Return in the expected format
    return {
      data: {
        results: historyRecords
      }
    };
    
  } catch (error) {
    console.error('Error fetching fund approval history:', error);
    throw error;
  }
};

export const getDepartmentLimits = async (departmentId, branchId) => {
  try {
    // Don't make the call if required IDs are missing
    if (!departmentId || departmentId === '' || departmentId === null) {
      throw new Error('Department ID is required');
    }

    const queryParams = new URLSearchParams();
    queryParams.append('department_id', departmentId);
    
    // Only add branch_id if it's not empty
    if (branchId && branchId !== '' && branchId !== null) {
      queryParams.append('branch_id', branchId);
    }

    const queryString = queryParams.toString();
    const response = await axiosInstance.get(`/fund-management/transactions/department_limits/?${queryString}`);

    console.log('getDepartmentLimits response:', response);
    console.log('Response data:', response.data);

    return response;
  } catch (error) {
    console.error('Error in getDepartmentLimits:', error);
    throw error;
  }
};

// Get Department P&L Metrics
export const getDepartmentPnLMetrics = async (params) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.department_id) queryParams.append('department_id', params.department_id);
    if (params.branch_id) queryParams.append('branch_id', params.branch_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.period_type) queryParams.append('period_type', params.period_type);
    if (params.fund_source_id) queryParams.append('fund_source_id', params.fund_source_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/fund-management/department-pnl/metrics/${queryString}`);
  } catch (error) {
    console.error('Error in getDepartmentPnLMetrics:', error);
    throw error;
  }
};

export const getDepartmentBudgets = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Filter by department
    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    // Filter by fund source
    if (filters.fund_source_id) {
      queryParams.append('fund_source_id', filters.fund_source_id);
    }

    // Filter by branch
    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    // Get current active budgets
    if (filters.current_budgets) {
      queryParams.append('current_budgets', 'true');
    }

    // Filter by active status
    if (filters.is_active !== undefined) {
      queryParams.append('is_active', filters.is_active);
    }

    // Date range filters
    if (filters.period_start) {
      queryParams.append('period_start', filters.period_start);
    }

    if (filters.period_end) {
      queryParams.append('period_end', filters.period_end);
    }

    // Ordering
    if (filters.ordering) {
      queryParams.append('ordering', filters.ordering);
    }

    // Pagination
    if (filters.page) {
      queryParams.append('page', filters.page);
    }

    if (filters.limit) {
      queryParams.append('limit', filters.limit);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/department-budgets/${queryString}`);

    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

export const getDepartmentBudgetStatus = async (departmentId, branchId = null, includeCategories = false) => {
  try {
    const params = {
      department_id: departmentId
    };

    if (branchId) {
      params.branch_id = branchId;
    }

    if (includeCategories) {
      params.include_categories = 'true';
    }

    return await api.get('/fund-management/department-budgets/department_status/', { params });
  } catch (error) {
    console.error('Error in getDepartmentBudgetStatus:', error);
    throw error;
  }
};

export const getBudgetImpactCheck = async (data) => {
  try {
    // Validate required fields before making API call
    if (!data.department || data.department === '' || data.department === null) {
      throw new Error('Department is required for budget impact check');
    }

    const params = {
      department_id: data.department,
      transaction_type: data.transaction_type,
      amount: data.amount
    };

    // Only add branch_id if it's not empty
    if (data.branch && data.branch !== '' && data.branch !== null) {
      params.branch_id = data.branch;
    }

    // Only add fund_source_id if it exists
    if (data.source && data.source !== '' && data.source !== null) {
      params.fund_source_id = data.source;
    }

    return await api.get('/fund-management/transactions/budget_impact_check/', { params });
  } catch (error) {
    console.error('Error in getBudgetImpactCheck:', error);
    throw error;
  }
};

export const getFundSourceBalanceSnapshot = async (fundSourceId, departmentId) => {
  try {
    const params = { fund_source_id: fundSourceId };
    if (departmentId) {
      params.department_id = departmentId;
    }
    return await api.get('/fund-management/transactions/balance_snapshot/', { params });
  } catch (error) {
    console.error('Error in getFundSourceBalanceSnapshot:', error);
    throw error;
  }
};

export const getFundSourcePnLImpact = async (fundSourceId, departmentId, startDate, endDate) => {
  try {
    const params = { fund_source_id: fundSourceId };
    if (departmentId) {
      params.department_id = departmentId;
    }
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }
    return await api.get('/fund-management/transactions/pnl_impact/', { params });
  } catch (error) {
    console.error('Error in getFundSourcePnLImpact:', error);
    throw error;
  }
};

export const getConsolidatedFinancial = async (params = {}) => {
  try {
    // Handle executive dashboard endpoint
    if (params.executive_dashboard === true) {
      const url = `/fund-management/consolidated-financial/executive_dashboard/`;
      const response = await api.get(url);
      return response;
    }

    const queryParams = new URLSearchParams();

    // Map 'branch' param to 'branch_id' for the API
    const branchId = params.branch || params.branch_id;
    if (branchId) {
      queryParams.append('branch_id', branchId);
    }

    let period_start, period_end;
    const currentDate = new Date();

    if (params.period) {
      switch (params.period) {
        case 'daily':
          period_start = format(currentDate, 'yyyy-MM-dd');
          period_end = format(currentDate, 'yyyy-MM-dd');
          break;
        case 'weekly':
          // The { weekStartsOn: 1 } option sets Monday as the first day of the week
          // Adjust to 0 for Sunday if that's your convention
          period_start = format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
          period_end = format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
          break;
        case 'monthly':
          period_start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
          period_end = format(endOfMonth(currentDate), 'yyyy-MM-dd');
          break;
        case 'quarterly':
          period_start = format(startOfQuarter(currentDate), 'yyyy-MM-dd');
          period_end = format(endOfQuarter(currentDate), 'yyyy-MM-dd');
          break;
        case 'yearly':
          period_start = format(startOfYear(currentDate), 'yyyy-MM-dd');
          period_end = format(endOfYear(currentDate), 'yyyy-MM-dd');
          break;
        default:
          // Use default values if an invalid period is provided
          period_start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
          period_end = format(endOfMonth(currentDate), 'yyyy-MM-dd');
          break;
      }
    } else {
      // If no period is specified, but custom dates are, use them
      period_start = params.period_start;
      period_end = params.period_end;
    }

    if (period_start) {
      queryParams.append('period_start', period_start);
    }
    if (period_end) {
      queryParams.append('period_end', period_end);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const url = `/fund-management/consolidated-financial/branch_summary/${queryString}`;

    const response = await api.get(url);
    return response;
  } catch (error) {
    if (error.response?.data?.requires_mfa) {
      return {
        error: true,
        requiresMfa: true,
        status: error.response.status,
        data: error.response.data
      };
    }
    console.error("Error fetching consolidated financial data:", error);
    throw error;
  }
};

/**
 * Get department P&L records with filtering
 * Maps to: /fund-management/department-pnl/
 */
export const getDepartmentPnL = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Filter by department
    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    // Filter by fund source
    if (filters.fund_source_id) {
      queryParams.append('fund_source_id', filters.fund_source_id);
    }

    // Filter by branch
    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    // Get current period data
    if (filters.current_period) {
      queryParams.append('current_period', 'true');
    }

    // Date range filters
    if (filters.period_start) {
      queryParams.append('period_start', filters.period_start);
    }

    if (filters.period_end) {
      queryParams.append('period_end', filters.period_end);
    }

    // Number of months to retrieve
    if (filters.months) {
      queryParams.append('months', filters.months);
    }

    // Filter by finalized status
    if (filters.is_final !== undefined) {
      queryParams.append('is_final', filters.is_final);
    }

    // Ordering
    if (filters.ordering) {
      queryParams.append('ordering', filters.ordering);
    }

    // Pagination
    if (filters.page) {
      queryParams.append('page', filters.page);
    }

    if (filters.limit) {
      queryParams.append('limit', filters.limit);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/department-pnl/${queryString}`);

    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

export const getDepartmentPLImpact = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    if (filters.period_type) {
      queryParams.append('period_type', filters.period_type);
    }

    if (filters.include_retirement_impact) {
      queryParams.append('include_retirement_impact', 'true');
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/department-pnl/department_pl_impact/${queryString}`);

    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

/**
 * Get fund balance snapshots
 * Maps to: /fund-management/balance-snapshots/
 */
export const getFundBalanceSnapshots = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Filter by department
    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    // Filter by fund source (assuming this maps to account_id in the backend)
    if (filters.fund_source_id) {
      queryParams.append('fund_source_id', filters.fund_source_id);
    }

    // Filter by branch
    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    // Date range filters
    if (filters.date_from) {
      queryParams.append('date_from', filters.date_from);
    }

    if (filters.date_to) {
      queryParams.append('date_to', filters.date_to);
    }

    // Verified only filter
    if (filters.verified_only !== undefined) {
      queryParams.append('verified_only', filters.verified_only ? 'true' : 'false');
    }

    // Ordering
    if (filters.ordering) {
      queryParams.append('ordering', filters.ordering);
    } else {
      // Default to most recent first
      queryParams.append('ordering', '-snapshot_time');
    }

    // Pagination
    if (filters.page) {
      queryParams.append('page', filters.page);
    }

    if (filters.limit) {
      queryParams.append('limit', filters.limit);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/balance-snapshots/${queryString}`);

    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

/**
 * Helper function to handle MFA redirects consistently
 */
const handleMFARedirect = (error) => {
  if (error.response?.data) {
    const errorData = error.response.data;

    if (errorData.error_code === 'mfa_required' || errorData.error_code === 'mfa_setup_required') {
      const operationType = errorData.operation_type || 'fund_management';
      const returnUrl = window.location.pathname;

      window.location.href = `/mfa-setup?operation=${operationType}&returnUrl=${encodeURIComponent(returnUrl)}`;

      throw new Error('MFA_REDIRECT');
    }
  }
};

// Get current budgets (uses the custom action)
export const getCurrentBudgets = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    if (filters.fund_source_id) {
      queryParams.append('fund_source_id', filters.fund_source_id);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/department-budgets/current_budgets/${queryString}`);

    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

export const getBudgetAlerts = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    if (filters.status && Array.isArray(filters.status)) {
      filters.status.forEach(status => {
        queryParams.append('status', status);
      });
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/department-budgets/budget_alerts/${queryString}`);

    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

// Get current period P&L (uses the custom action)
export const getCurrentPeriodPnL = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    if (filters.fund_source_id) {
      queryParams.append('fund_source_id', filters.fund_source_id);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/department-pnl/current_period/${queryString}`);

    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

// Calculate P&L for a specific period
export const calculatePnL = async (data) => {
  try {
    const response = await api.post('/fund-management/department-pnl/calculate_pnl/', data);
    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

/**
 * Finalize P&L record (POST request)
 * @param {string|number} pnlId - P&L record ID
 */
export const finalizeDepartmentPnL = async (pnlId) => {
  try {
    const url = `/department-pnl/${pnlId}/finalize_pnl/`;
    const response = await api.post(url);
    return response;
  } catch (error) {
    if (error.response?.data?.requires_mfa) {
      return {
        error: true,
        requiresMfa: true,
        status: error.response.status,
        data: error.response.data
      };
    }

    console.error("Error finalizing department P&L:", error);
    throw error;
  }
};

// Adjust budget amount
export const adjustBudget = async (budgetId, data) => {
  try {
    const response = await api.post(`/fund-management/department-budgets/${budgetId}/adjust_budget/`, data);
    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

// Commit funds from budget
export const commitFunds = async (budgetId, data) => {
  try {
    const response = await api.post(`/fund-management/department-budgets/${budgetId}/commit_funds/`, data);
    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

export const getFundAlerts = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add filters to query parameters based on your backend FundAlertViewSet
    if (filters.is_acknowledged !== undefined) queryParams.append('is_acknowledged', filters.is_acknowledged);
    if (filters.is_resolved !== undefined) queryParams.append('is_resolved', filters.is_resolved);
    if (filters.alert_type) queryParams.append('alert_type', filters.alert_type);
    if (filters.severity) queryParams.append('severity', filters.severity);
    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.ordering) queryParams.append('ordering', filters.ordering);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.page_size) queryParams.append('page_size', filters.page_size || 10);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/fund-management/alerts/${queryString}`);
  } catch (error) {
    console.error('Error in getFundAlerts:', error);
    throw error;
  }
};

export const getFundEmergencyControls = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add filters based on your FundEmergencyControlViewSet
    if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);
    if (filters.freeze_type) queryParams.append('freeze_type', filters.freeze_type);
    if (filters.is_frozen !== undefined) queryParams.append('is_frozen', filters.is_frozen);
    if (filters.freeze_reason) queryParams.append('freeze_reason', filters.freeze_reason);
    if (filters.frozen_by) queryParams.append('frozen_by', filters.frozen_by);
    if (filters.branch) queryParams.append('branch', filters.branch);
    if (filters.department) queryParams.append('department', filters.department);
    if (filters.account) queryParams.append('account', filters.account);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.ordering) queryParams.append('ordering', filters.ordering);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.page_size) queryParams.append('page_size', filters.page_size || 10);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/fund-management/emergency-controls/${queryString}`);
  } catch (error) {
    console.error('Error in getFundEmergencyControls:', error);
    throw error;
  }
};

// Additional emergency control actions
export const createEmergencySystemFreeze = async (data) => {
  try {
    return await axiosInstance.post('/fund-management/emergency-controls/emergency_system_freeze/', data);
  } catch (error) {
    console.error('Error creating emergency system freeze:', error);
    throw error;
  }
};

export const liftEmergencyFreeze = async (freezeId, data) => {
  try {
    return await axiosInstance.post(`/fund-management/emergency-controls/${freezeId}/lift_freeze/`, data);
  } catch (error) {
    console.error('Error lifting emergency freeze:', error);
    throw error;
  }
};

export const checkComplianceRules = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.department_id) {
      queryParams.append('department_id', filters.department_id);
    }

    if (filters.branch_id) {
      queryParams.append('branch_id', filters.branch_id);
    }

    if (filters.check_type) {
      queryParams.append('check_type', filters.check_type);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await api.get(`/fund-management/compliance-rules/check_compliance/${queryString}`);

    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

export const getComplianceRuleDetails = async (ruleId) => {
  try {
    const response = await api.get(`/fund-management/compliance-rules/${ruleId}/`);
    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

// Test Compliance Rule
export const testComplianceRule = async (ruleId, transactionData) => {
  try {
    const response = await api.post(`/fund-management/compliance-rules/${ruleId}/test_rule/`, {
      transaction_data: transactionData
    });

    return response;
  } catch (error) {
    handleMFARedirect(error);
    throw error;
  }
};

export const subscribeToTransactionUpdates = (callback) => {
  const { subscribeToUpdates } = require('./websocket');

  return subscribeToUpdates((data) => {
    if (data.type === 'fund_transaction_update' || data.event_type === 'transaction_update') {
      callback(data);
    }
  });
};

export const getAuditLogs = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add pagination
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.page_size) queryParams.append('page_size', filters.page_size);

    // Add filters based on backend viewset
    if (filters.user_id) queryParams.append('user_id', filters.user_id);
    if (filters.action) queryParams.append('action', filters.action);
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.content_type_id) queryParams.append('content_type_id', filters.content_type_id);
    if (filters.object_id) queryParams.append('object_id', filters.object_id);
    if (filters.risk_level) queryParams.append('risk_level', filters.risk_level);
    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/audit-logs/${queryString}`);
  } catch (error) {
    console.error('Error in getAuditLogs:', error);
    throw error;
  }
};

export const getAuditLogsSummary = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/audit-logs/branch_department_summary/${queryString}`);
  } catch (error) {
    console.error('Error in getAuditLogsSummary:', error);
    throw error;
  }
};

export const getRealTimeActivity = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/audit-logs/real_time_activity/${queryString}`);
  } catch (error) {
    console.error('Error in getRealTimeActivity:', error);
    throw error;
  }
};

export const getComplianceMetrics = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/audit-logs/compliance_metrics/${queryString}`);
  } catch (error) {
    console.error('Error in getComplianceMetrics:', error);
    throw error;
  }
};

export const getComplianceAlerts = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add pagination
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.page_size) queryParams.append('page_size', filters.page_size);

    // Add filters based on backend viewset
    if (filters.is_resolved !== undefined) queryParams.append('is_resolved', filters.is_resolved);
    if (filters.severity) queryParams.append('severity', filters.severity);
    if (filters.alert_type) queryParams.append('alert_type', filters.alert_type);
    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/compliance-alerts/${queryString}`);
  } catch (error) {
    console.error('Error in getComplianceAlerts:', error);
    throw error;
  }
};

export const getComplianceAlertsSummary = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/compliance-alerts/dashboard_summary/${queryString}`);
  } catch (error) {
    console.error('Error in getComplianceAlertsSummary:', error);
    throw error;
  }
};

export const resolveComplianceAlert = async (alertId, resolutionData) => {
  try {
    return await axiosInstance.post(`/audit-compliance/api/compliance-alerts/${alertId}/resolve_alert/`, resolutionData);
  } catch (error) {
    console.error('Error in resolveComplianceAlert:', error);
    throw error;
  }
};

export const getRealTimeAlerts = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/alerts/real-time/${queryString}`);
  } catch (error) {
    console.error('Error in getRealTimeAlerts:', error);
    throw error;
  }
};

export const getComplianceChecks = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add pagination
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.page_size) queryParams.append('page_size', filters.page_size);

    // Add filters based on backend viewset
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.requirement_id) queryParams.append('requirement_id', filters.requirement_id);
    if (filters.overdue) queryParams.append('overdue', filters.overdue);
    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/compliance-checks/${queryString}`);
  } catch (error) {
    console.error('Error in getComplianceChecks:', error);
    throw error;
  }
};

export const getComplianceChecksDashboard = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/compliance-checks/compliance_dashboard/${queryString}`);
  } catch (error) {
    console.error('Error in getComplianceChecksDashboard:', error);
    throw error;
  }
};

export const createComplianceCheck = async (checkData) => {
  try {
    return await axiosInstance.post('/audit-compliance/api/compliance-checks/', checkData);
  } catch (error) {
    console.error('Error in createComplianceCheck:', error);
    throw error;
  }
};

export const updateComplianceCheck = async (checkId, checkData) => {
  try {
    return await axiosInstance.put(`/audit-compliance/api/compliance-checks/${checkId}/`, checkData);
  } catch (error) {
    console.error('Error in updateComplianceCheck:', error);
    throw error;
  }
};

export const getComplianceCheck = async (checkId) => {
  try {
    return await axiosInstance.get(`/audit-compliance/api/compliance-checks/${checkId}/`);
  } catch (error) {
    console.error('Error in getComplianceCheck:', error);
    throw error;
  }
};

export const checkCompliance = async (checkType, complianceData) => {
  try {
    const response = await api.post('/audit-compliance/api/compliance-checks/check_compliance/', {
      check_type: checkType,
      ...complianceData
    });
    return response.data;
  } catch (error) {
    console.error('Error checking compliance:', error);
    throw error;
  }
};

export const getDashboardStats = async (filters = {}) => {
  console.log('🔍 getDashboardStats called with filters:', filters);
  
  try {
    const queryParams = new URLSearchParams();
    
    // Only add if they have actual values (not empty strings)
    if (filters.branch_id && filters.branch_id !== '') {
      queryParams.append('branch_id', filters.branch_id);
    }
    if (filters.department_id && filters.department_id !== '') {
      queryParams.append('department_id', filters.department_id);
    }
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    console.log('🔍 Query string:', queryString);
    
    console.log('🌐 Making API calls...');
    
    const [alertsSummary, checksDashboard, complianceMetrics] = await Promise.all([
      getComplianceAlertsSummary(filters),
      axiosInstance.get(`/audit-compliance/api/compliance-checks/compliance_dashboard/${queryString}`),
      axiosInstance.get(`/audit-compliance/api/audit-logs/compliance_metrics/${queryString}`)
    ]);

    console.log('✅ API Responses received:');
    console.log('📊 Alerts Summary:', alertsSummary.data);
    console.log('📊 Checks Dashboard:', checksDashboard.data);
    console.log('📊 Compliance Metrics:', complianceMetrics.data);

    const stats = {
      total_alerts: alertsSummary.data.total_alerts || 0,
      critical_alerts: alertsSummary.data.critical_alerts || 0,
      pending_checks: checksDashboard.data.overdue_checks || 0,
      compliance_score: checksDashboard.data.compliance_rate || 0,
      recent_activities: complianceMetrics.data.total_activities || 0,
      risk_score: complianceMetrics.data.high_risk_activities > 10 ? 'HIGH' :
                 complianceMetrics.data.high_risk_activities > 5 ? 'MEDIUM' : 'LOW',
      branches_monitored: checksDashboard.data.branch_performance?.length || 0,
      departments_monitored: checksDashboard.data.status_summary?.length || 0
    };

    console.log('✨ Final transformed stats:', stats);
    return { data: stats };
    
  } catch (error) {
    console.error('❌ Error in getDashboardStats:', error);
    console.error('❌ Error response:', error.response?.data);
    throw error;
  }
};

export const getComplianceRequirements = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.page) queryParams.append('page', filters.page);
    if (filters.page_size) queryParams.append('page_size', filters.page_size);
    if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);
    if (filters.branch_id) queryParams.append('branch_id', filters.branch_id);
    if (filters.department_id) queryParams.append('department_id', filters.department_id);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/compliance-requirements/${queryString}`);
  } catch (error) {
    console.error('Error in getComplianceRequirements:', error);
    throw error;
  }
};

export const generateComplianceReport = async (reportFilters) => {
  try {
    const queryParams = new URLSearchParams();

    if (reportFilters.branch_id) queryParams.append('branch_id', reportFilters.branch_id);
    if (reportFilters.department_id) queryParams.append('department_id', reportFilters.department_id);
    if (reportFilters.start_date) queryParams.append('start_date', reportFilters.start_date);
    if (reportFilters.end_date) queryParams.append('end_date', reportFilters.end_date);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`/audit-compliance/api/reports/generate/${queryString}`);
  } catch (error) {
    console.error('Error in generateComplianceReport:', error);
    throw error;
  }
};

export const createAuditTrail = async (auditData) => {
  try {
    
    const payload = {
      module: auditData.module || 'general',
      action: auditData.action || 'general',
      data: auditData.data || {},
      change_reason: auditData.change_reason || '',
      object_id: auditData.data?.requisition_id || 'unknown',
      field_name: auditData.action || 'general_action',
      old_value: '',  // Required by model
      new_value: JSON.stringify(auditData.data || {}),
    };

    const response = await api.post('/audit-compliance/api/audit-trails/', payload);
    return response;
  } catch (error) {
    console.error('Error creating audit trail:', error);
    throw error;
  }
};

export const getAuditTrail = async (params) => {
  try {
    const queryParams = new URLSearchParams();

    // Add parameters that match your backend filtering
    if (params.model_name) {
      // Convert model name to content_type lookup
      queryParams.append('content_type', params.model_name.toLowerCase());
    }
    if (params.object_id) {
      queryParams.append('object_id', params.object_id);
    }
    if (params.branch_id) {
      queryParams.append('branch_id', params.branch_id);
    }
    if (params.department_id) {
      queryParams.append('department_id', params.department_id);
    }
    if (params.user_id) {
      queryParams.append('changed_by', params.user_id);
    }
    if (params.start_date) {
      queryParams.append('start_date', params.start_date);
    }
    if (params.end_date) {
      queryParams.append('end_date', params.end_date);
    }
    if (params.field_name) {
      queryParams.append('field_name', params.field_name);
    }

    const response = await api.get(`/audit-compliance/api/audit-trails/?${queryParams.toString()}`);
    return response;
  } catch (error) {
    console.error('Error getting audit trail:', error);
    throw error;
  }
};

// Helper function to transform paginated API response to match component expectations
export const transformPaginatedResponse = (response) => {
  console.log('🔄 transformPaginatedResponse input:', response);
  
  // Check if response has data property
  if (response?.data) {
    console.log('🔄 Has response.data');
    
    // Django REST Framework pagination format
    if (response.data.results !== undefined) {
      console.log('�� DRF pagination format detected');
      return {
        results: response.data.results || [],
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous
      };
    }
    
    // Direct array response
    if (Array.isArray(response.data)) {
      console.log('🔄 Direct array format detected');
      return {
        results: response.data,
        count: response.data.length,
        next: null,
        previous: null
      };
    }
  }
  
  console.warn('⚠️ Unrecognized response format:', response);
  return {
    results: [],
    count: 0,
    next: null,
    previous: null
  };
};

// Helper function for exporting data
export const exportAuditData = async (dataType, filters = {}) => {
  try {
    let endpoint = '';
    switch (dataType.toLowerCase()) {
      case 'audit logs':
        endpoint = '/audit-compliance/api/audit-logs/';
        break;
      case 'compliance alerts':
        endpoint = '/audit-compliance/api/compliance-alerts/';
        break;
      case 'compliance checks':
        endpoint = '/audit-compliance/api/compliance-checks/';
        break;
      case 'dashboard summary':
        endpoint = '/audit-compliance/dashboard/export/';
        break;
      default:
        throw new Error(`Unknown export type: ${dataType}`);
    }

    const queryParams = new URLSearchParams({
      ...filters,
      format: 'csv',
      export: 'true'
    });

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await axiosInstance.get(`${endpoint}${queryString}`, {
      responseType: 'blob' // For file download
    });
  } catch (error) {
    console.error('Error in exportAuditData:', error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────
//  LOGISTICS — API SERVICE FUNCTIONS
// ── DRIVERS ──────────────────────────────────────────────────

export const fetchDrivers = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/drivers/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch drivers');
  } catch (error) {
    console.error('Error fetching drivers:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchDriverDetail = async (driverId) => {
  try {
    const response = await axiosInstance.get(`/logistics/drivers/${driverId}/`);
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch driver detail');
  } catch (error) {
    console.error('Error fetching driver detail:', error.response?.data || error.message);
    throw error;
  }
};

export const createDriver = async (data) => {
  try {
    const response = await axiosInstance.post('/logistics/drivers/', data);
    if (response.status === 201) return response.data;
    throw new Error('Failed to create driver');
  } catch (error) {
    console.error('Error creating driver:', error.response?.data || error.message);
    throw error;
  }
};

export const updateDriver = async (driverId, data) => {
  try {
    const response = await axiosInstance.patch(`/logistics/drivers/${driverId}/`, data);
    if (response.status === 200) return response.data;
    throw new Error('Failed to update driver');
  } catch (error) {
    console.error('Error updating driver:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteDriver = async (driverId) => {
  try {
    const response = await axiosInstance.delete(`/logistics/drivers/${driverId}/`);
    if (response.status === 204) return true;
    throw new Error('Failed to delete driver');
  } catch (error) {
    console.error('Error deleting driver:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchAvailableDrivers = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/drivers/available/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch available drivers');
  } catch (error) {
    console.error('Error fetching available drivers:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchLicenseExpiringSoonDrivers = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/drivers/license_expiring_soon/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch drivers with expiring licenses');
  } catch (error) {
    console.error('Error fetching expiring license drivers:', error.response?.data || error.message);
    throw error;
  }
};

export const updateDriverAvailability = async (driverId, availabilityStatus) => {
  try {
    const response = await axiosInstance.patch(
      `/logistics/drivers/${driverId}/update_availability/`,
      { availability_status: availabilityStatus }
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to update driver availability');
  } catch (error) {
    console.error('Error updating driver availability:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchDriverDeliveryHistory = async (driverId, params = {}) => {
  try {
    const response = await axiosInstance.get(
      `/logistics/drivers/${driverId}/delivery_history/`,
      { params }
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch driver delivery history');
  } catch (error) {
    console.error('Error fetching driver delivery history:', error.response?.data || error.message);
    throw error;
  }
};

// ── VEHICLES ─────────────────────────────────────────────────

export const fetchVehicles = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/vehicles/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch vehicles');
  } catch (error) {
    console.error('Error fetching vehicles:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchVehicleDetail = async (vehicleId) => {
  try {
    const response = await axiosInstance.get(`/logistics/vehicles/${vehicleId}/`);
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch vehicle detail');
  } catch (error) {
    console.error('Error fetching vehicle detail:', error.response?.data || error.message);
    throw error;
  }
};

export const createVehicle = async (data) => {
  try {
    const response = await axiosInstance.post('/logistics/vehicles/', data);
    if (response.status === 201) return response.data;
    throw new Error('Failed to create vehicle');
  } catch (error) {
    console.error('Error creating vehicle:', error.response?.data || error.message);
    throw error;
  }
};

export const updateVehicle = async (vehicleId, data) => {
  try {
    const response = await axiosInstance.patch(`/logistics/vehicles/${vehicleId}/`, data);
    if (response.status === 200) return response.data;
    throw new Error('Failed to update vehicle');
  } catch (error) {
    console.error('Error updating vehicle:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteVehicle = async (vehicleId) => {
  try {
    const response = await axiosInstance.delete(`/logistics/vehicles/${vehicleId}/`);
    if (response.status === 204) return true;
    throw new Error('Failed to delete vehicle');
  } catch (error) {
    console.error('Error deleting vehicle:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchAvailableVehicles = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/vehicles/available/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch available vehicles');
  } catch (error) {
    console.error('Error fetching available vehicles:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchMaintenanceDueVehicles = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/vehicles/maintenance_due/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch vehicles with maintenance due');
  } catch (error) {
    console.error('Error fetching maintenance due vehicles:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchInsuranceExpiringSoonVehicles = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/vehicles/insurance_expiring_soon/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch vehicles with expiring insurance');
  } catch (error) {
    console.error('Error fetching expiring insurance vehicles:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchVehicleMaintenanceHistory = async (vehicleId, params = {}) => {
  try {
    const response = await axiosInstance.get(
      `/logistics/vehicles/${vehicleId}/maintenance_history/`,
      { params }
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch vehicle maintenance history');
  } catch (error) {
    console.error('Error fetching vehicle maintenance history:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchVehicleDeliveryHistory = async (vehicleId, params = {}) => {
  try {
    const response = await axiosInstance.get(
      `/logistics/vehicles/${vehicleId}/delivery_history/`,
      { params }
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch vehicle delivery history');
  } catch (error) {
    console.error('Error fetching vehicle delivery history:', error.response?.data || error.message);
    throw error;
  }
};

// ── DELIVERY ROUTES ───────────────────────────────────────────

export const fetchDeliveryRoutes = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/routes/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch delivery routes');
  } catch (error) {
    console.error('Error fetching delivery routes:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchDeliveryRouteDetail = async (routeId) => {
  try {
    const response = await axiosInstance.get(`/logistics/routes/${routeId}/`);
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch delivery route detail');
  } catch (error) {
    console.error('Error fetching delivery route detail:', error.response?.data || error.message);
    throw error;
  }
};

export const createDeliveryRoute = async (data) => {
  try {
    const response = await axiosInstance.post('/logistics/routes/', data);
    if (response.status === 201) return response.data;
    throw new Error('Failed to create delivery route');
  } catch (error) {
    console.error('Error creating delivery route:', error.response?.data || error.message);
    throw error;
  }
};

export const updateDeliveryRoute = async (routeId, data) => {
  try {
    const response = await axiosInstance.patch(`/logistics/routes/${routeId}/`, data);
    if (response.status === 200) return response.data;
    throw new Error('Failed to update delivery route');
  } catch (error) {
    console.error('Error updating delivery route:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteDeliveryRoute = async (routeId) => {
  try {
    const response = await axiosInstance.delete(`/logistics/routes/${routeId}/`);
    if (response.status === 204) return true;
    throw new Error('Failed to delete delivery route');
  } catch (error) {
    console.error('Error deleting delivery route:', error.response?.data || error.message);
    throw error;
  }
};

// ── DELIVERIES ────────────────────────────────────────────────

export const fetchDeliveries = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/deliveries/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch deliveries');
  } catch (error) {
    console.error('Error fetching deliveries:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchDeliveryDetail = async (deliveryId) => {
  try {
    const response = await axiosInstance.get(`/logistics/deliveries/${deliveryId}/`);
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch delivery detail');
  } catch (error) {
    console.error('Error fetching delivery detail:', error.response?.data || error.message);
    throw error;
  }
};

export const createDelivery = async (data) => {
  try {
    const response = await axiosInstance.post('/logistics/deliveries/', data);
    if (response.status === 201) return response.data;
    throw new Error('Failed to create delivery');
  } catch (error) {
    console.error('Error creating delivery:', error.response?.data || error.message);
    throw error;
  }
};

export const updateDelivery = async (deliveryId, data) => {
  try {
    const response = await axiosInstance.patch(`/logistics/deliveries/${deliveryId}/`, data);
    if (response.status === 200) return response.data;
    throw new Error('Failed to update delivery');
  } catch (error) {
    console.error('Error updating delivery:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteDelivery = async (deliveryId) => {
  try {
    const response = await axiosInstance.delete(`/logistics/deliveries/${deliveryId}/`);
    if (response.status === 204) return true;
    throw new Error('Failed to delete delivery');
  } catch (error) {
    console.error('Error deleting delivery:', error.response?.data || error.message);
    throw error;
  }
};

export const voidDelivery = async (deliveryId, reason = '') => {
  try {
    const response = await axiosInstance.post(
      `/logistics/deliveries/${deliveryId}/void/`,
      { reason }
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to void delivery');
  } catch (error) {
    console.error('Error voiding delivery:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchActiveDeliveries = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/deliveries/active/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch active deliveries');
  } catch (error) {
    console.error('Error fetching active deliveries:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchOverdueDeliveries = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/deliveries/overdue/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch overdue deliveries');
  } catch (error) {
    console.error('Error fetching overdue deliveries:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchTodaysDeliveries = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/deliveries/scheduled_today/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch today\'s deliveries');
  } catch (error) {
    console.error('Error fetching today\'s deliveries:', error.response?.data || error.message);
    throw error;
  }
};

export const updateDeliveryStatus = async (deliveryId, status, failureReason = '') => {
  try {
    const payload = { status };
    if (failureReason) payload.failure_reason = failureReason;
    const response = await axiosInstance.patch(
      `/logistics/deliveries/${deliveryId}/update_status/`,
      payload
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to update delivery status');
  } catch (error) {
    console.error('Error updating delivery status:', error.response?.data || error.message);
    throw error;
  }
};

export const confirmLogisticsDelivery = async (deliveryId, data) => {
  try {
    const response = await axiosInstance.patch(
      `/logistics/deliveries/${deliveryId}/confirm_delivery/`,
      data
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to confirm delivery');
  } catch (error) {
    console.error('Error confirming delivery:', error.response?.data || error.message);
    throw error;
  }
};

export const recordDeliveryFee = async (deliveryId, paymentMethod) => {
  try {
    const response = await axiosInstance.post(
      `/logistics/deliveries/${deliveryId}/record_delivery_fee/`,
      { payment_method: paymentMethod }
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to record delivery fee');
  } catch (error) {
    console.error('Error recording delivery fee:', error.response?.data || error.message);
    throw error;
  }
};

export const recordDeliveryExpense = async (deliveryId, data) => {
  try {
    const response = await axiosInstance.post(
      `/logistics/deliveries/${deliveryId}/record_delivery_expense/`,
      data
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to record delivery expense');
  } catch (error) {
    console.error('Error recording delivery expense:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchDeliverySummary = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/deliveries/summary/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch delivery summary');
  } catch (error) {
    console.error('Error fetching delivery summary:', error.response?.data || error.message);
    throw error;
  }
};

// ── VEHICLE MAINTENANCE LOGS ──────────────────────────────────

export const fetchMaintenanceLogs = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/logistics/maintenance/', { params });
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch maintenance logs');
  } catch (error) {
    console.error('Error fetching maintenance logs:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchMaintenanceLogDetail = async (logId) => {
  try {
    const response = await axiosInstance.get(`/logistics/maintenance/${logId}/`);
    if (response.status === 200) return response.data;
    throw new Error('Failed to fetch maintenance log detail');
  } catch (error) {
    console.error('Error fetching maintenance log detail:', error.response?.data || error.message);
    throw error;
  }
};

export const createMaintenanceLog = async (data) => {
  try {
    const response = await axiosInstance.post('/logistics/maintenance/', data);
    if (response.status === 201) return response.data;
    throw new Error('Failed to create maintenance log');
  } catch (error) {
    console.error('Error creating maintenance log:', error.response?.data || error.message);
    throw error;
  }
};

export const updateMaintenanceLog = async (logId, data) => {
  try {
    const response = await axiosInstance.patch(`/logistics/maintenance/${logId}/`, data);
    if (response.status === 200) return response.data;
    throw new Error('Failed to update maintenance log');
  } catch (error) {
    console.error('Error updating maintenance log:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteMaintenanceLog = async (logId) => {
  try {
    const response = await axiosInstance.delete(`/logistics/maintenance/${logId}/`);
    if (response.status === 204) return true;
    throw new Error('Failed to delete maintenance log');
  } catch (error) {
    console.error('Error deleting maintenance log:', error.response?.data || error.message);
    throw error;
  }
};

// Material Requisition API Functions
export const createMaterialRequisition = async (requisitionData) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/material-requisitions/', requisitionData);

    console.log('=== CREATE RESPONSE DEBUG ===');
    console.log('Full response:', response);
    console.log('Response status:', response.status);
    console.log('Response data:', response.data);
    console.log('Available keys in response:', Object.keys(response));
    console.log('Available keys in response.data:', Object.keys(response.data || {}));
    console.log('=== END DEBUG ===');

    if (response.status === 201) {
      return response;
    } else {
      throw new Error('Failed to create material requisition');
    }
  } catch (error) {
    console.error('Error creating material requisition:', error.response?.data || error.message);
    throw error;
  }
};

export const updateMaterialRequisition = async (requisitionId, requisitionData) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/material-requisitions/${requisitionId}/`, requisitionData);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update material requisition');
    }
  } catch (error) {
    console.error('Error updating material requisition:', error.response?.data || error.message);
    throw error;
  }
};

export const getMaterialRequisition = async (requisitionId) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/material-requisitions/${requisitionId}/`);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch material requisition');
    }
  } catch (error) {
    console.error('Error fetching material requisition:', error.response?.data || error.message);
    throw error;
  }
};

export const submitMaterialRequisition = async (requisitionId) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/material-requisitions/${requisitionId}/submit/`);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to submit material requisition');
    }
  } catch (error) {
    console.error('Error submitting material requisition:', error.response?.data || error.message);
    throw error;
  }
};

// Suppliers API Function
export const getSuppliers = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add filters based on your backend's filterset_fields: ['branch', 'is_active', 'rating']
    if (filters.branch !== undefined) {
      queryParams.append('branch', filters.branch);
    }

    if (filters.is_active !== undefined) {
      queryParams.append('is_active', String(filters.is_active));
    }

    if (filters.rating !== undefined) {
      queryParams.append('rating', filters.rating);
    }

    // Add search functionality based on search_fields: ['supplier_name', 'supplier_code', 'contact_person', 'email']
    if (filters.search) {
      queryParams.append('search', encodeURIComponent(filters.search));
    }

    // Add ordering based on ordering_fields: ['supplier_name', 'rating', 'created_at']
    if (filters.ordering) {
      queryParams.append('ordering', filters.ordering);
    }

    // Add pagination
    if (filters.page) {
      queryParams.append('page', filters.page);
    }

    if (filters.page_size) {
      queryParams.append('page_size', filters.page_size);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const url = `/bakery-recipe/suppliers/${queryString}`;

    const response = await axiosInstance.get(url);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch suppliers');
    }
  } catch (error) {
    console.error('Error fetching suppliers:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchAvailableRawMaterials = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/materials-for-requisition/available_materials/', {
      params: params
    });
    return response;
  } catch (error) {
    console.error('Error fetching available raw materials:', error);
    throw error;
  }
};

// Validate requisition quantities against available stock
export const validateRequisitionQuantities = async (items) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/materials-for-requisition/validate_requisition_quantities/', {
      items: items
    });
    return response;
  } catch (error) {
    console.error('Error validating requisition quantities:', error);
    throw error;
  }
};

export const createRawMaterial = async (materialData) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/raw-materials/', materialData);
    return response;
  } catch (error) {
    console.error('Error creating raw material:', error);
    throw error;
  }
};

export const updateRawMaterial = async (id, materialData) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/raw-materials/${id}/`, materialData);
    return response;
  } catch (error) {
    console.error('Error updating raw material:', error);
    throw error;
  }
};

export const deleteRawMaterial = async (id) => {
  try {
    const response = await axiosInstance.delete(`/bakery-recipe/raw-materials/${id}/`);
    return response;
  } catch (error) {
    console.error('Error deleting raw material:', error);
    throw error;
  }
};

export const getRawMaterialById = async (id) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/raw-materials/${id}/`);
    return response;
  } catch (error) {
    console.error('Error fetching raw material details:', error);
    throw error;
  }
};

// Get raw material selection list (alternative endpoint)
export const getRawMaterialSelectionList = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/raw-materials/for_requisition/', {
      params: params
    });
    return response;
  } catch (error) {
    console.error('Error fetching raw material selection list:', error);
    throw error;
  }
};

// Get raw materials with full details
export const getRawMaterials = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/raw-materials/', {
      params: params
    });
    return response;
  } catch (error) {
    console.error('Error fetching raw materials:', error);
    throw error;
  }
};

// Get raw material categories
export const getMaterialCategories = async () => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/material-categories/');
    return response;
  } catch (error) {
    console.error('Error fetching material categories:', error);
    throw error;
  }
};

// Get low stock alerts
export const getRawMaterialLowStockAlerts = async () => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/raw-materials/low_stock_alerts/');
    return response;
  } catch (error) {
    console.error('Error fetching low stock alerts:', error);
    throw error;
  }
};

// Get inventory summary
export const getInventorySummary = async () => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/raw-materials/inventory_summary/');
    return response;
  } catch (error) {
    console.error('Error fetching inventory summary:', error);
    throw error;
  }
};

// Reserve stock for a specific raw material
export const reserveRawMaterialStock = async (stockLevelId, quantity) => {
  try {
    const response = await axiosInstance.post(
      `/bakery-recipe/raw-material-stock-levels/${stockLevelId}/reserve_stock/`,
      { quantity: quantity }
    );
    return response;
  } catch (error) {
    console.error('Error reserving raw material stock:', error);
    throw error;
  }
};

// Release reserved stock
export const releaseReservedRawMaterialStock = async (stockLevelId, quantity) => {
  try {
    const response = await axiosInstance.post(
      `/bakery-recipe/raw-material-stock-levels/${stockLevelId}/release_reserved_stock/`,
      { quantity: quantity }
    );
    return response;
  } catch (error) {
    console.error('Error releasing reserved stock:', error);
    throw error;
  }
};

// Adjust raw material stock
export const adjustRawMaterialStock = async (stockLevelId, adjustmentData) => {
  try {
    const response = await axiosInstance.post(
      `/bakery-recipe/raw-material-stock-levels/${stockLevelId}/adjust_stock/`,
      adjustmentData
    );
    return response;
  } catch (error) {
    console.error('Error adjusting raw material stock:', error);
    throw error;
  }
};

// Get stock adjustment history for a material
export const getRawMaterialStockHistory = async (materialId) => {
  try {
    const response = await axiosInstance.get(
      `/bakery-recipe/raw-materials/${materialId}/stock_history/`
    );
    return response;
  } catch (error) {
    console.error('Error fetching stock history:', error);
    throw error;
  }
};

// Bulk stock adjustment
export const bulkAdjustRawMaterialStock = async (adjustments) => {
  try {
    const response = await axiosInstance.post(
      '/bakery-recipe/raw-material-stock-adjustments/bulk_adjust/',
      { adjustments: adjustments }
    );
    return response;
  } catch (error) {
    console.error('Error performing bulk stock adjustment:', error);
    throw error;
  }
};

// Additional Material Requisition API Functions (based on your ViewSet actions)
export const getMaterialRequisitions = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add filters based on filterset_fields: ['department', 'branch', 'status', 'priority', 'requested_by']
    if (filters.department !== undefined) {
      queryParams.append('department', filters.department);
    }

    if (filters.branch !== undefined) {
      queryParams.append('branch', filters.branch);
    }

    if (filters.status) {
      queryParams.append('status', filters.status);
    }

    if (filters.priority) {
      queryParams.append('priority', filters.priority);
    }

    if (filters.requested_by !== undefined) {
      queryParams.append('requested_by', filters.requested_by);
    }

    // Add search functionality based on search_fields: ['requisition_number', 'notes']
    if (filters.search) {
      queryParams.append('search', encodeURIComponent(filters.search));
    }

    if (filters.date_from) {
      queryParams.append('date_from', filters.date_from);  // Not requisition_date__gte
    }

    if (filters.date_to) {
      queryParams.append('date_to', filters.date_to);  // Not requisition_date__lte
    }

    // Add ordering based on ordering_fields: ['requisition_date', 'required_date', 'total_estimated_cost']
    if (filters.ordering) {
      queryParams.append('ordering', filters.ordering);
    }

    // Add pagination
    if (filters.page) {
      queryParams.append('page', filters.page);
    }

    if (filters.page_size) {
      queryParams.append('page_size', filters.page_size);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const url = `/bakery-recipe/material-requisitions/${queryString}`;

    const response = await axiosInstance.get(url);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch material requisitions');
    }
  } catch (error) {
    console.error('Error fetching material requisitions:', error.response?.data || error.message);
    throw error;
  }
};

export const getMaterialRequisitionDetail = async (id) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/material-requisitions/${id}/`);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch material requisition details');
    }
  } catch (error) {
    console.error('Error fetching material requisition details:', error.response?.data || error.message);
    throw error;
  }
};

export const submitRequisition = async (requisitionId) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/material-requisitions/${requisitionId}/submit/`);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to submit requisition');
    }
  } catch (error) {
    console.error('Error submitting requisition:', error.response?.data || error.message);
    throw error;
  }
};

export const approveMaterialRequisition = async (id) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/material-requisitions/${id}/approve/`);
    return response.data;
  } catch (error) {
    const backendMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      "Failed to approve requisition";

    throw new Error(backendMessage);
  }
};


export const rejectMaterialRequisition = async (requisitionId, rejectionReason) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/material-requisitions/${requisitionId}/reject/`, {
      rejection_reason: rejectionReason
    });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to reject material requisition');
    }
  } catch (error) {
    console.error('Error rejecting material requisition:', error.response?.data || error.message);
    throw error;
  }
};

export const markRequisitionPurchased = async (requisitionId) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/material-requisitions/${requisitionId}/mark_purchased/`);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to mark requisition as purchased');
    }
  } catch (error) {
    console.error('Error marking requisition as purchased:', error.response?.data || error.message);
    throw error;
  }
};

export const getMyRequisitions = async () => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/material-requisitions/my_requisitions/');
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch my requisitions');
    }
  } catch (error) {
    console.error('Error fetching my requisitions:', error.response?.data || error.message);
    throw error;
  }
};

export const getPendingApprovalRequisitions = async () => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/material-requisitions/pending_approval/');
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch pending approval requisitions');
    }
  } catch (error) {
    console.error('Error fetching pending approval requisitions:', error.response?.data || error.message);
    throw error;
  }
};

export const getRequisitionStatistics = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/material-requisitions/statistics/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch requisition statistics');
    }
  } catch (error) {
    console.error('Error fetching requisition statistics:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete a material requisition (soft delete)
 * @param {number} requisitionId - The ID of the requisition to delete
 * @returns {Promise<Object>} Response data
 */
export const deleteMaterialRequisition = async (requisitionId) => {
  try {
    if (!requisitionId) {
      throw new Error('Requisition ID is required');
    }

    const response = await axiosInstance.delete(
      `/bakery-recipe/material-requisitions/${requisitionId}/soft_delete/`
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to delete material requisition');
    }
  } catch (error) {
    console.error('Error deleting material requisition:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Export material requisitions
 * @param {string} queryString - Query string with filters (as used in your component)
 * @param {string} format - Export format ('pdf', 'csv', or 'excel')
 * @returns {Promise<void>} Automatically downloads the file
 */
export const exportRequisitions = async (queryString = '', format = 'pdf') => {
  try {
    // Build the URL with query string and format
    const separator = queryString && !queryString.startsWith('?') ? '?' : '';
    const formatParam = queryString ? '&format=' + format : '?format=' + format;
    const url = `/bakery-recipe/material-requisitions/export/${separator}${queryString}${formatParam}`;

    const response = await axiosInstance.get(url, {
      responseType: 'blob', // Important for file downloads
    });

    if (response.status === 200) {
      // Create blob and download
      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      });

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `material_requisitions_${new Date().toISOString().slice(0, 10)}.${format}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Auto-download the file
      const url_obj = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url_obj;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url_obj);

      return {
        success: true,
        filename,
        contentType: response.headers['content-type']
      };
    } else {
      throw new Error('Failed to export requisitions');
    }
  } catch (error) {
    console.error('Error exporting requisitions:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Export material requisitions with options (alternative method)
 * @param {Object} options - Export options
 * @param {string} options.format - Export format ('pdf', 'csv', or 'excel')
 * @param {Object} options.filters - Filter object
 * @returns {Promise<void>} Automatically downloads the file
 */
export const exportRequisitionsWithOptions = async (options = {}) => {
  try {
    const { format = 'pdf', filters = {} } = options;

    // Build query parameters from filters
    const queryParams = new URLSearchParams();

    // Add filter parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const queryString = queryParams.toString();
    return await exportRequisitions(queryString, format);
  } catch (error) {
    console.error('Error exporting requisitions with options:', error);
    throw error;
  }
};

export const exportRequisitionPDF = async (requisitionId) => {
  try {
    const url = `/bakery-recipe/material-requisitions/${requisitionId}/export?format=pdf`;
    const response = await axiosInstance.get(url, {
      responseType: 'blob',
    });

    if (response.status === 200) {
      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `requisition_${requisitionId}_${new Date().toISOString().slice(0, 10)}.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const url_obj = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url_obj;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url_obj);

      return {
        success: true,
        filename,
        contentType: response.headers['content-type']
      };
    } else {
      throw new Error('Failed to export requisition');
    }
  } catch (error) {
    console.error('Error exporting requisition:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Helper function to trigger file download
 * @param {Blob} blob - File blob
 * @param {string} filename - Filename for download
 */
export const downloadFile = (blob, filename) => {
  try {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw new Error('Failed to download file');
  }
};

/**
 * Export and download requisitions in one step
 * @param {Object} options - Export options (same as exportRequisitions)
 */
export const exportAndDownloadRequisitions = async (options = {}) => {
  try {
    const result = await exportRequisitions(options);
    downloadFile(result.blob, result.filename);
    return result;
  } catch (error) {
    console.error('Error exporting and downloading requisitions:', error);
    throw error;
  }
};

export const getMfaStatus = async () => {
  try {
    const response = await axiosInstance.get('/users/mfa/status/');
    return response.data;
  } catch (error) {
    console.error('MFA Status API Error:', error);
    // Return default safe values if endpoint fails
    return {
      mfa_enabled: false,
      required_operations: [],
      session_verified: false
    };
  }
};

// Get Requisition Items
export const getRequisitionItems = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/requisition-items/', { params });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch requisition items');
    }
  } catch (error) {
    console.error('Error fetching requisition items:', error.response?.data || error.message);
    throw error;
  }
};

// Get Requisition Item by ID
export const getRequisitionItemById = async (id) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/requisition-items/${id}/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch requisition item');
    }
  } catch (error) {
    console.error('Error fetching requisition item by ID:', error.response?.data || error.message);
    throw error;
  }
};

// Create Requisition Item
export const createRequisitionItem = async (data) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/requisition-items/', data);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create requisition item');
    }
  } catch (error) {
    console.error('Error creating requisition item:', error.response?.data || error.message);
    throw error;
  }
};

// Update Requisition Item
export const updateRequisitionItem = async (id, data) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/requisition-items/${id}/`, data);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update requisition item');
    }
  } catch (error) {
    console.error('Error updating requisition item:', error.response?.data || error.message);
    throw error;
  }
};

// Delete Requisition Item
export const deleteRequisitionItem = async (id) => {
  try {
    const response = await axiosInstance.delete(`/bakery-recipe/requisition-items/${id}/`);

    if (response.status === 204) {
      return { success: true };
    } else {
      throw new Error('Failed to delete requisition item');
    }
  } catch (error) {
    console.error('Error deleting requisition item:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get material tracking data with optional filters
 * @param {Object} params - Filter parameters
 * @param {string} params.branch_id - Branch ID filter
 * @param {string} params.department_id - Department ID filter
 * @param {string} params.search - Search term for tracking_number, supplier_name, delivery_notes
 * @param {string} params.status - Delivery status filter
 * @param {string} params.supplier - Supplier filter
 * @param {string} params.dateFrom - Start date filter
 * @param {string} params.dateTo - End date filter
 * @param {boolean} params.overdue - Filter for overdue deliveries
 * @returns {Promise} API response with tracking data
 */
export const getMaterialTracking = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();

    if (params.branch_id) queryParams.append('branch_id', params.branch_id);
    if (params.department_id) queryParams.append('department_id', params.department_id);
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('delivery_status', params.status);
    if (params.supplier) queryParams.append('supplier', params.supplier);
    if (params.dateFrom) queryParams.append('order_date__gte', params.dateFrom);
    if (params.dateTo) queryParams.append('order_date__lte', params.dateTo);

    // Add ordering
    queryParams.append('ordering', '-order_date');

    const url = `/bakery-recipe/material-tracking/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await axiosInstance.get(url);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch material tracking data');
    }
  } catch (error) {
    console.error('Error fetching material tracking:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get overdue deliveries
 * @returns {Promise} API response with overdue deliveries
 */
export const getOverdueDeliveries = async () => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/material-tracking/overdue_deliveries/');

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch overdue deliveries');
    }
  } catch (error) {
    console.error('Error fetching overdue deliveries:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update tracking status
 * @param {string|number} trackingId - Tracking record ID
 * @param {Object} updateData - Update data
 * @param {string} updateData.delivery_status - New delivery status
 * @param {string} updateData.delivery_notes - Optional delivery notes
 * @returns {Promise} API response
 */
export const updateTrackingStatus = async (trackingId, updateData) => {
  try {
    const response = await axiosInstance.post(
      `/bakery-recipe/material-tracking/${trackingId}/update_status/`,
      updateData
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update tracking status');
    }
  } catch (error) {
    console.error('Error updating tracking status:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Confirm delivery
 * @param {string|number} trackingId - Tracking record ID
 * @param {Object} confirmationData - Confirmation data
 * @param {string} confirmationData.delivery_condition - Delivery condition
 * @param {string} confirmationData.delivery_notes - Optional delivery notes
 * @returns {Promise} API response
 */
export const confirmDelivery = async (trackingId, confirmationData) => {
  try {
    const response = await axiosInstance.post(
      `/bakery-recipe/material-tracking/${trackingId}/confirm_delivery/`,
      confirmationData
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to confirm delivery');
    }
  } catch (error) {
    console.error('Error confirming delivery:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get detailed tracking information
 * @param {string|number} trackingId - Tracking record ID
 * @returns {Promise} API response with detailed tracking data
 */
export const getTrackingDetails = async (trackingId) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/material-tracking/${trackingId}/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch tracking details');
    }
  } catch (error) {
    console.error('Error fetching tracking details:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Create new tracking record
 * @param {Object} trackingData - Tracking data
 * @returns {Promise} API response
 */
export const createMaterialTracking = async (trackingData) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/material-tracking/', trackingData);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create tracking record');
    }
  } catch (error) {
    console.error('Error creating tracking record:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update tracking record
 * @param {string|number} trackingId - Tracking record ID
 * @param {Object} trackingData - Updated tracking data
 * @returns {Promise} API response
 */
export const updateMaterialTracking = async (trackingId, trackingData) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/material-tracking/${trackingId}/`, trackingData);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update tracking record');
    }
  } catch (error) {
    console.error('Error updating tracking record:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete tracking record
 * @param {string|number} trackingId - Tracking record ID
 * @returns {Promise} API response
 */
export const deleteMaterialTracking = async (trackingId) => {
  try {
    const response = await axiosInstance.delete(`/bakery-recipe/material-tracking/${trackingId}/`);

    if (response.status === 204) {
      return { message: 'Tracking record deleted successfully' };
    } else {
      throw new Error('Failed to delete tracking record');
    }
  } catch (error) {
    console.error('Error deleting tracking record:', error.response?.data || error.message);
    throw error;
  }
};

// Tracking Items API Functions

/**
 * Get tracking items by tracking ID
 * @param {string|number} trackingId - Tracking record ID
 * @returns {Promise} API response with tracking items
 */
export const getTrackingItems = async (trackingId) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/tracking-items/by_tracking/?tracking_id=${trackingId}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch tracking items');
    }
  } catch (error) {
    console.error('Error fetching tracking items:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update tracking item delivery status
 * @param {string|number} trackingItemId - Tracking item ID
 * @param {Object} updateData - Update data
 * @param {number} updateData.quantity_delivered - Quantity delivered
 * @param {number} updateData.quantity_accepted - Quantity accepted
 * @param {number} updateData.quantity_rejected - Quantity rejected
 * @param {string} updateData.item_condition - Item condition
 * @param {string} updateData.rejection_reason - Rejection reason
 * @param {string} updateData.batch_number - Batch number
 * @param {string} updateData.expiry_date - Expiry date (YYYY-MM-DD)
 * @returns {Promise} API response
 */
export const updateTrackingItemStatus = async (trackingItemId, updateData) => {
  try {
    const response = await axiosInstance.post(
      `/bakery-recipe/tracking-items/${trackingItemId}/update_delivery_status/`,
      updateData
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update tracking item status');
    }
  } catch (error) {
    console.error('Error updating tracking item status:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get items expiring soon
 * @param {number} days - Number of days ahead to check (default: 7)
 * @returns {Promise} API response with expiring items
 */
export const getExpiringItems = async (days = 7) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/tracking-items/expiring_soon/?days=${days}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch expiring items');
    }
  } catch (error) {
    console.error('Error fetching expiring items:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Create tracking item
 * @param {Object} trackingItemData - Tracking item data
 * @returns {Promise} API response
 */
export const createTrackingItem = async (trackingItemData) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/tracking-items/', trackingItemData);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create tracking item');
    }
  } catch (error) {
    console.error('Error creating tracking item:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get all tracking items with filters
 * @param {Object} params - Filter parameters
 * @param {string} params.tracking - Tracking ID filter
 * @param {string} params.item_condition - Item condition filter
 * @param {string} params.requisition_item - Requisition item filter
 * @param {string} params.search - Search term for batch_number, rejection_reason
 * @returns {Promise} API response with tracking items
 */
export const getAllTrackingItems = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.tracking) queryParams.append('tracking', params.tracking);
    if (params.item_condition) queryParams.append('item_condition', params.item_condition);
    if (params.requisition_item) queryParams.append('requisition_item', params.requisition_item);
    if (params.search) queryParams.append('search', params.search);

    const url = `/bakery-recipe/tracking-items/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await axiosInstance.get(url);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch tracking items');
    }
  } catch (error) {
    console.error('Error fetching tracking items:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get tracking item details
 * @param {string|number} trackingItemId - Tracking item ID
 * @returns {Promise} API response with tracking item details
 */
export const getTrackingItemDetails = async (trackingItemId) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/tracking-items/${trackingItemId}/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch tracking item details');
    }
  } catch (error) {
    console.error('Error fetching tracking item details:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update tracking item
 * @param {string|number} trackingItemId - Tracking item ID
 * @param {Object} trackingItemData - Updated tracking item data
 * @returns {Promise} API response
 */
export const updateTrackingItem = async (trackingItemId, trackingItemData) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/tracking-items/${trackingItemId}/`, trackingItemData);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update tracking item');
    }
  } catch (error) {
    console.error('Error updating tracking item:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete tracking item
 * @param {string|number} trackingItemId - Tracking item ID
 * @returns {Promise} API response
 */
export const deleteTrackingItem = async (trackingItemId) => {
  try {
    const response = await axiosInstance.delete(`/bakery-recipe/tracking-items/${trackingItemId}/`);

    if (response.status === 204) {
      return { message: 'Tracking item deleted successfully' };
    } else {
      throw new Error('Failed to delete tracking item');
    }
  } catch (error) {
    console.error('Error deleting tracking item:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get purchase transactions with filtering and pagination
 * @param {Object} params - Query parameters for filtering, pagination, etc.
 * @returns {Promise} API response with transaction list
 */
export const getPurchaseTransactions = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/purchase-transactions/', {
      params: params
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch purchase transactions');
    }
  } catch (error) {
    console.error('Error fetching purchase transactions:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get a specific purchase transaction by ID
 * @param {number} transactionId - Transaction ID
 * @returns {Promise} API response with transaction details
 */
export const getPurchaseTransactionById = async (transactionId) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/purchase-transactions/${transactionId}/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch purchase transaction details');
    }
  } catch (error) {
    console.error('Error fetching purchase transaction details:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Create a new purchase transaction
 * @param {Object} transactionData - Transaction data to create
 * @returns {Promise} API response with created transaction
 */
export const createPurchaseTransaction = async (transactionData) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/purchase-transactions/', transactionData);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create purchase transaction');
    }
  } catch (error) {
    console.error('Error creating purchase transaction:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update an existing purchase transaction
 * @param {number} transactionId - Transaction ID to update
 * @param {Object} transactionData - Updated transaction data
 * @returns {Promise} API response with updated transaction
 */
export const updatePurchaseTransaction = async (transactionId, transactionData) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/purchase-transactions/${transactionId}/`, transactionData);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update purchase transaction');
    }
  } catch (error) {
    console.error('Error updating purchase transaction:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update payment status for a specific transaction
 * @param {number} transactionId - Transaction ID
 * @param {Object} paymentData - Payment update data (payment_status, amount_paid, payment_date)
 * @returns {Promise} API response with updated payment status
 */
export const updatePaymentStatus = async (transactionId, paymentData) => {
  try {
    const response = await axiosInstance.post(
      `/bakery-recipe/purchase-transactions/${transactionId}/update_payment_status/`,
      paymentData
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update payment status');
    }
  } catch (error) {
    console.error('Error updating payment status:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Approve a purchase transaction
 * @param {number} transactionId - Transaction ID to approve
 * @returns {Promise} API response with approval confirmation
 */
export const approveTransaction = async (transactionId) => {
  try {
    const response = await axiosInstance.post(
      `/bakery-recipe/purchase-transactions/${transactionId}/approve_transaction/`,
      {}
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to approve transaction');
    }
  } catch (error) {
    console.error('Error approving transaction:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get pending payments (transactions with 'pending' or 'partial' payment status)
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} API response with pending payments
 */
export const getPendingPayments = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/purchase-transactions/pending_payments/', {
      params: params
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch pending payments');
    }
  } catch (error) {
    console.error('Error fetching pending payments:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get overdue payments (transactions past due date with 'pending' or 'partial' status)
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} API response with overdue payments
 */
export const getOverduePayments = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/purchase-transactions/overdue_payments/', {
      params: params
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch overdue payments');
    }
  } catch (error) {
    console.error('Error fetching overdue payments:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get purchase transaction summary statistics
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} API response with summary data
 */
export const getPurchaseTransactionSummary = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/purchase-transactions/summary/', {
      params: params
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch purchase transaction summary');
    }
  } catch (error) {
    console.error('Error fetching purchase transaction summary:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get purchase transaction statistics
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} API response with statistics data
 */
export const getPurchaseTransactionStats = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/purchase-transactions/statistics/', {
      params: params
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch purchase transaction statistics');
    }
  } catch (error) {
    console.error('Error fetching purchase transaction statistics:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get transactions by supplier
 * @param {number} supplierId - Supplier ID
 * @param {Object} params - Additional query parameters
 * @returns {Promise} API response with supplier transactions
 */
export const getTransactionsBySupplier = async (supplierId, params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/purchase-transactions/by_supplier/', {
      params: { supplier_id: supplierId, ...params }
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch transactions by supplier');
    }
  } catch (error) {
    console.error('Error fetching transactions by supplier:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get current user's transactions
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} API response with user's transactions
 */
export const getMyTransactions = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/purchase-transactions/my_transactions/', {
      params: params
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch my transactions');
    }
  } catch (error) {
    console.error('Error fetching my transactions:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete a purchase transaction
 * @param {number} transactionId - Transaction ID to delete
 * @returns {Promise} API response confirming deletion
 */
export const deletePurchaseTransaction = async (transactionId) => {
  try {
    const response = await axiosInstance.delete(`/bakery-recipe/purchase-transactions/${transactionId}/`);

    if (response.status === 204) {
      return { message: 'Transaction deleted successfully' };
    } else {
      throw new Error('Failed to delete purchase transaction');
    }
  } catch (error) {
    console.error('Error deleting purchase transaction:', error.response?.data || error.message);
    throw error;
  }
};

// ==========================================
// Purchase Transaction Items API Functions
// ==========================================

/**
 * Get purchase transaction items with filtering
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} API response with transaction items
 */
export const getPurchaseTransactionItems = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/purchase-transaction-items/', {
      params: params
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch purchase transaction items');
    }
  } catch (error) {
    console.error('Error fetching purchase transaction items:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get purchase transaction item by ID
 * @param {number} itemId - Transaction item ID
 * @returns {Promise} API response with item details
 */
export const getPurchaseTransactionItemById = async (itemId) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/purchase-transaction-items/${itemId}/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch purchase transaction item details');
    }
  } catch (error) {
    console.error('Error fetching purchase transaction item details:', error.response?.data || error.message);
    throw error;
  }
};

export const getPurchaseOrders = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();

    // Add filtering parameters
    if (params.branch) {
      queryParams.append('branch', params.branch);
    }

    if (params.department) {
      queryParams.append('department', params.department);
    }

    if (params.status) {
      queryParams.append('status', params.status);
    }

    // For approved orders (what your frontend specifically needs)
    if (params.active || params.status === 'approved') {
      // Use the custom endpoint for approved orders
      const response = await axiosInstance.get(`/core/purchase-orders/approved_orders/?${queryParams.toString()}`);

      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error('Failed to fetch approved purchase orders');
      }
    }

    // For general purchase order listing
    const response = await axiosInstance.get(`/core/purchase-orders/?${queryParams.toString()}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch purchase orders');
    }

  } catch (error) {
    console.error('Error fetching purchase orders:', error.response?.data || error.message);
    throw error;
  }
};

export const getPaymentHistory = async (transactionId, params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/bakery-recipe/purchase-transactions/${transactionId}/payment_history/${queryParams ? `?${queryParams}` : ''}`;

    const response = await axiosInstance.get(url);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch payment history');
    }
  } catch (error) {
    console.error('Error fetching payment history:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Create a new purchase transaction item
 * @param {Object} itemData - Item data to create
 * @returns {Promise} API response with created item
 */
export const createPurchaseTransactionItem = async (itemData) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/purchase-transaction-items/', itemData);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create purchase transaction item');
    }
  } catch (error) {
    console.error('Error creating purchase transaction item:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update an existing purchase transaction item
 * @param {number} itemId - Item ID to update
 * @param {Object} itemData - Updated item data
 * @returns {Promise} API response with updated item
 */
export const updatePurchaseTransactionItem = async (itemId, itemData) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/purchase-transaction-items/${itemId}/`, itemData);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update purchase transaction item');
    }
  } catch (error) {
    console.error('Error updating purchase transaction item:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete a purchase transaction item
 * @param {number} itemId - Item ID to delete
 * @returns {Promise} API response confirming deletion
 */
export const deletePurchaseTransactionItem = async (itemId) => {
  try {
    const response = await axiosInstance.delete(`/bakery-recipe/purchase-transaction-items/${itemId}/`);

    if (response.status === 204) {
      return { message: 'Transaction item deleted successfully' };
    } else {
      throw new Error('Failed to delete purchase transaction item');
    }
  } catch (error) {
    console.error('Error deleting purchase transaction item:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get items by transaction ID
 * @param {number} transactionId - Transaction ID
 * @param {Object} params - Additional query parameters
 * @returns {Promise} API response with transaction items
 */
export const getItemsByTransaction = async (transactionId, params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/purchase-transaction-items/by_transaction/', {
      params: { transaction_id: transactionId, ...params }
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch items by transaction');
    }
  } catch (error) {
    console.error('Error fetching items by transaction:', error.response?.data || error.message);
    throw error;
  }
};


/**
 * Get items by material name
 * @param {string} materialName - Material name to search for
 * @param {Object} params - Additional query parameters
 * @returns {Promise} API response with matching items
 */
export const getItemsByMaterial = async (materialName, params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/purchase-transaction-items/by_material/', {
      params: { material_name: materialName, ...params }
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch items by material');
    }
  } catch (error) {
    console.error('Error fetching items by material:', error.response?.data || error.message);
    throw error;
  }
};


/**
 * Get supplier by ID
 * @param {number|string} supplierId - Supplier ID
 * @returns {Promise<Object>} Supplier details
 */
export const getSupplierById = async (supplierId) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/suppliers/${supplierId}/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch supplier details');
    }
  } catch (error) {
    console.error('Error fetching supplier details:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Create a new supplier
 * @param {Object} supplierData - Supplier data
 * @param {string} supplierData.supplier_name - Supplier name
 * @param {string} supplierData.supplier_code - Supplier code
 * @param {string} supplierData.contact_person - Contact person name
 * @param {string} supplierData.email - Email address
 * @param {string} supplierData.phone - Phone number
 * @param {string} supplierData.address - Address
 * @param {string} supplierData.payment_terms - Payment terms
 * @param {number} supplierData.rating - Rating (0.0-5.0)
 * @param {boolean} supplierData.is_active - Active status
 * @param {number} supplierData.branch - Branch ID
 * @returns {Promise<Object>} Created supplier
 */
export const createSupplier = async (supplierData) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/suppliers/', supplierData);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create supplier');
    }
  } catch (error) {
    console.error('Error creating supplier:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update an existing supplier
 * @param {number|string} supplierId - Supplier ID
 * @param {Object} supplierData - Updated supplier data
 * @returns {Promise<Object>} Updated supplier
 */
export const updateSupplier = async (supplierId, supplierData) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/suppliers/${supplierId}/`, supplierData);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update supplier');
    }
  } catch (error) {
    console.error('Error updating supplier:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Partially update a supplier
 * @param {number|string} supplierId - Supplier ID
 * @param {Object} supplierData - Partial supplier data
 * @returns {Promise<Object>} Updated supplier
 */
export const patchSupplier = async (supplierId, supplierData) => {
  try {
    const response = await axiosInstance.patch(`/bakery-recipe/suppliers/${supplierId}/`, supplierData);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update supplier');
    }
  } catch (error) {
    console.error('Error updating supplier:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete a supplier
 * @param {number|string} supplierId - Supplier ID
 * @returns {Promise<void>}
 */
export const deleteSupplier = async (supplierId) => {
  try {
    const response = await axiosInstance.delete(`/bakery-recipe/suppliers/${supplierId}/`);

    if (response.status === 204) {
      return;
    } else {
      throw new Error('Failed to delete supplier');
    }
  } catch (error) {
    console.error('Error deleting supplier:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get supplier statistics
 * @param {Object} params - Query parameters
 * @param {string} params.branch - Branch ID filter
 * @returns {Promise<Object>} Supplier statistics
 */
export const getSupplierStats = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/suppliers/stats/', {
      params
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch supplier statistics');
    }
  } catch (error) {
    console.error('Error fetching supplier statistics:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get supplier performance metrics
 * @param {number|string} supplierId - Supplier ID
 * @returns {Promise<Object>} Supplier performance data
 */
export const getSupplierPerformance = async (supplierId) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/suppliers/${supplierId}/performance/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch supplier performance');
    }
  } catch (error) {
    console.error('Error fetching supplier performance:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get supplier transaction history
 * @param {number|string} supplierId - Supplier ID
 * @param {Object} params - Query parameters for pagination
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @returns {Promise<Object>} Supplier transactions (paginated)
 */
export const getSupplierTransactions = async (supplierId, params = {}) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/suppliers/${supplierId}/transactions/`, {
      params
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch supplier transactions');
    }
  } catch (error) {
    console.error('Error fetching supplier transactions:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update supplier rating
 * @param {number|string} supplierId - Supplier ID
 * @param {number} rating - New rating (0.0-5.0)
 * @returns {Promise<Object>} Success response
 */
export const updateSupplierRating = async (supplierId, rating) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/suppliers/${supplierId}/update_rating/`, {
      rating
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update supplier rating');
    }
  } catch (error) {
    console.error('Error updating supplier rating:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get top-rated suppliers
 * @param {Object} params - Query parameters
 * @param {string} params.branch - Branch ID filter
 * @returns {Promise<Array>} Top 10 rated suppliers
 */
export const getTopRatedSuppliers = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/suppliers/top_rated/', {
      params
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch top-rated suppliers');
    }
  } catch (error) {
    console.error('Error fetching top-rated suppliers:', error.response?.data || error.message);
    throw error;
  }
};

// Additional utility functions that might be useful

/**
 * Search suppliers by name or code
 * @param {string} searchTerm - Search term
 * @param {Object} additionalParams - Additional query parameters
 * @returns {Promise<Object>} Search results
 */
export const searchSuppliers = async (searchTerm, additionalParams = {}) => {
  return getSuppliers({
    search: searchTerm,
    ...additionalParams
  });
};

/**
 * Get suppliers by branch
 * @param {number|string} branchId - Branch ID
 * @param {Object} additionalParams - Additional query parameters
 * @returns {Promise<Object>} Suppliers in the branch
 */
export const getSuppliersByBranch = async (branchId, additionalParams = {}) => {
  return getSuppliers({
    branch: branchId,
    ...additionalParams
  });
};

/**
 * Get active suppliers only
 * @param {Object} additionalParams - Additional query parameters
 * @returns {Promise<Object>} Active suppliers
 */
export const getActiveSuppliers = async (additionalParams = {}) => {
  return getSuppliers({
    is_active: true,
    ...additionalParams
  });
};

/**
 * Get suppliers with minimum rating
 * @param {number} minRating - Minimum rating threshold
 * @param {Object} additionalParams - Additional query parameters
 * @returns {Promise<Object>} Suppliers meeting rating criteria
 */
export const getSuppliersByRating = async (minRating, additionalParams = {}) => {
  return getSuppliers({
    rating_min: minRating,
    ...additionalParams
  });
};

// 1. Get Material Usage Records
export const getMaterialUsage = async (endpoint = '/', params = {}) => {
  try {
    let url = '/bakery-recipe/material-usage';

    // Handle different endpoint types
    if (endpoint === '/my-records/') {
      url += '/my_records/';
    } else if (endpoint === '/by-production-batch/') {
      url += '/by_production_batch/';
    } else if (endpoint === '/waste-analysis/') {
      url += '/waste_analysis/';
    } else if (endpoint === '/efficiency-report/') {
      url += '/efficiency_report/';
    } else if (endpoint === '/summary/') {
      url += '/summary/';
    } else if (endpoint === '/unverified-records/') {
      url += '/unverified_records/';
    } else {
      url += '/';
    }

    const response = await axiosInstance.get(url, { params });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch material usage records');
    }
  } catch (error) {
    console.error('Error fetching material usage:', error.response?.data || error.message);
    throw error;
  }
};

// 2. Get Material Usage by ID
export const getMaterialUsageById = async (id) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/material-usage/${id}/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch material usage record');
    }
  } catch (error) {
    console.error('Error fetching material usage by ID:', error.response?.data || error.message);
    throw error;
  }
};

// 3. Create Material Usage Record
export const createMaterialUsage = async (data) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/material-usage/', data);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create material usage record');
    }
  } catch (error) {
    console.error('Error creating material usage:', error.response?.data || error.message);
    throw error;
  }
};

// 4. Update Material Usage Record
export const updateMaterialUsage = async (id, data) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/material-usage/${id}/`, data);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update material usage record');
    }
  } catch (error) {
    console.error('Error updating material usage:', error.response?.data || error.message);
    throw error;
  }
};

// 5. Verify Material Usage Record
export const verifyMaterialUsage = async (id) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/material-usage/${id}/verify_usage/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to verify material usage record');
    }
  } catch (error) {
    console.error('Error verifying material usage:', error.response?.data || error.message);
    throw error;
  }
};

export const getMaterialUsageByBatch = async (batchId) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/material-usage/by_production_batch/', {
      params: { batch_id: batchId }
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch material usage by batch');
    }
  } catch (error) {
    console.error('Error fetching material usage by batch:', error.response?.data || error.message);
    throw error;
  }
};

// Get Waste Analysis
export const getMaterialWasteAnalysis = async (params = {}) => {
  try {
    // Ensure params is an object, not a string
    const queryParams = typeof params === 'string' ? {} : params;
    
    const response = await axiosInstance.get('/bakery-recipe/material-usage/waste_analysis/', { 
      params: queryParams 
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch waste analysis');
    }
  } catch (error) {
    console.error('Error fetching waste analysis:', error.response?.data || error.message);
    throw error;
  }
};

// Get Efficiency Report
export const getMaterialEfficiencyReport = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/material-usage/efficiency_report/', { params });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch efficiency report');
    }
  } catch (error) {
    console.error('Error fetching efficiency report:', error.response?.data || error.message);
    throw error;
  }
};

// Get Material Usage Summary
export const getMaterialUsageSummary = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/material-usage/summary/', { params });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch usage summary');
    }
  } catch (error) {
    console.error('Error fetching usage summary:', error.response?.data || error.message);
    throw error;
  }
};

// Get Unverified Records
export const getUnverifiedMaterialUsage = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/material-usage/unverified_records/', { params });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch unverified records');
    }
  } catch (error) {
    console.error('Error fetching unverified records:', error.response?.data || error.message);
    throw error;
  }
};

// Get Production Batches
export const getProductionBatches = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/production-batches/', { params });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch production batches');
    }
  } catch (error) {
    console.error('Error fetching production batches:', error.response?.data || error.message);
    throw error;
  }
};

export const startProductionBatch = async (batchId, data = {}) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/production-batches/${batchId}/start_production/`, data);
    
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to start production batch');
    }
  } catch (error) {
    console.error('Error starting production batch:', error.response?.data || error.message);
    throw error;
  }
};

// Complete Production Batch
export const completeProductionBatch = async (batchId, data) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/production-batches/${batchId}/complete_production/`, data);
    
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to complete production batch');
    }
  } catch (error) {
    console.error('Error completing production batch:', error.response?.data || error.message);
    throw error;
  }
};

// Cancel Production Batch
export const cancelProductionBatch = async (batchId, data = {}) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/production-batches/${batchId}/cancel_production/`, data);
    
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to cancel production batch');
    }
  } catch (error) {
    console.error('Error canceling production batch:', error.response?.data || error.message);
    throw error;
  }
};

// Get Production Batch by ID
export const getProductionBatchById = async (id) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/production-batches/${id}/`);
    
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch production batch');
    }
  } catch (error) {
    console.error('Error fetching production batch by ID:', error.response?.data || error.message);
    throw error;
  }
};

// Create Production Batch
export const createProductionBatch = async (data) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/production-batches/', data);
    
    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create production batch');
    }
  } catch (error) {
    console.error('Error creating production batch:', error.response?.data || error.message);
    throw error;
  }
};

// Update Production Batch
export const updateProductionBatch = async (id, data) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/production-batches/${id}/`, data);
    
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update production batch');
    }
  } catch (error) {
    console.error('Error updating production batch:', error.response?.data || error.message);
    throw error;
  }
};

// Production Batch Statistics API Functions
export const getProductionBatchStats = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/production-batches/dashboard_stats/', { params });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch production batch statistics');
    }
  } catch (error) {
    console.error('Error fetching production batch stats:', error.response?.data || error.message);
    throw error;
  }
};

// Get Low Yield Batches
export const getLowYieldBatches = async (threshold = 90) => {
  try {
    const params = new URLSearchParams();
    if (threshold) {
      params.append('threshold', threshold);
    }

    const response = await axiosInstance.get(`/bakery-recipe/production-batches/low_yield_batches/?${params}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch low yield batches');
    }
  } catch (error) {
    console.error('Error fetching low yield batches:', error.response?.data || error.message);
    throw error;
  }
};

// Get Production Cost Analysis
export const getProductionCostAnalysis = async (startDate = null, endDate = null) => {
  try {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('start_date', startDate);
    }
    if (endDate) {
      params.append('end_date', endDate);
    }

    const response = await axiosInstance.get(`/bakery-recipe/production-batches/cost_analysis/?${params}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch production cost analysis');
    }
  } catch (error) {
    console.error('Error fetching production cost analysis:', error.response?.data || error.message);
    throw error;
  }
};

// Recipe API Functions

// Get all recipes with filtering options
export const getRecipes = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add supported filter parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });

    const response = await axiosInstance.get(`/bakery-recipe/recipes/?${queryParams}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch recipes');
    }
  } catch (error) {
    console.error('Error fetching recipes:', error.response?.data || error.message);
    throw error;
  }
};

// Get recipe by ID
export const getRecipeById = async (id) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/recipes/${id}/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch recipe');
    }
  } catch (error) {
    console.error('Error fetching recipe by ID:', error.response?.data || error.message);
    throw error;
  }
};

// Create new recipe
export const createRecipe = async (recipeData) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/recipes/', recipeData);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create recipe');
    }
  } catch (error) {
    console.error('Error creating recipe:', error.response?.data || error.message);
    throw error;
  }
};

// Update existing recipe
export const updateRecipe = async (id, recipeData) => {
  try {
    const response = await axiosInstance.patch(`/bakery-recipe/recipes/${id}/`, recipeData);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update recipe');
    }
  } catch (error) {
    console.error('Error updating recipe:', error.response?.data || error.message);
    throw error;
  }
};

// Delete recipe
export const deleteRecipe = async (id) => {
  try {
    const response = await axiosInstance.delete(`/bakery-recipe/recipes/${id}/`);

    if (response.status === 204) {
      return { message: 'Recipe deleted successfully' };
    } else {
      throw new Error('Failed to delete recipe');
    }
  } catch (error) {
    console.error('Error deleting recipe:', error.response?.data || error.message);
    throw error;
  }
};

// Approve recipe
export const approveRecipe = async (id) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/recipes/${id}/approve/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to approve recipe');
    }
  } catch (error) {
    console.error('Error approving recipe:', error.response?.data || error.message);
    throw error;
  }
};

// Create recipe version
export const createRecipeVersion = async (parentId, versionData) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/recipes/${parentId}/create_version/`, versionData);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create recipe version');
    }
  } catch (error) {
    console.error('Error creating recipe version:', error.response?.data || error.message);
    throw error;
  }
};

// Get current user's recipes
export const getMyRecipes = async () => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/recipes/my_recipes/');

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch my recipes');
    }
  } catch (error) {
    console.error('Error fetching my recipes:', error.response?.data || error.message);
    throw error;
  }
};

// Get pending approval recipes (Note: This endpoint exists in URLs but not in the provided views code)
export const getPendingApprovalRecipes = async () => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/recipes/pending_approval/');

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch pending approval recipes');
    }
  } catch (error) {
    console.error('Error fetching pending approval recipes:', error.response?.data || error.message);
    throw error;
  }
};

// Additional utility functions for common operations

// Get active recipes only
export const getActiveRecipes = async (additionalParams = {}) => {
  return getRecipes({
    is_active: true,
    ...additionalParams
  });
};

// Get approved recipes only
export const getApprovedRecipes = async (additionalParams = {}) => {
  return getRecipes({
    is_approved: true,
    ...additionalParams
  });
};

// Get recipes by department
export const getRecipesByDepartment = async (departmentId, additionalParams = {}) => {
  return getRecipes({
    department: departmentId,
    ...additionalParams
  });
};

// Get recipes by difficulty level
export const getRecipesByDifficulty = async (difficultyLevel, additionalParams = {}) => {
  return getRecipes({
    difficulty_level: difficultyLevel,
    ...additionalParams
  });
};

// Search recipes by name or code
export const searchRecipes = async (searchTerm, additionalParams = {}) => {
  return getRecipes({
    search: searchTerm,
    ...additionalParams
  });
};

export const getRecipeIngredients = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });

    const response = await axiosInstance.get(`/bakery-recipe/recipe-ingredients/?${queryParams}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch recipe ingredients');
    }
  } catch (error) {
    console.error('Error fetching recipe ingredients:', error.response?.data || error.message);
    throw error;
  }
};

export const getIngredientsByRecipe = async (recipeId) => {
  try {
    const response = await axiosInstance.get(`/bakery-recipe/recipe-ingredients/by_recipe/?recipe_id=${recipeId}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch recipe ingredients');
    }
  } catch (error) {
    console.error('Error fetching recipe ingredients:', error.response?.data || error.message);
    throw error;
  }
};

export const createRecipeIngredient = async (ingredientData) => {
  try {
    const response = await axiosInstance.post('/bakery-recipe/recipe-ingredients/', ingredientData);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create recipe ingredient');
    }
  } catch (error) {
    console.error('Error creating recipe ingredient:', error.response?.data || error.message);
    throw error;
  }
};

// Update recipe ingredient
export const updateRecipeIngredient = async (id, ingredientData) => {
  try {
    const response = await axiosInstance.put(`/bakery-recipe/recipe-ingredients/${id}/`, ingredientData);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update recipe ingredient');
    }
  } catch (error) {
    console.error('Error updating recipe ingredient:', error.response?.data || error.message);
    throw error;
  }
};

// Delete recipe ingredient
export const deleteRecipeIngredient = async (id) => {
  try {
    const response = await axiosInstance.delete(`/bakery-recipe/recipe-ingredients/${id}/`);

    if (response.status === 204) {
      return true;
    } else {
      throw new Error('Failed to delete recipe ingredient');
    }
  } catch (error) {
    console.error('Error deleting recipe ingredient:', error.response?.data || error.message);
    throw error;
  }
};

// Update ingredient cost
export const updateIngredientCost = async (id, costPerUnit) => {
  try {
    const response = await axiosInstance.post(`/bakery-recipe/recipe-ingredients/${id}/update_cost/`, {
      cost_per_unit: costPerUnit
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update ingredient cost');
    }
  } catch (error) {
    console.error('Error updating ingredient cost:', error.response?.data || error.message);
    throw error;
  }
};

export const getStoredBakeryPerformanceMetrics = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/bakery-performance-metrics/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch bakery performance metrics');
    }
  } catch (error) {
    console.error('Error fetching bakery performance metrics:', error.response?.data || error.message);
    throw error;
  }
};

export const getCustomPeriodMetrics = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/bakery-performance-metrics/custom_period_metrics/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch custom period metrics');
    }
  } catch (error) {
    console.error('Error fetching custom period metrics:', error.response?.data || error.message);
    throw error;
  }
};

export const calculateRecipeYield = async (recipeId, params = {}) => {
  try {
    const response = await axiosInstance.post(
      `/bakery-recipe/recipes/${recipeId}/calculate_yield/`,
      params
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to calculate recipe yield');
    }
  } catch (error) {
    console.error('Error calculating recipe yield:', error.response?.data || error.message);
    throw error;
  }
};

export const calculateRecipeMaterials = async (recipeId, numBatches) => {
  try {
    const response = await axiosInstance.post(
      `/bakery-recipe/recipes/${recipeId}/calculate_materials_needed/`,
      { num_batches: numBatches }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to calculate required materials');
    }
  } catch (error) {
    console.error('Error calculating materials:', error.response?.data || error.message);
    throw error;
  }
};

export const addScannedItemToOrder = async (orderId, scannedData) => {
  try {
    const response = await axiosInstance.post(`/core/orders/${orderId}/add-scanned-item/`, scannedData);
    console.log('API Response for adding scanned item:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error adding scanned item:', error.response?.data || error.message);
    if (error.response?.data?.errors) {
      const errorMessages = Object.entries(error.response.data.errors)
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
        .join('; ');
      throw new Error(errorMessages);
    }
    throw error;
  }
};

// Function to create an order
export const createOrder = async (orderData) => {
  try {
    console.log('Sending order data:', JSON.stringify(orderData, null, 2));
    const response = await axiosInstance.post('/core/orders/', orderData);
    console.log('API Response for create order:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating order:', error.response?.data || error.message);
    if (error.response?.data?.errors) {
      const errorMessages = Object.entries(error.response.data.errors)
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
        .join('; ');
      throw new Error(errorMessages);
    } else if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// Function to fetch orders
export const fetchOrders = async (params = {}) => {
  try {
    const processedParams = { ...params };
    
    // Process dates if they exist
    if (processedParams.start_date || processedParams.end_date) {
      const { formattedStartDate, formattedEndDate } = dateUtils.processDateRange(
        processedParams.start_date,
        processedParams.end_date
      );
      processedParams.start_date = formattedStartDate;
      processedParams.end_date = formattedEndDate;
    }

    const response = await axiosInstance.get('/core/orders/', {
      params: {
        ...processedParams,
        include_items: true
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching orders:', error.response?.data || error.message);
    throw error;
  }
};

// Function to delete an order
export const deleteOrder = async (orderId) => {
  try {
    await axiosInstance.delete(`/core/orders/${orderId}/`);
  } catch (error) {
    console.error('Error deleting order:', error.response?.data || error.message);
    throw error;
  }
};

export const voidOrder = async (orderId, reason) => {
  try {
    const response = await axiosInstance.post(`/core/orders/${orderId}/void/`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error voiding order:', error.response?.data || error.message);
    throw error;
  }
};

// Function to apply a promotion to an order
export const applyPromotionToOrder = async (orderId, promotionCode) => {
  try {
    const response = await axiosInstance.post(`/core/orders/${orderId}/apply_promotion/`, { promotion_code: promotionCode });
    return response.data;
  } catch (error) {
    console.error('Error applying promotion:', error.response?.data || error.message);
    throw error;
  }
};

// Function to reorder an existing order
export const reorderExistingOrder = async (orderId) => {
  try {
    const response = await axiosInstance.post(`/core/orders/${orderId}/reorder/`);
    return response.data;
  } catch (error) {
    console.error('Error reordering:', error.response?.data || error.message);
    throw error;
  }
};

// Function to get order details
export const getOrderDetails = async (orderId) => {
  try {
    const response = await axiosInstance.get(`/core/orders/${orderId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching order details:', error.response?.data || error.message);
    throw error;
  }
};

// Function to update an order
export const updateOrder = async (orderId, orderData) => {
  try {
    const response = await axiosInstance.put(`/core/orders/${orderId}/`, orderData);
    return response.data;
  } catch (error) {
    console.error('Error updating order:', error.response?.data || error.message);
    throw error;
  }
};

// Function to fetch order history
export const fetchOrderHistory = async (customerId) => {
  try {
    const response = await axiosInstance.get(`/core/orders/history/`, { params: { customer_id: customerId } });
    return response.data;
  } catch (error) {
    console.error('Error fetching order history:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Approve an order that requires approval
 * @param {number} orderId - The ID of the order to approve
 * @returns {Promise<Object>} The approved order data
 */
export const approveOrder = async (orderId) => {
  try {
    const response = await axiosInstance.post(`/core/orders/${orderId}/approve_order/`);
    return response.data;
  } catch (error) {
    console.error('Error approving order:', error.response?.data || error.message);

    // Handle specific error cases
    if (error.response?.status === 400) {
      const errorMessage = error.response.data?.error || 'Bad request';
      throw new Error(errorMessage);
    } else if (error.response?.status === 403) {
      throw new Error('You are not authorized to approve this order');
    } else if (error.response?.status === 404) {
      throw new Error('Order not found');
    }

    throw error;
  }
};

/**
 * Fetch orders pending approval that the current user can approve
 * @param {Object} params - Optional query parameters (page, page_size, etc.)
 * @returns {Promise<Object>} Paginated list of pending approval orders
 */
export const fetchPendingApprovals = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/core/orders/pending_approvals/', {
      params: {
        page_size: 10,
        ...params
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching pending approvals:', error.response?.data || error.message);

    // Handle specific error cases
    if (error.response?.status === 403) {
      throw new Error('You do not have permission to view pending approvals');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication required');
    }

    throw error;
  }
};

/**
 * Fetch department summary with order statistics
 * @param {Object} params - Optional query parameters for filtering
 * @returns {Promise<Array>} Array of department summary data
 */
export const fetchDepartmentSummary = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/core/orders/department_summary/', {
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching department summary:', error.response?.data || error.message);

    // Handle specific error cases
    if (error.response?.status === 403) {
      throw new Error('You do not have permission to view department summary');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication required');
    }

    throw error;
  }
};

export const checkDepartmentLimits = async (departmentId, orderValue, orderId = null) => {
  try {
    const params = {
      department_id: departmentId,
      order_value: orderValue
    };

    // Add order_id if provided (for order updates)
    if (orderId) {
      params.order_id = orderId;
    }

    const response = await axiosInstance.get('/core/orders/check-department-limits/', {
      params
    });

    return response.data;
  } catch (error) {
    console.error('Error checking department limits:', error.response?.data || error.message);

    // Handle specific error cases
    if (error.response?.status === 403) {
      throw new Error('You do not have permission to check department limits');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 404) {
      throw new Error(error.response?.data?.error || 'Department or limits not found');
    } else if (error.response?.status === 400) {
      throw new Error(error.response?.data?.error || 'Invalid request parameters');
    }

    throw error;
  }
};

export const searchCustomers = async (query) => {
  try {
    const response = await axiosInstance.get(`/core/customers/search/?query=${query}`);
    return response.data.map(customer => ({
      value: customer.id,
      label: `${customer.first_name} ${customer.last_name} (${customer.email})`
    }));
  } catch (error) {
    console.error('Error searching customers:', error.response?.data || error.message);
    throw error;
  }
};

// Function to get a single customer by ID
export const getCustomerById = async (customerId) => {
  try {
    const response = await axiosInstance.get(`/core/customers/${customerId}/`);
    const customer = response.data;
    return {
      value: customer.id,
      label: `${customer.first_name} ${customer.last_name} (${customer.email})`
    };
  } catch (error) {
    console.error('Error fetching customer:', error.response?.data || error.message);
    throw error;
  }
};


// Function to fetch customers
export const fetchCustomers = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/core/customers/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching customers:', error.response?.data || error.message);
    throw error;
  }
};

// Function to fetch customers
export const fetchSalesReps = async () => {
  try {
    const response = await axiosInstance.get('/core/sales-reps/');
    return response.data;
  } catch (error) {
    console.error('Error fetching sales representatives:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

// Function to create customers
export const createCustomer = async (customer) => {
  try {
    if (!customer.first_name) throw new Error('First name is required');
    if (!customer.last_name) throw new Error('Last name is required');
    if (!customer.email) throw new Error('Email is required');
    
    console.log('Sending customer data:', customer);
    const response = await axiosInstance.post('/core/customers/', customer);
    console.log('Server response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating customer:', error.response?.data || error.message);
    throw error;
  }
};

// Function to update customers
export const updateCustomer = async (id, customer) => {
  try {
    if (!id || !customer) {
      throw new Error('Customer ID and updated data are required');
    }
    if (!customer.first_name || !customer.last_name) {
      throw new Error('First name and last name are required');
    }
    console.log('Updating customer with data:', customer);
    const response = await axiosInstance.put(`/core/customers/${id}/`, customer);
    return response.data;
  } catch (error) {
    console.error('Error updating customer:', error.response?.data || error.message);
    throw error;
  }
};

// Function to delete customers
export const deleteCustomer = async (id) => {
  try {
    if (!id) {
      throw new Error('Customer ID is required');
    }
    await axiosInstance.delete(`/core/customers/${id}/`);
  } catch (error) {
    console.error('Error deleting customer:', error.response?.data || error.message);
    
    throw error;
  }
};

// Function to create a new product
export const createProduct = async (productData) => {
  try {
    if (!productData.name || !productData.price || !productData.sku || !productData.stock) {
      throw new Error('Name, price, SKU, and stock are required for a product');
    }
    console.log('Sending request to:', `${BASE_URL}/products/products/`);
    console.log('Product data:', productData);
    const response = await axiosInstance.post('/products/products/', productData);
    return response.data;
  } catch (error) {
    console.error('Error creating product:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchProducts = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/products/products/', { params });
      if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch products');
    }
  } catch (error) {
    console.error('Error fetching products:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchProductPriceTiers = async (productId) => {
  try {
    const response = await axiosInstance.get(`/products/products/${productId}/price-tiers/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching price tiers:', error.response?.data || error.message);
    throw error;
  }
};

export const createProductPriceTier = async (productId, tierData) => {
  try {
    const response = await axiosInstance.post(
      `/products/products/${productId}/price-tiers/`,
      tierData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating price tier:', error.response?.data || error.message);
    throw error;
  }
};

export const updateProductPriceTier = async (productId, tierId, tierData) => {
  try {
    const response = await axiosInstance.patch(
      `/products/products/${productId}/price-tiers/${tierId}/`,
      tierData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating price tier:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteProductPriceTier = async (productId, tierId) => {
  try {
    const response = await axiosInstance.delete(
      `/products/products/${productId}/price-tiers/${tierId}/`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting price tier:', error.response?.data || error.message);
    throw error;
  }
};

// ── Customer Price Agreements ─────────────────────────────────────────────────

export const fetchCustomerPriceAgreements = async (productId) => {
  try {
    const response = await axiosInstance.get(
      `/products/products/${productId}/price-agreements/`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching price agreements:', error.response?.data || error.message);
    throw error;
  }
};

export const createCustomerPriceAgreement = async (productId, agreementData) => {
  try {
    const response = await axiosInstance.post(
      `/products/products/${productId}/price-agreements/`,
      agreementData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating price agreement:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteCustomerPriceAgreement = async (productId, agreementId) => {
  try {
    const response = await axiosInstance.delete(
      `/products/products/${productId}/price-agreements/${agreementId}/`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting price agreement:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchProductByBarcode = async (barcode) => {
  try {
    const response = await axiosInstance.get(`/products/products/barcode/${barcode}/`);
    if (response.status === 200) {
      return response.data;
    }
    throw new Error('Failed to fetch product');
  } catch (error) {
    console.error('Error fetching product by barcode:', error.response?.data || error.message);
    throw error;
  }
};

export const scanBarcodeProduct = async (productId, data) => {
  try {
    const response = await axiosInstance.post(`/products/products/${productId}/scan-barcode/`, data);
    if (response.status === 201) {
      return response.data;
    }
    throw new Error('Failed to process barcode scan');
  } catch (error) {
    console.error('Error processing barcode scan:', error.response?.data || error.message);
    throw error;
  }
};

// Function to bulk delete products
export const bulkDeleteProducts = async (productIds) => {
  try {
    const response = await axiosInstance.post('/products/products/bulk_delete/', { ids: productIds });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to delete products');
    }
  } catch (error) {
    console.error('Error deleting products:', error.response?.data || error.message);
    throw error;
  }
};

// Function to export products as CSV
export const exportProductsCsv = async () => {
  try {
    const response = await axiosInstance.get('/products/products/export_csv/', {
      responseType: 'blob', // Important for file downloads
    });
    if (response.status === 200) {
      // Create a Blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });
      // Create a link element and trigger the download
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'products_export.csv';
      link.click();
      return true;
    } else {
      throw new Error('Failed to export products as CSV');
    }
  } catch (error) {
    console.error('Error exporting products as CSV:', error.response?.data || error.message);
    throw error;
  }
};

// Function to export products as PDF
export const exportProductsPdf = async () => {
  try {
    const response = await axiosInstance.get('/products/products/export_pdf/', {
      responseType: 'blob', // Important for file downloads
    });
    if (response.status === 200) {
      // Create a Blob from the response data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      // Create a link element and trigger the download
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'products_export.pdf';
      link.click();
      return true;
    } else {
      throw new Error('Failed to export products as PDF');
    }
  } catch (error) {
    console.error('Error exporting products as PDF:', error.response?.data || error.message);
    throw error;
  }
};

// Function to fetch product details
export const fetchProductDetails = async (productId) => {
  try {
    const response = await axiosInstance.get(`/products/products/${productId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product details:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get detailed information about a specific product
 * @param {number} productId - The ID of the product to fetch
 * @returns {Promise<Object>} Product details object
 */
export const getProductDetails = async (productId) => {
  try {
    const response = await axiosInstance.get(`/products/products/${productId}/`);

    // Transform the response to match what the component expects
    const product = response.data;

    return {
      id: product.id,
      name: product.name,
      price: product.price,
      stock: product.stock,
      department: product.department,
      department_name: product.department_name,
      is_shared_across_departments: product.is_shared_across_departments,
      branch: product.branch,
      sku: product.sku,
      description: product.description,
      category: product.category,
      images: product.images || [],
      created_at: product.created_at,
      updated_at: product.updated_at
    };
  } catch (error) {
    console.error('Error fetching product details:', error);

    // Handle different error scenarios
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.error || error.response.data?.detail || 'Failed to fetch product details';

      if (status === 404) {
        throw new Error('Product not found');
      } else if (status === 403) {
        throw new Error('You do not have permission to view this product');
      } else if (status === 401) {
        throw new Error('Authentication required');
      } else {
        throw new Error(message);
      }
    } else if (error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Transfer a product to a different department
 * @param {number} productId - The ID of the product to transfer
 * @param {Object} transferData - Transfer configuration object
 * @param {string} transferData.target_department - ID of the target department
 * @param {string} transferData.transfer_type - 'move' or 'copy'
 * @param {boolean} transferData.keep_original - Whether to keep original (for copy operations)
 * @param {boolean} transferData.transfer_stock - Whether to transfer stock
 * @param {string} transferData.notes - Optional transfer notes
 * @returns {Promise<Object>} Transfer result object
 */
export const transferProductToDepartment = async (productId, transferData) => {
  try {
    const {
      target_department,
      transfer_type,
      keep_original,
      transfer_stock,
      notes
    } = transferData;

    // Validate required fields
    if (!target_department) {
      throw new Error('Target department is required');
    }

    if (!['move', 'copy'].includes(transfer_type)) {
      throw new Error('Transfer type must be either "move" or "copy"');
    }

    // For move operations, use the existing transfer_department endpoint
    if (transfer_type === 'move') {
      const response = await axiosInstance.post(
        `/products/products/${productId}/transfer-department/`,
        {
          department_id: target_department,
          notes: notes || ''
        }
      );

      return {
        success: true,
        message: response.data.message,
        product: response.data.product,
        transfer_type: 'move'
      };
    }

    // For copy operations, use the dedicated copy endpoint
    if (transfer_type === 'copy') {
      const response = await axiosInstance.post(
        `/products/products/${productId}/copy-to-department/`,
        {
          target_department: target_department,
          transfer_stock: transfer_stock || false,
          notes: notes || ''
        }
      );

      return {
        success: true,
        message: response.data.message,
        original_product: response.data.original_product,
        copied_product: response.data.copied_product,
        transfer_type: 'copy',
        transfer_stock: response.data.transfer_stock
      };
    }

  } catch (error) {
    console.error('Error transferring product:', error);

    // Handle different error scenarios
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.error || error.response.data?.detail || 'Failed to transfer product';

      if (status === 404) {
        throw new Error('Product or target department not found');
      } else if (status === 403) {
        throw new Error('You do not have permission to transfer this product');
      } else if (status === 401) {
        throw new Error('Authentication required');
      } else if (status === 400) {
        // Handle validation errors
        if (error.response.data?.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error('Invalid transfer request');
        }
      } else {
        throw new Error(message);
      }
    } else if (error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error(error.message || 'An unexpected error occurred during transfer');
    }
  }
};

/**
 * Transfer products using bulk operations endpoint
 * This works with your existing bulk_operations endpoint
 * @param {number|Array} productIds - Single product ID or array of product IDs
 * @param {Object} transferData - Transfer configuration object
 * @returns {Promise<Object>} Bulk transfer result object
 */
export const transferProductToDepartmentBulk = async (productIds, transferData) => {
  try {
    // Ensure productIds is an array
    const idsArray = Array.isArray(productIds) ? productIds : [productIds];

    const response = await axiosInstance.post(
      '/products/products/bulk-operations/',
      {
        operation: 'transfer',
        product_ids: idsArray,
        data: {
          target_department: transferData.target_department,
          transfer_type: transferData.transfer_type || 'move',
          transfer_stock: transferData.transfer_stock || false,
          notes: transferData.notes || ''
        }
      }
    );

    return {
      success: true,
      message: response.data.message,
      results: response.data.results,
      successful_transfers: response.data.successful_transfers,
      failed_transfers: response.data.failed_transfers,
      target_department: response.data.target_department,
      transfer_type: response.data.transfer_type
    };
  } catch (error) {
    console.error('Error in bulk transfer:', error);

    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.error || error.response.data?.detail || 'Failed to perform bulk transfer';

      if (status === 404) {
        throw new Error('No valid products found for transfer');
      } else if (status === 403) {
        throw new Error('You do not have permission to transfer these products');
      } else if (status === 401) {
        throw new Error('Authentication required');
      } else if (status === 400) {
        throw new Error(message);
      } else {
        throw new Error(message);
      }
    } else if (error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error(error.message || 'An unexpected error occurred during bulk transfer');
    }
  }
};

/**
 * Toggle product sharing across all departments
 */
export const toggleProductSharing = async (productId) => {
  try {
    const response = await axiosInstance.post(`/products/${productId}/toggle-sharing/`);
    return response.data;
  } catch (error) {
    console.error('Error toggling product sharing:', error);
    throw error;
  }
};

/**
 * Fetch shared products across departments
 */
export const fetchSharedProducts = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/products/products/shared/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching shared products:', error);
    throw error;
  }
};

/**
 * Fetch products for a specific department
 */
export const fetchDepartmentProducts = async (departmentId, params = {}) => {
  try {
    const response = await axiosInstance.get(`/products/department/${departmentId}/`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching department products:', error);
    throw error;
  }
};

/**
 * Share product with specific departments
 */
export const shareProductToDepartments = async (productId, departmentIds) => {
  try {
    const response = await axiosInstance.post(`/products/${productId}/share-to-departments/`, {
      department_ids: departmentIds
    });
    return response.data;
  } catch (error) {
    console.error('Error sharing product to departments:', error);
    throw error;
  }
};

/**
 * Remove product sharing (make it private to its original department)
 */
export const removeProductSharing = async (productId) => {
  try {
    const response = await axiosInstance.post(`/products/${productId}/remove-sharing/`);
    return response.data;
  } catch (error) {
    console.error('Error removing product sharing:', error);
    throw error;
  }
};

/**
 * Bulk update products with common fields
 * @param {Array} productIds - Array of product IDs to update
 * @param {Object} updateData - Data to update (category, supplier, etc.)
 * @returns {Promise} API response
 */
export const bulkUpdateProducts = async (productIds, updateData) => {
  try {
    const response = await axiosInstance.post('/products/bulk-operations/', {
      operation: 'update',
      product_ids: productIds,
      data: updateData
    });
    return response.data;
  } catch (error) {
    console.error('Bulk update products error:', error);
    throw error;
  }
};

/**
 * Bulk transfer products to a different department
 * @param {Array} productIds - Array of product IDs to transfer
 * @param {number} targetDepartmentId - ID of the target department
 * @returns {Promise} API response
 */
export const bulkTransferProducts = async (productIds, targetDepartmentId) => {
  try {
    const response = await axiosInstance.post('/products/bulk-operations/', {
      operation: 'transfer',
      product_ids: productIds,
      data: {
        target_department: targetDepartmentId
      }
    });
    return response.data;
  } catch (error) {
    console.error('Bulk transfer products error:', error);
    throw error;
  }
};

/**
 * Bulk toggle product status (active/inactive)
 * @param {Array} productIds - Array of product IDs to update
 * @param {boolean} status - New status (true for active, false for inactive)
 * @returns {Promise} API response
 */
export const bulkToggleProductStatus = async (productIds, status) => {
  try {
    const response = await axiosInstance.post('/products/bulk-operations/', {
      operation: 'toggle_status',
      product_ids: productIds,
      data: {
        status: status
      }
    });
    return response.data;
  } catch (error) {
    console.error('Bulk toggle product status error:', error);
    throw error;
  }
};

/**
 * Bulk update product prices
 * @param {Array} productIds - Array of product IDs to update
 * @param {Object} priceData - Price adjustment data
 * @param {number} priceData.adjustment - Adjustment value
 * @param {string} priceData.type - Adjustment type ('percentage', 'fixed', 'discount')
 * @returns {Promise} API response
 */
export const bulkUpdateProductPrices = async (productIds, priceData) => {
  try {
    const response = await axiosInstance.post('/products/bulk-operations/', {
      operation: 'update_prices',
      product_ids: productIds,
      data: {
        adjustment: priceData.adjustment,
        adjustment_type: priceData.type
      }
    });
    return response.data;
  } catch (error) {
    console.error('Bulk update product prices error:', error);
    throw error;
  }
};

/**
 * Bulk update product stock levels
 * @param {Array} productIds - Array of product IDs to update
 * @param {Object} stockData - Stock adjustment data
 * @param {number} stockData.adjustment - Adjustment value
 * @param {string} stockData.type - Adjustment type ('add', 'subtract', 'set')
 * @returns {Promise} API response
 */
export const bulkUpdateProductStock = async (productIds, stockData) => {
  try {
    const response = await axiosInstance.post('/products/bulk-operations/', {
      operation: 'update_stock',
      product_ids: productIds,
      data: {
        adjustment: stockData.adjustment,
        adjustment_type: stockData.type
      }
    });
    return response.data;
  } catch (error) {
    console.error('Bulk update product stock error:', error);
    throw error;
  }
};

export const fetchStockLevels = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/stock_adjustments/stock-levels/', {
      params: {
        ...params,
        // Remove empty/undefined params
        ...Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== undefined && value !== '')
        )
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching stock levels:', error);
    throw error;
  }
};

export const fetchRawMaterialStockLevels = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/bakery-recipe/raw-material-stock-levels/', {
      params: Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== '')
      )
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching raw material stock levels:', error.response?.data || error.message);
    throw error;
  }
};

// Stock Transfers API Functions
export const fetchStockTransfers = async (params = {}) => {
  try {
    // Build query string manually to handle arrays properly
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          // Add each array item as a separate parameter
          value.forEach(item => queryParams.append(key, item));
        } else {
          queryParams.append(key, value);
        }
      }
    });

    const queryString = queryParams.toString();
    const url = `/stock_adjustments/stock-transfers/${queryString ? '?' + queryString : ''}`;
    
    console.log('Request URL:', url); // Debug log to see the actual URL
    
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching stock transfers:', error);
    throw error;
  }
};

export const createStockTransfer = async (transferData) => {
  try {
    const response = await axiosInstance.post('/stock_adjustments/stock-transfers/', {
      product: transferData.product_id,
      quantity_requested: transferData.quantity_requested,
      from_branch: transferData.from_branch_id,
      from_department: transferData.from_department_id,
      to_branch: transferData.to_branch_id,
      to_department: transferData.to_department_id,
      transfer_type: transferData.transfer_type || 'INTER_DEPARTMENT',
      transfer_reason: transferData.transfer_reason || 'Stock rebalancing',
      priority: transferData.priority || 'MEDIUM',
      requires_approval: transferData.requires_approval !== false, // defaults to true
      estimated_delivery: transferData.estimated_delivery
    });
    return response.data;
  } catch (error) {
    console.error('Error creating stock transfer:', error);
    throw error;
  }
};

export const approveStockTransfer = async (transferId, approvalData = {}) => {
  try {
    const response = await axiosInstance.patch(`/stock_adjustments/stock-transfers/${transferId}/`, {
      status: 'APPROVED',
      quantity_approved: approvalData.quantity_approved,
      approval_notes: approvalData.approval_notes || '',
      approved_at: new Date().toISOString()
    });
    return response.data;
  } catch (error) {
    console.error('Error approving stock transfer:', error);
    throw error;
  }
};

export const fetchBranchById = async (branchId) => {
  try {
    const response = await axiosInstance.get(`/core/branches/${branchId}/`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch branch with ID ${branchId}:`, error);
    throw error;
  }
};

// Fetch department by ID
export const fetchDepartmentById = async (departmentId) => {
  try {
    const response = await axiosInstance.get(`/core/departments/${departmentId}/`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch department with ID ${departmentId}:`, error);
    throw error;
  }
};

// Department Stock Summary API Function
export const fetchDepartmentStockSummary = async (branchId, departmentId) => {
  try {
    const params = {};
    if (branchId) params.branch = branchId;
    if (departmentId) params.department = departmentId;

    const response = await axiosInstance.get('/stock_adjustments/stock-levels/stock_summary/', { params });

    const summary = response.data;

    let enhancedSummary = {
      ...summary,
      low_stock_items: summary.low_stock_count,
      out_of_stock_items: summary.out_of_stock_count,
    };

    if (branchId || departmentId) {
      try {
        const [branchData, departmentData] = await Promise.all([
          branchId ? fetchBranchById(branchId) : Promise.resolve(null),
          departmentId ? fetchDepartmentById(departmentId) : Promise.resolve(null),
        ]);

        enhancedSummary.branch_name = branchData?.name || 'All Branches';
        enhancedSummary.department_name = departmentData?.name || 'All Departments';
      } catch (nameError) {
        console.warn('Could not fetch branch/department names:', nameError);
        enhancedSummary.branch_name = branchId ? `Branch ${branchId}` : 'All Branches';
        enhancedSummary.department_name = departmentId ? `Department ${departmentId}` : 'All Departments';
      }
    } else {
      enhancedSummary.branch_name = 'All Branches';
      enhancedSummary.department_name = 'All Departments';
    }

    return enhancedSummary;
  } catch (error) {
    console.error('Error fetching department stock summary:', error);
    throw error;
  }
};

// Additional helper functions that might be useful

export const updateStockLevel = async (stockLevelId, updateData) => {
  try {
    const response = await axiosInstance.patch(`/stock_adjustments/stock-levels/${stockLevelId}/`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error updating stock level:', error);
    throw error;
  }
};

export const completeStockTransfer = async (transferId, completionData = {}) => {
  try {
    const response = await axiosInstance.patch(`/stock_adjustments/stock-transfers/${transferId}/`, {
      status: 'COMPLETED',
      quantity_transferred: completionData.quantity_transferred,
      actual_delivery: new Date().toISOString(),
      completed_at: new Date().toISOString()
    });
    return response.data;
  } catch (error) {
    console.error('Error completing stock transfer:', error);
    throw error;
  }
};

export const rejectStockTransfer = async (transferId, rejectionReason) => {
  try {
    const response = await axiosInstance.patch(`/stock_adjustments/stock-transfers/${transferId}/`, {
      status: 'REJECTED',
      rejection_reason: rejectionReason
    });
    return response.data;
  } catch (error) {
    console.error('Error rejecting stock transfer:', error);
    throw error;
  }
};

// Bulk operations
export const bulkUpdateStockLevels = async (updates) => {
  try {
    const response = await axiosInstance.post('/stock_adjustments/stock-levels/bulk-update/', {
      updates
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk updating stock levels:', error);
    throw error;
  }
};

export const getStockLevelsByProduct = async (productId, params = {}) => {
  try {
    const response = await axiosInstance.get('/stock_adjustments/stock-levels/', {
      params: {
        product: productId,
        ...params
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching stock levels by product:', error);
    throw error;
  }
};

export const createStockAdjustment = async (data) => {
  try {
    // Ensure we're sending 'product' instead of 'product_id'
    const adjustmentData = {
      ...data,
      product: data.product_id || data.product,
    };
    
    // Remove product_id if it exists to avoid confusion
    delete adjustmentData.product_id;

    const response = await axiosInstance.post('/stock_adjustments/stock_adjustments/', adjustmentData);
    return response.data;
  } catch (error) {
    console.error('Error creating stock adjustment:', error);
    throw error;
  }
};

export const fetchStockAdjustments = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/stock_adjustments/stock_adjustments/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching stock adjustments:', error);
    throw error;
  }
};

export const fetchAdjustmentTypes = async () => {
  try {
    const response = await axiosInstance.get('/stock_adjustments/stock_adjustments/get_adjustment_types/');
    
    // Check if the response data is already an array
    if (Array.isArray(response.data)) {
      return response.data; // Directly return the array
    } else {
      console.error('Unexpected API response:', response.data);
      return []; // Return an empty array as a fallback
    }
  } catch (error) {
    console.error('Error fetching adjustment types:', error);
    return []; // Ensure it returns an empty array on error
  }
};

export const updateStockAdjustment = async (id, data) => {
  try {
    const response = await axiosInstance.put(`/stock_adjustments/stock_adjustments/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating stock adjustment:', error);
    throw error;
  }
};

export const deleteStockAdjustment = async (id) => {
  try {
    await axiosInstance.delete(`/stock_adjustments/stock_adjustments/${id}/`);
  } catch (error) {
    console.error('Error deleting stock adjustment:', error);
    throw error;
  }
};

export const bulkDeleteStockAdjustments = async (ids) => {
  try {
    await axiosInstance.post('/stock_adjustments/stock_adjustments/bulk_delete/', { ids });
  } catch (error) {
    console.error('Error bulk deleting stock adjustments:', error);
    throw error;
  }
};

export const exportStockAdjustmentsCsv = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/stock_adjustments/stock_adjustments/export_csv/', {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting stock adjustments to CSV:', error);
    throw error;
  }
};

export const exportStockAdjustmentsPdf = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/stock_adjustments/stock_adjustments/export_pdf/', {
      params,
      responseType: 'blob',
    });
    
    // Create a blob from the response data
    const blob = new Blob([response.data], { type: 'application/pdf' });
    
    // Create a link element and trigger the download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'stock_adjustments.pdf');
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return response.data;
  } catch (error) {
    console.error('Error exporting stock adjustments to PDF:', error);
    throw error;
  }
};

// Function to update product stock
export const updateProductStock = async (productId, newStock) => {
  try {
    if (!productId || typeof newStock !== 'number') {
      throw new Error('Product ID and new stock value are required');
    }
    const response = await axiosInstance.patch(`/products/${productId}/`, {
      stock: newStock
    });
    return response.data;
  } catch (error) {
    console.error('Error updating product stock:', error.response?.data || error.message);
    throw error;
  }
};

// Function to fetch Top product
export const fetchTopProducts = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('fetchTopProducts input dates:', { startDate, endDate });

    // Process the date range properly
    const { formattedStartDate, formattedEndDate } = dateUtils.processDateRange(startDate, endDate);
    
    const params = {
      start_date: formattedStartDate,
      end_date: formattedEndDate
    };

    console.log('API request params:', params);
    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;
    const response = await axiosInstance.get('/products/top/', {
      headers: getAuthHeader(),
      params,
    });
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching top products:', error.response?.data || error.message);
    throw error;
  }
};

// Function to fetch recent transaction
export const fetchRecentTransactions = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('fetchRecentTransactions input dates:', { startDate, endDate });

    // Process the date range properly
    const { formattedStartDate, formattedEndDate } = dateUtils.processDateRange(startDate, endDate);
    
    const params = {
      start_date: formattedStartDate,
      end_date: formattedEndDate
    };

    console.log('API request params:', params);

    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;

    const response = await axiosInstance.get('/transactions/recent/', {
      headers: getAuthHeader(),
      params,
    });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching recent transactions:', error.response?.data || error.message);
    throw error;
  }
};

// Function to fetch net profit data
export const fetchNetProfitData = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('fetchNetProfitData input dates:', { startDate, endDate });

    // Process the date range properly
    const { formattedStartDate, formattedEndDate } = dateUtils.processDateRange(startDate, endDate);

    const params = {
      start_date: formattedStartDate,
      end_date: formattedEndDate
    };

    console.log('API request params:', params);

    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;
    
    const response = await axiosInstance.get('/analytics/net-profit/', {
      headers: getAuthHeader(),
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching net profit data:', error.response?.data || error.message);
    throw error;
  }
};

// function to fetch ConversionRate Data
export const fetchConversionRateData = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('fetched Conversion Rate input dates:', { startDate, endDate });

    // Process the date range properly
    const { formattedStartDate, formattedEndDate } = dateUtils.processDateRange(startDate, endDate);
    
    const params = {
      start_date: formattedStartDate,
      end_date: formattedEndDate
    };

    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;

    console.log('API request params:', params);
    
    const response = await axiosInstance.get('/analytics/conversion-rate/', {
      headers: getAuthHeader(),
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching conversion rate data:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchInventoryLevels = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.group('🔍 Inventory Levels Fetch Debug');

    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Authentication token is missing');
    }

    // ✅ Properly define params first
    const params = {
      start_date: startDate,
      end_date: endDate
    };

    // ✅ Safely append optional filters
    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;

    console.log('Request Configuration:', {
      endpoint: '/inventory/levels/',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      },
      params
    });

    const response = await axiosInstance.get('/inventory/levels/', {
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      },
      params, // ✅ Use the params object here
      validateStatus: (status) => {
        return status < 500;
      }
    });

    if (response.status === 200) {
      const inventoryData = response.data?.data || [];
      console.log('✅ Successful Response:', {
        status: response.status,
        dataLength: inventoryData.length,
        data: inventoryData
      });
      console.groupEnd();
      return inventoryData;
    } else {
      throw new Error(`Unexpected response status: ${response.status}`);
    }

  } catch (error) {
    console.error('❌ Inventory Fetch Error Details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      requestConfig: error.config
    });

    const formattedError = {
      message: error.response?.data?.detail || error.message,
      status: error.response?.status,
      timestamp: new Date().toISOString(),
      requestParams: { startDate, endDate },
      technical_details: error.response?.data?.error || error.response?.data
    };

    console.groupEnd();
    throw formattedError;
  }
};


export const fetchCashFlow = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('Fetched Cash Flow input dates:', { startDate, endDate });

    // Process the date range properly
    const { formattedStartDate, formattedEndDate } = dateUtils.processDateRange(startDate, endDate);
    
    const params = {
      start_date: formattedStartDate,
      end_date: formattedEndDate
    };

    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;

    console.log('API request params:', params); 

    const response = await axiosInstance.get('/finance/cash-flow/', {
      headers: getAuthHeader(),
      params,
    });

    console.log('Cash flow API response:', response.data);
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching cash flow:', error);
    console.error('Error details:', error.response?.data);
    return [];
  }
};

// Tax Analytics
export const fetchTaxAnalytics = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('Fetching Tax Analytics:', { startDate, endDate, branchId, departmentId });

    const year = moment(startDate).year();
    const params = {
      period: 'monthly',
      year: year
    };

    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;

    console.log('Tax Analytics API request params:', params);

    const response = await axiosInstance.get('/analytics/tax/', {
      headers: getAuthHeader(),
      params,
    });

    console.log('Tax Analytics API response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching tax analytics:', error);
    console.error('Error details:', error.response?.data);
    return null;
  }
};

// Compliance Analytics
export const fetchComplianceAnalytics = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('Fetching Compliance Analytics:', { startDate, endDate, branchId, departmentId });

    const year = moment(startDate).year();
    const params = {
      period: 'monthly',
      year: year
    };

    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;

    console.log('Compliance Analytics API request params:', params);

    const response = await axiosInstance.get('/analytics/compliance/', {
      headers: getAuthHeader(),
      params,
    });

    console.log('Compliance Analytics API response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching compliance analytics:', error);
    console.error('Error details:', error.response?.data);
    return null;
  }
};

// Fund Management Analytics
export const fetchFundAnalytics = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('Fetching Fund Analytics:', { startDate, endDate, branchId, departmentId });

    const year = moment(startDate).year();
    const params = {
      period: 'monthly',
      year: year
    };

    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;

    console.log('Fund Analytics API request params:', params);

    const response = await axiosInstance.get('/finance/fund-management/', {
      headers: getAuthHeader(),
      params,
    });

    console.log('Fund Analytics API response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching fund analytics:', error);
    console.error('Error details:', error.response?.data);
    return null;
  }
};

// Audit Log Analytics
export const fetchAuditLogAnalytics = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('Fetching Audit Log Analytics:', { startDate, endDate, branchId, departmentId });

    const days = moment(endDate).diff(moment(startDate), 'days') || 30;
    const params = {
      period: 'daily',
      days: days
    };

    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;

    console.log('Audit Log Analytics API request params:', params);

    const response = await axiosInstance.get('/admin/audit-logs/', {
      headers: getAuthHeader(),
      params,
    });

    console.log('Audit Log Analytics API response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching audit log analytics:', error);
    console.error('Error details:', error.response?.data);
    return null;
  }
};

export const fetchReceipt = async (id) => {
  try {
    const response = await axiosInstance.get(`/receipts/${id}/`);
    console.log('Receipt API response:', response.data);
    return response.data;
  } catch (error) {
    console.error('API error:', error);
    throw error;
  }
};

// Function to fetch receipts
export const fetchReceipts = async (params) => {
  console.log('fetchReceipts called with params:', params);
  try {
    console.log('Sending GET request to /receipts/');
    const response = await axiosInstance.get('/receipts/', { params });
    console.log('Raw response:', response);

    if (response.status !== 200) {
      throw new Error(`Unexpected status code: ${response.status}`);
    }

    if (!response.data) {
      throw new Error('Response data is undefined');
    }

    console.log('Receipts fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error in fetchReceipts:', error);
    throw error;
  }
};

export const checkReceiptExists = async (invoiceId) => {
  try {
    const response = await fetch(`/invoices/invoices/${invoiceId}/has-receipt/`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to check receipt status');
    }

    const data = await response.json();
    return data.has_receipt;
  } catch (error) {
    console.error('Error checking receipt status:', error);
    return false; // Default to false if check fails
  }
};

// Function to create a new receipt
export const createReceipt = async (receiptData) => {
  try {
    const response = await axiosInstance.post('/receipts/', receiptData);
    return response.data;
  } catch (error) {
    console.error('Error creating receipt:', error);
    throw error;
  }
};

// Function to update a receipt
export const updateReceipt = async (receiptId, receiptData) => {
  try {
    const response = await axiosInstance.put(`/receipts/${receiptId}/`, receiptData);
    return response.data;
  } catch (error) {
    console.error('Error updating receipt:', error);
    throw error;
  }
};

// Function to delete a receipt
export const deleteReceipt = async (receiptId) => {
  try {
    await axiosInstance.delete(`/receipts/${receiptId}/`);
    return true;
  } catch (error) {
    console.error('Error deleting receipt:', error);
    throw error;
  }
};

// Function to bulk delete receipts
export const bulkDeleteReceipts = async (receiptIds) => {
  try {
    await axiosInstance.post('/receipts/bulk-delete/', { receipt_ids: receiptIds });
    return true;
  } catch (error) {
    console.error('Error bulk deleting receipts:', error);
    throw error;
  }
};

export const generateReceiptPDF = async (receiptId) => {
  try {
    const response = await axiosInstance.get(`/receipts/${receiptId}/pdf/`, {
      responseType: 'blob',
    });

    // Create a URL for the blob
    const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
    const today = format(new Date(), 'yyyy-MM-dd');

    // Trigger download
    saveAs(pdfBlob, `Receipt-${receiptId}-${today}.pdf`);

    return response.data;
  } catch (error) {
    console.error('Error generating receipt PDF:', error);
    throw error;
  }
};

export const exportReceiptsPdf = async (ids, format = 'detailed') => {
  try {
    const response = await axiosInstance.post(
      `/receipts/export/pdf/`,
      {
        ids,
        format,
      },
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf',
        },
      }
    );

    // Check if the response is valid
    if (response.status !== 200 || !response.data) {
      throw new Error(`Failed to generate PDF: ${response.statusText}`);
    }

    // Create a URL for the blob
    const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
    const today = new Date().toISOString().split('T')[0]; // Format as YYYY-MM-DD
    const fileName = ids.length > 1
      ? `receipts-export-${today}.pdf`
      : `receipt-${ids[0]}-${today}.pdf`;

    // Trigger download
    saveAs(pdfBlob, fileName);
    
    return response.data;
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    // Provide more detailed error information if available
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    }
    throw error;
  }
};

// Function to create an invoice
export const createInvoice = async (invoiceData) => {
  try {
    const response = await axiosInstance.post('/invoices/invoices/', invoiceData);
    console.log('API response for createInvoice:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating invoice:', error.response?.data || error.message);
    throw error;
  }
};

// Function to fetch invoices
export const fetchInvoices = async (params) => {
  console.log('fetchInvoices called with params:', params);
  try {
    console.log('Sending GET request to /invoices/invoices/');
    const response = await axiosInstance.get('/invoices/invoices/', { params });
    console.log('Raw response:', response);

    if (response.status !== 200) {
      throw new Error(`Unexpected status code: ${response.status}`);
    }

    if (!response.data) {
      throw new Error('Response data is undefined');
    }

    console.log('Invoices fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error in fetchInvoices:', error);
    throw error;
  }
};

// Function to fetch a specific invoice by ID
export const fetchInvoice = async (invoiceId) => {
  console.log(`Fetching invoice with ID: ${invoiceId}`);
  try {
    const response = await axiosInstance.get(`/invoices/invoices/${invoiceId}/`);
    console.log('Invoice fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching invoice ${invoiceId}:`, error.response?.data || error.message);
    throw error;
  }
};

export const fetchInvoiceSummary = async (params) => {
  try {
    const response = await axiosInstance.get('/invoices/invoices/summary/', { params });
    if (response.status !== 200) {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
    return response.data;
  } catch (error) {
    console.error('Error in fetchInvoiceSummary:', error);
    throw error;
  }
};

// Function to update an invoice
export const updateInvoice = async (invoiceId, invoiceData) => {
  try {
    const response = await axiosInstance.put(`/invoices/invoices/${invoiceId}/`, invoiceData);
    return response.data;
  } catch (error) {
    console.error('Error updating invoice:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchInvoicePayments = async (invoiceId) => {
  try {
    const response = await axiosInstance.get(
      '/invoices/invoice-payments/',
      { params: { invoice_id: invoiceId } }
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error fetching invoice payments:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const recordInvoicePayment = async (paymentData) => {
  try {
    const response = await axiosInstance.post(
      '/invoices/invoice-payments/',
      paymentData
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error recording invoice payment:',
      error.response?.data || error.message
    );
    throw error;
  }
};

// Function to fetch company information
export const fetchCompanyInfo = async () => {
  try {
    const response = await axiosInstance.get('/core/company-info/');
    // Assuming the API returns an array with a single company info object
    return response.data[0] || null;
  } catch (error) {
    console.error('Error fetching company info:', error.response?.data || error.message);
    throw error;
  }
};

// Function to update company information
export const updateCompanyInfo = async (companyInfo) => {
  try {
    let response;
    if (companyInfo.id) {
      // If the company info has an ID, update the existing record
      response = await axiosInstance.put(`/core/company-info/${companyInfo.id}/`, companyInfo);
    } else {
      // If there's no ID, create a new company info record
      response = await axiosInstance.post('/core/company-info/', companyInfo);
    }
    return response.data;
  } catch (error) {
    console.error('Error updating company info:', error.response?.data || error.message);
    throw error;
  }
};


// Function to delete an invoice
export const deleteInvoice = async (invoiceId) => {
  try {
    await axiosInstance.delete(`/invoices/invoices/${invoiceId}/`);
  } catch (error) {
    console.error('Error deleting invoice:', error.response?.data || error.message);
    throw error;
  }
};

// Function to generate PDF for an invoice
export const generateInvoicePDF = async (invoiceId) => {
  try {
    const response = await axiosInstance.get(`/invoices/invoices/${invoiceId}/generate_pdf/`, {
      responseType: 'blob'
    });
    
    if (response.data instanceof Blob) {
      return response.data;
    } else {
      console.error('Unexpected response type:', response.data);
      throw new Error('Invalid response type. Expected a Blob.');
    }
  } catch (error) {
    console.error('Error in generateInvoicePDF:', error);
    throw error;
  }
};

export const exportPdf = async (exportParams) => {
  try {
    const response = await axiosInstance.post('/invoices/invoices/export_pdf/', exportParams, {
      responseType: 'blob', // Important for file download
    });

    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:T]/g, '-').split('.')[0];
    link.setAttribute('download', `invoice_export_${timestamp}.pdf`);

    document.body.appendChild(link);
    link.click();
    link.remove();

    return response.data; // Return the blob data if needed
  } catch (error) {
    console.error('Export PDF error:', error);

    if (error.response) {
      console.error('Error details:', error.response.data);
    }
    throw error;
  }
};

// Function to mark an invoice as paid
export const markInvoiceAsPaid = async (invoiceId) => {
  try {
    const response = await axiosInstance.post(`/invoices/invoices/${invoiceId}/mark_as_paid/`);
    return response.data;
  } catch (error) {
    console.error('Error marking invoice as paid:', error.response?.data || error.message);
    throw error;
  }
};

export const voidInvoice = async (invoiceId, reason) => {
  try {
    const response = await axiosInstance.post(`/invoices/invoices/${invoiceId}/void/`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error voiding invoice:', error.response?.data || error.message);
    throw error;
  }
};

export const bulkDeleteInvoices = async (invoiceIds) => {
  try {
    const response = await axiosInstance.post('/invoices/invoices/bulk_delete/', { invoice_ids: invoiceIds }, {
    });
    console.log('Invoices deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error deleting invoices:', error);
    if (error.response) {
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
      console.error('Error response headers:', error.response.headers);
      throw new Error(error.response.data.error || 'Failed to delete invoices');
    } else if (error.request) {
      console.error('No response received:', error.request);
      throw new Error('No response received from server');
    } else {
      console.error('Error message:', error.message);
      throw new Error('An error occurred while deleting invoices');
    }
  }
};

// Function to fetch transactions data
export const fetchTransactions = async (params = {}) => {
  try {
    console.log('Calling API to fetch transactions with params:', params);
    const headers = getAuthHeader();
    console.log('Auth headers:', headers);
    
    const response = await axiosInstance.get('/transactions/transactions/', {
      params,
      headers,
    });
    
    console.log('API response received:', response.data);
    
    // Ensure we always return a consistent structure
    const formattedResponse = {
      results: response.data.results || [],
      count: response.data.count || 0,
      next: response.data.next || null,
      previous: response.data.previous || null,
    };
    
    console.log('Number of transactions:', formattedResponse.results.length);
    return formattedResponse;
  } catch (error) {
    console.error('Error in fetchTransactionsAPI:', error.response || error);
    // Return a consistent structure even in case of an error
    return {
      results: [],
      count: 0,
      next: null,
      previous: null,
      error: error.response?.data?.error || 'An unexpected error occurred'
    };
  }
};

// Function to create a transaction
export const createTransaction = async (transactionData) => {
  try {
    const response = await axiosInstance.post('/transactions/transactions/', transactionData, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error creating transaction:', error.response?.data || error.message);
    throw error;
  }
};

// Function to bulk update transactions status
export const bulkUpdateTransactions = async (updatedTransactions) => {
  try {
    const response = await axiosInstance.post('/transactions/transactions/bulk_update_status/', {
      transactions: updatedTransactions
    });
    return response.data;
  } catch (error) {
    console.error('Error updating transactions:', error.response?.data || error.message);
    throw error;
  }
};


// Function to update a transaction
export const updateTransaction = async (id, transactionData) => {
  try {
    const response = await axiosInstance.put(`/transactions/transactions/${id}/`, transactionData);
    return response.data;
  } catch (error) {
    console.error('Error updating transaction:', error.response?.data || error.message);
    console.error('Status:', error.response?.status);
    console.error('Headers:', error.response?.headers);
    console.error('Data sent:', transactionData);
    throw error;
  }
};

// Dedicated cancel/refund call. Uses PATCH (partial update) and sends
// ONLY the status field — never the amount — so a completed transaction's
// amount can never be changed in the same request that triggers the
// balance-reversal signal chain (see transactions/signals.py). Do not
// repurpose updateTransaction (PUT, full payload) for this action.
export const reverseTransaction = async (id, status) => {
  try {
    const response = await axiosInstance.patch(`/transactions/transactions/${id}/`, { status });
    return response.data;
  } catch (error) {
    console.error('Error reversing transaction:', error.response?.data || error.message);
    console.error('Status:', error.response?.status);
    throw error;
  }
};

// Function to delete a transaction
export const deleteTransaction = async (id) => {
  try {
    await axiosInstance.delete(`/transactions/transactions/${id}/`);
  } catch (error) {
    console.error('Error deleting transaction:', error.response?.data || error.message);
    throw error;
  }
};

export const bulkDeleteTransactions = async (ids) => {
  try {
    const response = await axiosInstance.post('/transactions/transactions/bulk_delete/', { ids });
    return response.data;
  } catch (error) {
    console.error('Error in bulkDeleteTransactions:', error.response?.data || error.message);
    throw error.response?.data || { error: 'An unexpected error occurred' };
  }
};

// Function to export transactions to CSV
export const exportTransactionsToCSV = async (params = {}) => {
  try {
  const response = await axiosInstance.get('/transactions/transactions/export_csv/', { params, responseType: 'blob' });
  return response.data;
  } catch (error) {
  console.error('Error exporting transactions to CSV:', error.response?.data || error.message);
  throw error;
  }
};

// Function to fetch summary data with date range
export const fetchSummaryData = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('fetchSummaryData input dates:', { startDate, endDate });

    const { formattedStartDate, formattedEndDate } = dateUtils.processDateRange(startDate, endDate);

    const params = {
      start_date: formattedStartDate,
      end_date: formattedEndDate
    };

    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;

    const netProfitResponse = await axiosInstance.get('/analytics/net-profit/', {
      params,
      headers: getAuthHeader()
    });

    const [products, customers, transactions] = await Promise.all([
      axiosInstance.get('/products/', { params }),
      axiosInstance.get('/core/customers/', { params }),
      axiosInstance.get('/transactions/transactions/', { params })
    ]);

    const totalRevenue = netProfitResponse.data.revenue;

    const transactionData = Array.isArray(transactions.data) ? transactions.data : (transactions.data.results || []);

    const orderCountResponse = await axiosInstance.get('/analytics/transaction-count/', {
      params,
      headers: getAuthHeader()
    });

    const totalOrders = orderCountResponse.data.count;
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    console.log('Processing Results:', {
      totalRevenue,
      totalOrders: totalOrders,
      averageOrderValue
    });

    // CHANGE: Return raw numbers instead of formatted strings
    const summaryData = {
      totalRevenue: totalRevenue,           // ← Return NUMBER, not formatted string
      averageOrderValue: averageOrderValue, // ← Return NUMBER, not formatted string
      totalOrders: totalOrders,
      totalProducts: Array.isArray(products.data) ? products.data.length : (products.data?.results?.length || 0),
      totalCustomers: Array.isArray(customers.data) ? customers.data.length : (customers.data?.results?.length || 0),
      recentTransactions: transactionData.slice(0, 5)
    };

    console.log('Final Summary Data:', summaryData);
    return summaryData;
  } catch (error) {
    console.error('Error in fetchSummaryData:', error);
    throw error;
  }
};

// Function to fetch sales data with date range
export const fetchSalesData = async (startDate, endDate, branchId, departmentId) => {
  try {
    console.log('SalesData input dates:', { startDate, endDate });

    // Process the date range properly
    const { formattedStartDate, formattedEndDate } = dateUtils.processDateRange(startDate, endDate);
    
    const params = {
      start_date: formattedStartDate,
      end_date: formattedEndDate
    };

    if (branchId) params.branch_id = branchId;
    if (departmentId) params.department_id = departmentId;

    console.log('API request params:', params);
    
    // NEW: Use transactions-based sales endpoint
    const response = await axiosInstance.get('/analytics/sales-data/', {
      params,
      headers: getAuthHeader()
    });

    // Return the data directly - it's already in the correct format
    return response.data.data || [];
    
  } catch (error) {
    console.error('Error fetching sales data:', error.response?.data || error.message);
    throw error;
  }
};

// Function to fetch user preferences
export const fetchUserPreferences = async () => {
  try {
    const response = await axiosInstance.get('/users/preferences/', {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user preferences:', error.response?.data || error.message);
    throw error;
  }
};

// Function to update user preferences
export const updateUserPreferences = async (updatedPreferences) => {
  try {
    const response = await axiosInstance.put('/users/preferences/', updatedPreferences, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating user preferences:', error.response?.data || error.message);
    throw error;
  }
};

// Function to fetch detail data
export const fetchDetailData = async (type, id) => {
  try {
    const response = await axiosInstance.get(`/${type}/${id}/`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${type} detail data:`, error.response?.data || error.message);
    throw error;
  }
};

// Function to fetch insights
export const fetchInsights = async () => {
  try {
    const response = await axiosInstance.get('/insights/', {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching insights:', error.response?.data || error.message);
    throw error;
  }
};

// Function to export transactions to PDF
export const exportTransactionsToPDF = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/transactions/transactions/export_pdf/', { 
    params, 
    responseType: 'blob' 
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting transactions to PDF:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchTransactionsSummary = async (params = {}) => {
  try {
    const headers = getAuthHeader();
    const response = await axiosInstance.get('/transactions/transactions/summary/', {
      params,
      headers,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching transactions summary:', error.response?.data || error.message);
    throw error;
  }
};

// Function to create reports
async function createReport(reportData) {
  try {
    console.log('API: Creating report with data:', reportData);
    
    // Send reportData directly - it already contains all fields
    const response = await axiosInstance.post('/reports/reports/', reportData);
    
    console.log('API: Report created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating report:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
}

export { createReport };

// Function to fetch reports
export const fetchReports = async () => {
  try {
    const response = await axiosInstance.get('/reports/reports/');
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error fetching reports:", error.response.data);
      throw new Error(`Failed to fetch reports: ${error.response.data.message || 'Unknown error'}`);
    } else if (error.request) {
      console.error("No response received when fetching reports");
      throw new Error('No response received from server');
    } else {
      console.error("Error setting up request to fetch reports:", error.message);
      throw new Error('Error setting up request');
    }
  }
};

// Function to generate reports
export const generateReport = async (reportData) => {
  if (!reportData || !reportData.name) {
    throw new Error('Report name is required');
  }

  try {
    // Sanitize and prepare report data
    const sanitizedReportData = {
      name: String(reportData.name).trim(), // Ensure name is a string and trimmed
      description: reportData.description || `Generated on ${new Date().toLocaleString()}`,
      is_template: !!reportData.is_template,
      start_date: reportData.startDate || null,
      end_date: reportData.endDate || null
    };

    // Validate name is not empty after trimming
    if (!sanitizedReportData.name) {
      throw new Error('Report name cannot be empty');
    }

    // Perform deep clone to remove any potential circular references
    const cleanPayload = JSON.parse(JSON.stringify(sanitizedReportData));

    // Make API call with clean, sanitized data
    const response = await axiosInstance.post('/reports/reports/', cleanPayload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    // Comprehensive error handling
    if (error.response) {
      // Server responded with an error status
      const errorMessage = 
        (error.response.data.name && error.response.data.name[0]) ||
        error.response.data.detail || 
        error.response.data.message || 
        'An unknown error occurred';

      throw new Error(errorMessage);
    }
    throw error;
  }
};

// Function to download reports
export const downloadReport = async (id, startDate = null, endDate = null, branchId = null, departmentId = null) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    // NEW: Add branch/department context
    if (branchId) params.append('branch_id', branchId);
    if (departmentId) params.append('department_id', departmentId);
    
    const response = await axiosInstance.post(
      `/reports/reports/${id}/generate_pdf/${params.toString() ? '?' + params.toString() : ''}`,
      {},
      { responseType: 'blob' }
    );
    
    // Create a blob URL and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `report_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      throw new Error('Report not found or not yet ready for download');
    }
    console.error("PDF Download Error:", error);
    throw new Error(`Failed to download PDF: ${error.message}`);
  }
};

// Function to delete report
export const deleteReport = async (id) => {
  try {
    const response = await axiosInstance.delete(`/reports/reports/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Delete Report Error:", error);
    throw new Error(`Failed to delete report: ${error.message}`);
  }
};

// Function to export report to CSV
export async function exportReportToCsv(reportId, params) {
  try {
    const response = await axiosInstance.get(
      `/reports/reports/${reportId}/export_csv/?${params.toString()}`,
      {
        responseType: 'blob',
      }
    );

    // Create a URL for the blob
    const blob = new Blob([response.data], { type: 'text/csv' });
    const file_url = window.URL.createObjectURL(blob);

    // Get filename from response headers if available
    const contentDisposition = response.headers['content-disposition'];
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1].replace(/"/g, '')
      : null;

    return { file_url, filename };
  } catch (error) {
    console.error('Error exporting report to CSV:', error);
    throw error;
  }
}


// Function to export report to Excel
export const exportReportToExcel = async (reportId, startDate, endDate) => {
  try {
    const response = await axiosInstance.get(
      `/reports/reports/${reportId}/export_excel/`,
      {
        params: {
          start_date: startDate,
          end_date: endDate
        },
        responseType: 'arraybuffer'
      }
    );

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = window.URL.createObjectURL(blob);
    const filename = `report_${reportId}_${startDate}_${endDate}.xlsx`;

    return { url, filename };
  } catch (error) {
    console.error('Excel export error:', error);
    throw new Error('Failed to export report to Excel');
  }
};

export const sendReportEmail = async (reportId, emailData, dateRange) => {
  try {
    const requestData = {
      ...emailData,
      start_date: dateRange.startDate,
      end_date: dateRange.endDate
    };
    const response = await axiosInstance.post(`/reports/reports/${reportId}/email_report/`, requestData);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error sending report email:", error.response.data);
      throw new Error(`Failed to send report email: ${error.response.data.message || 'Unknown error'}`);
    } else if (error.request) {
      console.error("No response received when sending report email");
      throw new Error('No response received from server');
    } else {
      console.error("Error setting up request to send report email:", error.message);
      throw new Error('Error setting up request');
    }
  }
};

// Function to clone a report template
export async function cloneReportTemplate(templateId) {
  try {
    const response = await axiosInstance.post(`/reports/reports/${templateId}/clone_template/`);
    return response.data;
  } catch (error) {
    console.error('Error cloning report template:', error);
    throw error;
  }
}

// Function to create a calculated field
export async function createCalculatedField(reportId, name, formula) {
  try {
    const response = await axiosInstance.post('/reports/calculated-fields/', {
      report: reportId,
      name,
      formula
    });
    return response.data;
  } catch (error) {
    console.error('Error creating calculated field:', error);
    throw error;
  }
}

// Function to calculate a custom field
export async function calculateCustomField(fieldId) {
  try {
    const response = await axiosInstance.get(`/reports/calculated-fields/${fieldId}/calculate/`);
    return response.data.result;
  } catch (error) {
    console.error('Error calculating custom field:', error);
    throw error;
  }
}

// Function to fetch report access logs
export async function fetchReportAccessLogs(reportId) {
  try {
    const response = await axiosInstance.get(`/reports/report-access-logs/?report=${reportId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching report access logs:', error);
    throw error;
  }
}

// Function to update report schedule
export async function updateReportSchedule(reportId, schedule) {
  try {
    const response = await axiosInstance.patch(`/reports/reports/${reportId}/`, {
      schedule: schedule
    });
    return response.data;
  } catch (error) {
    console.error('Error updating report schedule:', error);
    throw error;
  }
}

// Function to fetch current user
export async function fetchCurrentUser() {
  try {
    const response = await axiosInstance.get('/users/me/');
    console.log('Current User API Response:', response.data);

    // Normalize the response data
    const user = response.data;

    return {
      id: user.id || null,
      username: user.username || 'Unknown',
      email: user.email || 'No Email',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      displayName: user.first_name
        ? `${user.first_name} ${user.last_name || ''}`.trim()
        : user.username || 'Unknown User',
    };
  } catch (error) {
    console.error('Error fetching current user:', error);

    if (error.response) {
      switch (error.response.status) {
        case 404:
          console.error('Endpoint not found. Check API routes.');
          break;
        case 401:
          console.error('Unauthorized. Please log in.');
          break;
        case 500:
          console.error('Server error. Please try again later.');
          break;
        default:
          console.error('Unexpected error occurred.');
      }
      throw new Error(JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error('No response received:', error.request);
      throw new Error('No response received from server');
    } else {
      console.error('Error setting up request:', error.message);
      throw error;
    }
  }
}

// Function to create user
async function createUser(userData) {
  if (!userData.username) {
    throw new Error('Username is required');
  }

  try {
    const response = await axiosInstance.post('/users/users/register/', userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    if (error.response) {
      console.error('Server responded with:', error.response.data);
      console.error('Status code:', error.response.status);
      throw new Error(JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error('No response received:', error.request);
      throw new Error('No response received from server');
    } else {
      console.error('Error setting up request:', error.message);
      throw error;
    }
  }
}

export { createUser };

// Function to fetch all users
async function fetchUsers(filterParams = {}) {
  try {
    // Build query string from filter parameters
    const queryParams = new URLSearchParams();
    
    // Add all filter parameters to query string
    Object.entries(filterParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    
    // Ensure page parameter exists
    if (!queryParams.has('page')) {
      queryParams.append('page', '1');
    }
    
    const url = `/users/users/?${queryParams.toString()}`;
    console.log('Fetching users from URL:', url); // Debug log
    
    const response = await axiosInstance.get(url);

    console.log('Raw API Response:', response.data);

    // Comprehensive data normalization
    let users = [];
    let paginationData = null;

    // Case 1: Paginated response with results
    if (response.data.results) {
      users = response.data.results;
      paginationData = {
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
        total_pages: response.data.total_pages || Math.ceil(response.data.count / response.data.results.length)
      };
    }
    // Case 2: Direct array response
    else if (Array.isArray(response.data)) {
      users = response.data;
      paginationData = {
        count: users.length,
        next: null,
        previous: null,
        total_pages: 1
      };
    }
    else {
      console.error('Unexpected API response structure:', response.data);
      users = [];
      paginationData = {
        count: 0,
        next: null,
        previous: null,
        total_pages: 0
      };
    }

    // Map users with proper fallback
    const normalizedUsers = users.map((user, index) => ({
      ...user,
      username: user.username || `User-${index}`,
      email: user.email || 'No Email',
      displayName: user.first_name
        ? `${user.first_name} ${user.last_name || ''}`.trim()
        : user.username || `User-${index}`
    }));

    // Return both users and pagination data
    return {
      results: normalizedUsers,
      ...paginationData
    };

  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
}

// pagination helper function
export function getUsersPagination(response) {
  // Handle both response.data and direct response formats
  const data = response.data || response;
  
  return {
    count: data.count || 0,
    next: data.next || null,
    previous: data.previous || null,
    totalPages: data.total_pages || Math.ceil((data.count || 0) / (data.results?.length || 1))
  };
}

// Delete a user
async function deleteUser(userId) {
  try {
    await axiosInstance.delete(`/users/${userId}/`);
    return true;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
}

// Function to update a User
export const updateUser = async (userId, userData) => {
  // Validate inputs
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const cleanUserId = userId.toString()
      .replace(/^(user-|temp-)/, '')
      .split('-')[0];

    console.log('Updating user:', cleanUserId);
    console.log('User data:', userData);

    const response = await axiosInstance.patch(
      `/users/users/${cleanUserId}/`,
      userData
    );

    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    if (error.response) {
      console.error('Error response data:', error.response.data);
      throw new Error(error.response.data.error || 'Failed to update user');
    }
    throw error;
  }
};

export const setupMfa = async () => {
  try {
    const response = await api.post('/users/setup-mfa/');
    return response;
  } catch (error) {
    throw error;
  }
};

// Function to update user roles
export const updateUserRoles = async (userId, data) => {
  // Validate inputs
  if (!userId) {
    throw new Error('Invalid user ID Cannot update roles for undefined user');
  }

  // Allow empty array for role removal
  if (!Array.isArray(data.role_ids)) {
    throw new Error('Invalid role IDs: Must be an array');
  }

  try {
    const cleanUserId = userId.toString()
      .replace(/^(user-|temp-)/, '')
      .split('-')[0];

    const formattedData = {
      roles: data.role_ids
        .map(id => {
          const parsedId = typeof id === 'string' 
            ? parseInt(id, 10) 
            : typeof id === 'number' 
              ? id 
              : null;
          return parsedId;
        })
        .filter(id => id !== null && !isNaN(id))
    };

    console.log('Role Update Request:', {
      url: `/users/users/${cleanUserId}/roles/`,
      data: formattedData
    });

    const response = await axiosInstance.put(
      `/users/users/${cleanUserId}/roles/`,
      formattedData
    );

    return response.data;
  } catch (error) {
    console.error('Comprehensive Role Update Error:', error);
    throw error;
  }
};

export const fetchEnhancedRoles = async (context = {}) => {
  try {
    let url = '/users/enhanced-roles/';

    // Build query parameters based on context for filtering
    const params = new URLSearchParams();

    if (context.branch_id) {
      params.append('branch_id', context.branch_id);
    }

    if (context.department_id) {
      params.append('department_id', context.department_id);
    }

    // Add query string if we have parameters
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await axiosInstance.get(url);

    console.log('Raw Enhanced Roles API Response:', response.data);

    let enhancedRoles = [];

    // Handle different response structures
    if (response.data.results) {
      enhancedRoles = response.data.results;
    } else if (Array.isArray(response.data)) {
      enhancedRoles = response.data;
    } else {
      console.error('Unexpected enhanced roles API response structure:', response.data);
      enhancedRoles = [];
    }

    return enhancedRoles.map(role => ({
      ...role,
      id: role.id,
      name: role.name || 'Unnamed Enhanced Role',
      description: role.description || '',
      branch_id: role.branch_id || null,
      department_id: role.department_id || null,
      approval_limits: role.approval_limits || {
        fund_limit: 0,
        transaction_limit: 0,
        daily_limit: 0
      },
      permissions: role.permissions || [],
      is_active: role.is_active !== false,
      displayName: role.name || 'Unnamed Enhanced Role',
      created_at: role.created_at,
      updated_at: role.updated_at
    }));

  } catch (error) {
    console.error('Error fetching enhanced roles:', error);
    throw error;
  }
};

export const updateUserEnhancedRoles = async (userId, data) => {
  if (!userId) {
    throw new Error('Invalid user ID: Cannot update enhanced roles for undefined user');
  }

  if (!Array.isArray(data.enhanced_role_ids)) {
    throw new Error('Invalid enhanced role IDs: Must be an array');
  }

  try {
    const cleanUserId = userId.toString()
      .replace(/^(user-|temp-)/, '')
      .split('-')[0];

    const formattedData = {
      enhanced_role_ids: data.enhanced_role_ids
        .map(id => {
          const parsedId = typeof id === 'string'
            ? parseInt(id, 10)
            : typeof id === 'number'
              ? id
              : null;
          return parsedId;
        })
        .filter(id => id !== null && !isNaN(id)),

      ...(data.branch_id && { branch_id: data.branch_id }),
      ...(data.department_id && { department_id: data.department_id })
    };

    console.log('Enhanced Role Update Request:', {
      url: `/users/users/${cleanUserId}/assign_enhanced_roles/`,
      data: formattedData
    });

    const response = await axiosInstance.post(
      `/users/users/${cleanUserId}/assign_enhanced_roles/`,
      formattedData
    );

    return response.data;
  } catch (error) {
    console.error('Comprehensive Enhanced Role Update Error:', error);
    throw error;
  }
};


// Function for assigning roles
export const assignUserRoles = async (userId, roleIds) => {
  try {
    const response = await axios.post(`/users/${userId}/assign-roles/`, {
      role_ids: roleIds
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update a product
async function updateProduct(productId, productData) {
  try {
    const response = await axiosInstance.put(`/products/products/${productId}/`, productData);
    return response.data;
  } catch (error) {
    console.error('Error fetching product details:', error.response?.data || error.message);
    throw error;
  }
}

export const fetchTaxCategories = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/audit-compliance/api/tax-categories/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch tax categories');
    }
  } catch (error) {
    console.error('Error fetching tax categories:', error.response?.data || error.message);
    throw error;
  }
};

export const createTaxCategory = async (data) => {
  try {
    const response = await axiosInstance.post('/audit-compliance/api/tax-categories/', data);
    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to create tax category');
    }
  } catch (error) {
    console.error('Error creating tax category:', error.response?.data || error.message);
    throw error;
  }
};

export const updateTaxCategory = async (id, data) => {
  try {
    const response = await axiosInstance.put(`/audit-compliance/api/tax-categories/${id}/`, data);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update tax category');
    }
  } catch (error) {
    console.error('Error updating tax category:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteTaxCategory = async (id) => {
  try {
    const response = await axiosInstance.delete(`/audit-compliance/api/tax-categories/${id}/`);
    if (response.status === 204) {
      return { success: true };
    } else {
      throw new Error('Failed to delete tax category');
    }
  } catch (error) {
    console.error('Error deleting tax category:', error.response?.data || error.message);
    throw error;
  }
};

export const updateProductTaxCategories = async (productId, taxData) => {
  try {
    const response = await axiosInstance.patch(`/products/products/${productId}/`, taxData);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update product tax categories');
    }
  } catch (error) {
    console.error('Error updating product tax categories:', error.response?.data || error.message);
    throw error;
  }
};

// Delete a product
async function deleteProduct(productId) {
  try {
    await axiosInstance.delete(`/products/products/${productId}/`);
    return true;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
}

export { fetchUsers, deleteUser, updateProduct, deleteProduct };

export const fetchPermissions = async () => {
  try {
    const response = await axiosInstance.get(`/users/permissions/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching permissions:', error);
    throw error;
  }
};

export const fetchUserPermissions = async () => {
  try {
    const response = await axiosInstance.get('/users/permissions/');
    return {
      permissions: response.data.permissions || [],
      accessibleRoutes: response.data.accessible_routes || []
    };
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    throw error;
  }
};

// Function to fetch roles
export const fetchRoles = async () => {
  const response = await axiosInstance.get('/users/roles/');
  return response.data;
};

// Function to create role
export const createRole = async (roleData) => {
  try {
    const response = await axiosInstance.post(`/users/roles/`, {
      name: roleData.name,
      description: roleData.description,
      permission_ids: roleData.permissions
    });
    return response.data;
  } catch (error) {
    console.error('Error creating role:', error);
    throw error;
  }
};

export const updateRole = async (roleId, roleData) => {
  try {
    const response = await axiosInstance.patch(`/users/roles/${roleId}/`, {
      name: roleData.name,
      description: roleData.description,
      permission_ids: roleData.permissions.map(p => p.id || p)
    });
    // Trigger a comprehensive permission refresh mechanism
    await triggerPermissionRefresh(roleId);
    return response.data;
  } catch (error) {
    console.error('Error updating role:', error);
    throw error;
  }
};

// Permission refresh utility
const triggerPermissionRefresh = async (roleId) => {
  try {
    // Endpoint to force permission re-evaluation
    await axiosInstance.post('/auth/refresh-permissions/', {
      role_id: roleId,
      refresh_type: 'full'
    });

    // Optional: Invalidate existing authentication tokens
    await invalidateUserTokens();
  } catch (refreshError) {
    console.error('Permission refresh failed:', refreshError);
    // Implement fallback mechanism or user notification
    throw refreshError;
  }
};

// Function to delete role
export const deleteRole = async (id) => {
  await axiosInstance.delete(`/users/roles/${id}/`);
};

// Function to fetch Categories
export async function fetchCategories() {
  try {
    const response = await axiosInstance.get('/products/categories/', {
      headers: getAuthHeader(),
    });
    console.log('Categories fetched:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
}

// Function to fetch reviews
async function fetchReviews(productId) {
  if (!productId) {
    throw new Error('Product ID is required');
  }

  try {
    const response = await axiosInstance.get(`/products/${productId}/reviews/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching reviews for product ${productId}:`, error);
    throw error;
  }
}

export { fetchReviews };

/**
 * Fetch all photography equipment with optional filters
 * @param {Object} params - Query parameters for filtering, pagination, sorting
 * @returns {Promise<Object>} - Paginated equipment list
 */
export const fetchPhotographyEquipment = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/photography/equipment/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch photography equipment');
    }
  } catch (error) {
    console.error('Error fetching photography equipment:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetch a single equipment item by ID
 * @param {number} id - Equipment ID
 * @returns {Promise<Object>} - Equipment details
 */
export const fetchPhotographyEquipmentById = async (id) => {
  try {
    const response = await axiosInstance.get(`/photography/equipment/${id}/`);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch equipment details');
    }
  } catch (error) {
    console.error('Error fetching equipment details:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Create new photography equipment
 * @param {Object} equipmentData - Equipment data to create
 * @returns {Promise<Object>} - Created equipment object
 */
export const createPhotographyEquipment = async (equipmentData) => {
  try {
    // Validate required fields, but serial_number is only required for non-quantity-based equipment
    if (!equipmentData.equipment_name || !equipmentData.equipment_type || 
        !equipmentData.purchase_date || !equipmentData.purchase_cost) {
      throw new Error('Equipment name, type, purchase date, and cost are required');
    }

    // Serial number validation: only required if NOT quantity-based
    if (!equipmentData.is_quantity_based && !equipmentData.serial_number) {
      throw new Error('Serial number is required for individual equipment');
    }

    console.log('Sending request to:', `${BASE_URL}/photography/equipment/`);
    console.log('Equipment data:', equipmentData);

    const response = await axiosInstance.post('/photography/equipment/', equipmentData);
    return response.data;
  } catch (error) {
    console.error('Error creating photography equipment:', error.response?.data || error.message);
    throw error;
  }
};


/**
 * Update existing photography equipment
 * @param {number} id - Equipment ID
 * @param {Object} equipmentData - Updated equipment data
 * @returns {Promise<Object>} - Updated equipment object
 */
export const updatePhotographyEquipment = async (id, equipmentData) => {
  try {
    console.log('Updating equipment:', id, equipmentData);
    const response = await axiosInstance.put(`/photography/equipment/${id}/`, equipmentData);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update photography equipment');
    }
  } catch (error) {
    console.error('Error updating photography equipment:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Partially update photography equipment
 * @param {number} id - Equipment ID
 * @param {Object} equipmentData - Partial equipment data to update
 * @returns {Promise<Object>} - Updated equipment object
 */
export const patchPhotographyEquipment = async (id, equipmentData) => {
  try {
    console.log('Patching equipment:', id, equipmentData);
    const response = await axiosInstance.patch(`/photography/equipment/${id}/`, equipmentData);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to patch photography equipment');
    }
  } catch (error) {
    console.error('Error patching photography equipment:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete photography equipment
 * @param {number} id - Equipment ID to delete
 * @returns {Promise<Object>} - Deletion confirmation
 */
export const deletePhotographyEquipment = async (id) => {
  try {
    const response = await axiosInstance.delete(`/photography/equipment/${id}/`);
    if (response.status === 204 || response.status === 200) {
      return response.data || { message: 'Equipment deleted successfully' };
    } else {
      throw new Error('Failed to delete photography equipment');
    }
  } catch (error) {
    console.error('Error deleting photography equipment:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Bulk delete photography equipment
 * @param {Array<number>} equipmentIds - Array of equipment IDs to delete
 * @returns {Promise<Object>} - Bulk deletion result
 */
export const bulkDeletePhotographyEquipment = async (equipmentIds) => {
  try {
    const response = await axiosInstance.post('/photography/equipment/bulk_delete/', {
      ids: equipmentIds
    });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to bulk delete equipment');
    }
  } catch (error) {
    console.error('Error bulk deleting equipment:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetch available photography equipment
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} - Available equipment list
 */
export const fetchAvailableEquipment = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/photography/equipment/available/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch available equipment');
    }
  } catch (error) {
    console.error('Error fetching available equipment:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetch all equipment incidents
 */
export const fetchEquipmentIncidents = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/photography/incidents/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching equipment incidents:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetch a single incident by ID
 */
export const fetchEquipmentIncident = async (incidentId) => {
  try {
    const response = await axiosInstance.get(`/photography/incidents/${incidentId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching equipment incident:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Report a new equipment incident (loss/damage)
 */
export const reportEquipmentIncident = async (equipmentId, incidentData) => {
  try {
    // Validate required fields
    if (!incidentData.incident_type || !incidentData.incident_date || !incidentData.description) {
      throw new Error('Incident type, date, and description are required');
    }

    console.log('Reporting incident for equipment:', equipmentId);
    console.log('Incident data:', incidentData);

    const response = await axiosInstance.post(
      `/photography/equipment/${equipmentId}/report_incident/`,
      incidentData
    );
    return response.data;
  } catch (error) {
    console.error('Error reporting equipment incident:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update incident resolution status
 */
export const updateIncidentResolution = async (incidentId, resolutionData) => {
  try {
    if (!resolutionData.resolution_status) {
      throw new Error('Resolution status is required');
    }

    console.log('Updating incident resolution:', incidentId);
    console.log('Resolution data:', resolutionData);

    const response = await axiosInstance.post(
      `/photography/incidents/${incidentId}/update_resolution/`,
      resolutionData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating incident resolution:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete an equipment incident (if allowed)
 */
export const deleteEquipmentIncident = async (incidentId) => {
  try {
    const response = await axiosInstance.delete(`/photography/incidents/${incidentId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting equipment incident:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetch equipment with maintenance due
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} - Equipment with maintenance due
 */
export const fetchMaintenanceDueEquipment = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/photography/equipment/maintenance_due/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch maintenance due equipment');
    }
  } catch (error) {
    console.error('Error fetching maintenance due equipment:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetch equipment by type
 * @param {string} type - Equipment type
 * @param {Object} params - Additional query parameters
 * @returns {Promise<Object>} - Equipment list of specified type
 */
export const fetchEquipmentByType = async (type, params = {}) => {
  try {
    const response = await axiosInstance.get('/photography/equipment/by_type/', {
      params: { type, ...params }
    });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch equipment by type');
    }
  } catch (error) {
    console.error('Error fetching equipment by type:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Assign equipment to a user
 * @param {number} equipmentId - Equipment ID
 * @param {number} userId - User ID to assign to
 * @returns {Promise<Object>} - Updated equipment object
 */
export const assignEquipment = async (equipmentId, userId, notes = '') => {
  try {
    const response = await axiosInstance.post(
      `/photography/equipment/${equipmentId}/assign/`,
      { 
        user_id: userId,
        notes: notes
      }
    );
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to assign equipment');
    }
  } catch (error) {
    console.error('Error assigning equipment:', error.response?.data || error.message);
    throw error;
  }
};

export const assignQuantityEquipment = async (equipmentId, userId, quantity, notes = '') => {
  try {
    const response = await axiosInstance.post(
      `/photography/equipment/${equipmentId}/assign_quantity/`,
      {
        user_id: userId,
        quantity: quantity,
        notes: notes
      }
    );
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to assign quantity equipment');
    }
  } catch (error) {
    console.error('Error assigning quantity equipment:', error.response?.data || error.message);
    throw error;
  }
};

export const returnQuantityEquipment = async (assignmentId, quantity, returnNotes = '') => {
  try {
    const response = await axiosInstance.post(
      `/photography/assignments/${assignmentId}/return_equipment/`,
      {
        quantity,
        return_notes: returnNotes
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error returning quantity equipment:', error.response?.data || error.message);
    throw error;
  }
};

export const unassignEquipment = async (equipmentId, notes = '') => {
  try {
    const response = await axiosInstance.post(
      `/photography/equipment/${equipmentId}/unassign/`,
      { notes: notes }
    );
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to unassign equipment');
    }
  } catch (error) {
    console.error('Error unassigning equipment:', error.response?.data || error.message);
    throw error;
  }
};

// This function is for viewing assignment history
export const fetchAssignmentHistory = async (equipmentId) => {
  try {
    const response = await axiosInstance.get(
      `/photography/equipment/${equipmentId}/assignment_history/`
    );
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch assignment history');
    }
  } catch (error) {
    console.error('Error fetching assignment history:', error.response?.data || error.message);
    throw error;
  }
};

export const acknowledgeAssignment = async (equipmentId) => {
  try {
    const response = await axiosInstance.post(
      `/photography/equipment/${equipmentId}/acknowledge_assignment/`
    );
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to acknowledge assignment');
    }
  } catch (error) {
    console.error('Error acknowledging assignment:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchMyAssignments = async () => {
  try {
    const response = await axiosInstance.get(
      `/photography/equipment/my_assignments/`
    );
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch assignments');
    }
  } catch (error) {
    console.error('Error fetching my assignments:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchUnacknowledgedAssignments = async () => {
  try {
    const response = await axiosInstance.get(
      `/photography/equipment/unacknowledged_assignments/`
    );
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch unacknowledged assignments');
    }
  } catch (error) {
    console.error('Error fetching unacknowledged assignments:', error.response?.data || error.message);
    throw error;
  }
};


export const fetchEquipmentWithAssignments = async (params) => {
  try {
    const response = await axiosInstance.get('/photography/equipment/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching equipment:', error.response?.data || error.message);
    throw error;
  }
};


/**
 * Update equipment maintenance information
 * @param {number} equipmentId - Equipment ID
 * @param {Object} maintenanceData - Maintenance data to update
 * @returns {Promise<Object>} - Updated equipment object
 */
export const updateEquipmentMaintenance = async (equipmentId, maintenanceData) => {
  try {
    const response = await axiosInstance.patch(
      `/photography/equipment/${equipmentId}/`,
      maintenanceData
    );
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update maintenance information');
    }
  } catch (error) {
    console.error('Error updating maintenance:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetch users available for equipment assignment
 * @param {Object} params - Query parameters (branch_id, department_id, etc.)
 * @returns {Promise<Array>} - List of users
 */
export const fetchUsersForAssignment = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/users/users/', { params });
    if (response.status === 200) {
      return response.data.results || response.data;
    } else {
      throw new Error('Failed to fetch users');
    }
  } catch (error) {
    console.error('Error fetching users for assignment:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetch photo projects with optional filters and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.endpoint - Optional custom endpoint (upcoming, in_progress, overdue, etc.)
 * @returns {Promise} Photo projects data
 */
export const fetchPhotoProjects = async (params = {}) => {
  try {
    // Extract custom endpoint if provided
    const { endpoint, ...queryParams } = params;

    // Build the URL
    let url = '/photography/projects/';
    if (endpoint) {
      url = `/photography/projects/${endpoint}/`;
    }

    const response = await axiosInstance.get(url, { params: queryParams });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch photo projects');
    }
  } catch (error) {
    console.error('Error fetching photo projects:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetch a single photo project by ID
 * @param {number} projectId - Project ID
 * @returns {Promise} Photo project details
 */
export const fetchPhotoProjectById = async (projectId) => {
  try {
    const response = await axiosInstance.get(`/photography/projects/${projectId}/`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch photo project');
    }
  } catch (error) {
    console.error('Error fetching photo project:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Create a new photo project
 * @param {Object} projectData - Project data
 * @returns {Promise} Created project
 */
export const createPhotoProject = async (projectData) => {
  try {
    if (!projectData.project_name || !projectData.client || !projectData.scheduled_date) {
      throw new Error('Project name, client, and scheduled date are required');
    }

    const response = await axiosInstance.post('/photography/projects/', projectData);
    return response.data;
  } catch (error) {
    console.error('Error creating photo project:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update an existing photo project
 * @param {number} projectId - Project ID
 * @param {Object} projectData - Updated project data
 * @returns {Promise} Updated project
 */
export const updatePhotoProject = async (projectId, projectData) => {
  try {
    const response = await axiosInstance.patch(`/photography/projects/${projectId}/`, projectData);
    return response.data;
  } catch (error) {
    console.error('Error updating photo project:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete a photo project
 * @param {number} projectId - Project ID
 * @returns {Promise}
 */
export const deletePhotoProject = async (projectId) => {
  try {
    const response = await axiosInstance.delete(`/photography/projects/${projectId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting photo project:', error.response?.data || error.message);
    throw error;
  }
};

export const voidPhotoProject = async (projectId, reason = '') => {
  try {
    const response = await axiosInstance.post(
      `/photography/projects/${projectId}/void/`,
      { reason }
    );
    return response.data;
  } catch (error) {
    console.error('Error voiding photo project:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update photo project status
 * @param {number} projectId - Project ID
 * @param {string} status - New status (booked, confirmed, in_progress, completed, delivered, cancelled)
 * @returns {Promise} Updated project
 */
export const updatePhotoProjectStatus = async (projectId, status) => {
  try {
    const response = await axiosInstance.post(
      `/photography/projects/${projectId}/update_status/`,
      { status }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating project status:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update photo project payment information
 * @param {number} projectId - Project ID
 * @param {Object} paymentData - Payment data (deposit_amount, payment_status)
 * @returns {Promise} Updated project
 */
export const updatePhotoProjectPayment = async (projectId, paymentData) => {
  try {
    const response = await axiosInstance.post(
      `/photography/projects/${projectId}/update_payment/`,
      paymentData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating project payment:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Record deposit payment for a photo project
 * @param {number} projectId - Project ID
 * @param {string} paymentMethod - Payment method (cash, bank_transfer, card, etc.)
 * @returns {Promise} Transaction and updated project data
 */
export const recordPhotoProjectDeposit = async (projectId, paymentMethod = 'bank_transfer') => {
  try {
    const response = await axiosInstance.post(
      `/photography/projects/${projectId}/record_deposit/`,
      { payment_method: paymentMethod }
    );
    return response.data;
  } catch (error) {
    console.error('Error recording deposit:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Record final payment for a photo project
 * @param {number} projectId - Project ID
 * @param {string} paymentMethod - Payment method (cash, bank_transfer, card, etc.)
 * @returns {Promise} Transaction and updated project data
 */
export const recordPhotoProjectFinalPayment = async (projectId, paymentMethod = 'bank_transfer') => {
  try {
    const response = await axiosInstance.post(
      `/photography/projects/${projectId}/record_final_payment/`,
      { payment_method: paymentMethod }
    );
    return response.data;
  } catch (error) {
    console.error('Error recording final payment:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Record an expense for a photo project
 * @param {number} projectId - Project ID
 * @param {Object} expenseData - Expense details
 * @param {number} expenseData.amount - Expense amount
 * @param {string} expenseData.description - Expense description
 * @param {string} expenseData.expense_category - Expense category
 * @param {string} expenseData.payment_method - Payment method
 * @returns {Promise} Transaction and updated project data
 */
export const recordPhotoProjectExpense = async (projectId, expenseData) => {
  try {
    if (!expenseData.amount || !expenseData.description) {
      throw new Error('Amount and description are required for expense');
    }

    const response = await axiosInstance.post(
      `/photography/projects/${projectId}/record_expense/`,
      expenseData
    );
    return response.data;
  } catch (error) {
    console.error('Error recording expense:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Assign tax categories to a photo project
 * @param {number} projectId - Project ID
 * @param {Array<number>} taxCategoryIds - Array of tax category IDs
 * @returns {Promise} Updated project with tax calculation
 */
export const assignPhotoProjectTaxCategories = async (projectId, taxCategoryIds) => {
  try {
    if (!Array.isArray(taxCategoryIds) || taxCategoryIds.length === 0) {
      throw new Error('At least one tax category ID is required');
    }

    const response = await axiosInstance.post(
      `/photography/projects/${projectId}/assign_tax_categories/`,
      { tax_category_ids: taxCategoryIds }
    );
    return response.data;
  } catch (error) {
    console.error('Error assigning tax categories:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get tax breakdown for a photo project
 * @param {number} projectId - Project ID
 * @returns {Promise} Tax breakdown details
 */
export const getPhotoProjectTaxBreakdown = async (projectId) => {
  try {
    const response = await axiosInstance.get(
      `/photography/projects/${projectId}/tax_breakdown/`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching tax breakdown:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Toggle tax exemption status for a photo project
 * @param {number} projectId - Project ID
 * @returns {Promise} Updated project with tax calculation
 */
export const togglePhotoProjectTaxExempt = async (projectId) => {
  try {
    const response = await axiosInstance.post(
      `/photography/projects/${projectId}/toggle_tax_exempt/`
    );
    return response.data;
  } catch (error) {
    console.error('Error toggling tax exemption:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get financial report for a photo project
 * @param {number} projectId - Project ID
 * @returns {Promise} Detailed financial report
 */
export const getPhotoProjectFinancialReport = async (projectId) => {
  try {
    const response = await axiosInstance.get(
      `/photography/projects/${projectId}/financial_report/`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching financial report:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get projects by photographer
 * @param {number} photographerId - Photographer user ID
 * @returns {Promise} Projects assigned to photographer
 */
export const getPhotoProjectsByPhotographer = async (photographerId) => {
  try {
    const response = await axiosInstance.get(
      `/photography/projects/by_photographer/`,
      { params: { photographer_id: photographerId } }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching projects by photographer:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get projects by client
 * @param {number} clientId - Client/Customer ID
 * @returns {Promise} Projects for client
 */
export const getPhotoProjectsByClient = async (clientId) => {
  try {
    const response = await axiosInstance.get(
      `/photography/projects/by_client/`,
      { params: { client_id: clientId } }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching projects by client:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetch all photographers (users who can be assigned to projects)
 * This assumes you have a user/staff endpoint - adjust as needed
 * @returns {Promise} List of photographers
 */
export const fetchPhotographers = async () => {
  const response = await axiosInstance.get('/users/users/staff/', {
    params: {
      is_active: true
    }
  });
  return response.data;
};


/**
 * Bulk delete photo projects
 * @param {Array<number>} projectIds - Array of project IDs to delete
 * @returns {Promise}
 */
export const bulkDeletePhotoProjects = async (projectIds) => {
  try {
    if (!Array.isArray(projectIds) || projectIds.length === 0) {
      throw new Error('Project IDs array is required');
    }

    const deletePromises = projectIds.map(id => deletePhotoProject(id));
    const results = await Promise.allSettled(deletePromises);

    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    return {
      successful,
      failed,
      total: projectIds.length,
      results
    };
  } catch (error) {
    console.error('Error in bulk delete:', error);
    throw error;
  }
};

/**
 * Export photo projects to CSV
 * @param {Object} params - Filter parameters
 * @returns {Promise} CSV file blob
 */
export const exportPhotoProjectsCsv = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/photography/projects/export_csv/', {
      params,
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting projects CSV:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Export photo projects to PDF
 * @param {Object} params - Filter parameters
 * @returns {Promise} PDF file blob
 */
export const exportPhotoProjectsPdf = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/photography/projects/export_pdf/', {
      params,
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting projects PDF:', error.response?.data || error.message);
    throw error;
  }
};

/* ============================================================
   SALON API FUNCTIONS
   Append these to src/services/api.js — they reuse the same
   `axiosInstance` already configured at the top of that file.
   Mirrors the exact patterns of fetchPhotographyEquipment /
   createPhotographyEquipment / fetchPhotoProjects / etc.
   ============================================================ */

// ---------------------------------------------------------------
// Salon Equipment
// ---------------------------------------------------------------

export const fetchSalonEquipment = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/salon/equipment/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch salon equipment');
    }
  } catch (error) {
    console.error('Error fetching salon equipment:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchAvailableSalonEquipment = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/salon/equipment/available/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch available salon equipment');
    }
  } catch (error) {
    console.error('Error fetching available salon equipment:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchMaintenanceDueSalonEquipment = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/salon/equipment/maintenance_due/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch salon equipment with maintenance due');
    }
  } catch (error) {
    console.error('Error fetching maintenance-due salon equipment:', error.response?.data || error.message);
    throw error;
  }
};

export const createSalonEquipment = async (equipmentData) => {
  try {
    if (!equipmentData.equipment_name || !equipmentData.equipment_type ||
        !equipmentData.purchase_date || !equipmentData.purchase_cost) {
      throw new Error('Equipment name, type, purchase date, and cost are required');
    }
    if (!equipmentData.is_quantity_based && !equipmentData.serial_number) {
      throw new Error('Serial number is required for individual equipment');
    }

    const response = await axiosInstance.post('/salon/equipment/', equipmentData);
    return response.data;
  } catch (error) {
    console.error('Error creating salon equipment:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchSalonEquipmentById = async (equipmentId) => {
  try {
    const response = await axiosInstance.get(`/salon/equipment/${equipmentId}/`);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch salon equipment item');
    }
  } catch (error) {
    console.error('Error fetching salon equipment item:', error.response?.data || error.message);
    throw error;
  }
};

export const updateSalonEquipment = async (equipmentId, equipmentData) => {
  try {
    const response = await axiosInstance.patch(`/salon/equipment/${equipmentId}/`, equipmentData);
    return response.data;
  } catch (error) {
    console.error('Error updating salon equipment:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteSalonEquipment = async (equipmentId) => {
  try {
    const response = await axiosInstance.delete(`/salon/equipment/${equipmentId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting salon equipment:', error.response?.data || error.message);
    throw error;
  }
};

// ---------------------------------------------------------------
// Salon Services (income/expense entity)
// ---------------------------------------------------------------

export const fetchSalonServices = async (params = {}) => {
  try {
    const { endpoint, ...queryParams } = params;
    let url = '/salon/services/';
    if (endpoint) {
      url = `/salon/services/${endpoint}/`;
    }
    const response = await axiosInstance.get(url, { params: queryParams });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch salon services');
    }
  } catch (error) {
    console.error('Error fetching salon services:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchSalonServiceById = async (serviceId) => {
  try {
    const response = await axiosInstance.get(`/salon/services/${serviceId}/`);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch salon service');
    }
  } catch (error) {
    console.error('Error fetching salon service:', error.response?.data || error.message);
    throw error;
  }
};

export const createSalonService = async (serviceData) => {
  try {
    if (!serviceData.service_type || !serviceData.service_date || !serviceData.amount_charged) {
      throw new Error('Service type, service date, and amount charged are required');
    }
    const response = await axiosInstance.post('/salon/services/', serviceData);
    return response.data;
  } catch (error) {
    console.error('Error creating salon service:', error.response?.data || error.message);
    throw error;
  }
};

export const updateSalonService = async (serviceId, serviceData) => {
  try {
    const response = await axiosInstance.patch(`/salon/services/${serviceId}/`, serviceData);
    return response.data;
  } catch (error) {
    console.error('Error updating salon service:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteSalonService = async (serviceId) => {
  try {
    const response = await axiosInstance.delete(`/salon/services/${serviceId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting salon service:', error.response?.data || error.message);
    throw error;
  }
};

export const updateSalonServiceStatus = async (serviceId, status) => {
  try {
    const response = await axiosInstance.post(`/salon/services/${serviceId}/update_status/`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating salon service status:', error.response?.data || error.message);
    throw error;
  }
};

export const recordSalonServiceIncome = async (serviceId, paymentMethod) => {
  try {
    const response = await axiosInstance.post(`/salon/services/${serviceId}/record_income/`, {
      payment_method: paymentMethod
    });
    return response.data;
  } catch (error) {
    console.error('Error recording salon service income:', error.response?.data || error.message);
    throw error;
  }
};

export const recordSalonServiceExpense = async (serviceId, expenseData) => {
  try {
    const response = await axiosInstance.post(`/salon/services/${serviceId}/record_expense/`, expenseData);
    return response.data;
  } catch (error) {
    console.error('Error recording salon service expense:', error.response?.data || error.message);
    throw error;
  }
};

export const assignSalonServiceTaxCategories = async (serviceId, taxCategoryIds) => {
  try {
    const response = await axiosInstance.post(`/salon/services/${serviceId}/assign_tax_categories/`, {
      tax_category_ids: taxCategoryIds
    });
    return response.data;
  } catch (error) {
    console.error('Error assigning salon service tax categories:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchSalonServiceTaxBreakdown = async (serviceId) => {
  try {
    const response = await axiosInstance.get(`/salon/services/${serviceId}/tax_breakdown/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching salon service tax breakdown:', error.response?.data || error.message);
    throw error;
  }
};

export const toggleSalonServiceTaxExempt = async (serviceId) => {
  try {
    const response = await axiosInstance.post(`/salon/services/${serviceId}/toggle_tax_exempt/`);
    return response.data;
  } catch (error) {
    console.error('Error toggling salon service tax exemption:', error.response?.data || error.message);
    throw error;
  }
};

export const getSalonServiceFinancialReport = async (serviceId) => {
  try {
    const response = await axiosInstance.get(`/salon/services/${serviceId}/financial_report/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching salon service financial report:', error.response?.data || error.message);
    throw error;
  }
};

// ---------------------------------------------------------------
export const fetchSalonStylists = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/users/users/staff/', { params });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch salon stylists');
    }
  } catch (error) {
    console.error('Error fetching salon stylists:', error.response?.data || error.message);
    throw error;
  }
};

export const voidSalonService = async (serviceId, reason) => {
  try {
    const response = await axiosInstance.post(`/salon/services/${serviceId}/void/`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error voiding salon service:', error.response?.data || error.message);
    throw error;
  }
};
